/* global globalThis */
import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import axios from '../../api/axios';
import { addHashTagRoute, deleteHashTagRoute } from '../../api/routes';
import {
  ROW_STEP, COL_STEP, CENTER_COL, PAD_X, PAD_Y, CIRCLE_R, PLUS_R, CARD_W, CARD_H,
  VOTE_CARD_H, VOTE_CARD_GAP, DISRUPTOR_CARD_H, DISRUPTOR_CARD_GAP,
  DISRUPTOR_HEADER_H, DISRUPTOR_FOOTER_H,
  WINNER_NODE_H, HASH_GAP, TRIG_H,
  TRIG_W,
  IMAGE_STACK_CARD_H, IMAGE_STACK_CARD_GAP, BRANCH_STEM,
  PARTICLE_SETTINGS_H,
  COUNTDOWN_SETTINGS_H, COUNTDOWN_TIMER_H, COUNTDOWN_CARD_H, COUNTDOWN_CARD_GAP,
  VIDEO_SETTINGS_H, VIDEO_CARD_H, VIDEO_CARD_GAP,
  AUDIO_CARD_W, AUDIO_CARD_H, AUDIO_CARD_GAP,
  POLL_CARD_H, POLL_CARD_GAP,
  POLL_AUDIO_HEADER_H, POLL_AUDIO_SLOT_LBL_H, POLL_AUDIO_FILE_H, POLL_AUDIO_FILE_GAP,
  POLL_AUDIO_EMPTY_H, POLL_AUDIO_SLOT_GAP,
  POLL_AUDIO_ADD_BTN_H,
  POLL_OUTCOME_H,
  SLOT_REELS_HEADER_H, SLOT_REELS_ITEM_H, SLOT_REELS_ITEM_GAP, SLOT_REELS_COL_W, SLOT_REELS_COL_GAP, SLOT_REELS_NODE_PADDING,
  SLOT_RESULTS_HEADER_H, SLOT_RESULTS_ROW_H, SLOT_RESULTS_ROW_GAP, SLOT_RESULTS_ADD_H,
  POLL_TRIGGER_PANEL_GAP, POLL_TRIGGER_PANEL_W,
  GOAL_CARD_W, GOAL_CARD_GAP, GOALS_NODE_PADDING, GOALS_NODE_H,
  PRESET_TEXT_W, PRESET_TEXT_CARD_H, PRESET_TEXT_CARD_H_NO_HEADER, PRESET_TEXT_CARD_GAP,
  goalsNodeHalfW,
  DIAMOND_H, OUTCOME_H,
  PUZZLE_CARD_H, PUZZLE_CARD_GAP,
  C, getPathValue, nx, ny, stepKind, halfW, halfH,
} from './flowCanvas.constants';
import Connector       from './nodes/Connector';
import CircleNode      from './nodes/CircleNode';
import CardNode        from './nodes/CardNode';
import TriggerNode     from './nodes/TriggerNode';
import InfoButtonNode  from './nodes/InfoButtonNode';
import DisruptorNode   from './nodes/DisruptorNode';
import LeaderboardTriggerNode from './nodes/LeaderboardTriggerNode';
import LeaderboardStartAudioNode from './nodes/LeaderboardStartAudioNode';
import LeaderboardSettingsNode from './nodes/LeaderboardSettingsNode';
import LeaderboardDataNode from './nodes/LeaderboardDataNode';
import WaveWinnerNode  from './nodes/WaveWinnerNode';
import WaveGraphicsNode    from './nodes/WaveGraphicsNode';
import ZapperObjectsNode  from './nodes/ZapperObjectsNode';
import ImageStackNode     from './nodes/ImageStackNode';
import ParticleStackNode  from './nodes/ParticleStackNode';
import PollAudioNode      from './nodes/PollAudioNode';
import PollCardsNode      from './nodes/PollCardsNode';
import TriviaCardsNode    from './nodes/TriviaCardsNode';
import PollOutcomeNode    from './nodes/PollOutcomeNode';
import SlotReelsNode      from './nodes/SlotReelsNode';
import SlotReelsCountNode from './nodes/SlotReelsCountNode';
import SlotResultsNode    from './nodes/SlotResultsNode';
import CircleInfoPanel   from './nodes/CircleInfoPanel';
import GoalsNode         from './nodes/GoalsNode';
import DiamondNode       from './nodes/DiamondNode';
import OutcomeNode       from './nodes/OutcomeNode';
import PuzzleStackNode   from './nodes/PuzzleStackNode';
import ParticleSettingsNode from './nodes/ParticleSettingsNode';
import ParticleTimerNode    from './nodes/ParticleTimerNode';
import PresetTextNode          from './nodes/PresetTextNode';
import LiveTextSettingsNode    from './nodes/LiveTextSettingsNode';
import SubBombDisplaySettingsNode from './nodes/SubBombDisplaySettingsNode';
import SubBombSpeedNode        from './nodes/SubBombSpeedNode';
import InfoBoxNode             from './nodes/InfoBoxNode';
import StatusScreenImageNode   from './nodes/StatusScreenImageNode';
import AnimatedOverlayMainViewNode from './nodes/AnimatedOverlayMainViewNode';
import CountdownSettingsNode   from './nodes/CountdownSettingsNode';
import CountdownTimerNode      from './nodes/CountdownTimerNode';
import CountdownImageStackNode from './nodes/CountdownImageStackNode';
import CountdownTextStackNode  from './nodes/CountdownTextStackNode';
import VideoSettingsNode       from './nodes/VideoSettingsNode';
import VideoStackNode          from './nodes/VideoStackNode';
import AudioStackNode          from './nodes/AudioStackNode';
import './FlowCanvas.scss';

// ─── FlowCanvas main component ─────────────────────────────────────────────────
const FlowCanvas = ({
  flow,
  zoom = 1,
  onZoomChange,
  onStepClick,
  selectedStepId,
  campaignID,
  featureInfo,
  onRefresh,
  onActivateNode,
  onRemoveGraphic,
  onPollQuestionChange,
  onPollAnswerChange,
  onPollAnswerAdd,
  onPollAnswerRemove,
  onImageFieldChange,
  onGoalFieldChange,
  onPresetFieldChange,
  onFlowBoolChange,
  onFlowFieldChange,
  getStepItems,
  onPollTriggerDrop,
  onPollTriggerRemove,
  liveCustomisationData,
  liveFeatureData,
  onActivateDisruptor,
  onDropDisruptor,
  onRemoveDisruptor,
  onGoalRewardDisruptorDrop,
  campaignFeatures,
  onClearSelection,
  activeAudioSlotVarName,
  onAddPreset,
  onVideoSettingsChange,
  onSlotOptionDisruptorDrop,
  onSlotOptionDisruptorRemove,
  onSlotOptionRemove,
  onSlotResultAdd,
  onSlotResultRemove,
  onSlotResultDisruptorDrop,
  onSlotResultDisruptorRemove,
  activeNode,
  selectedFieldDataLink,
  onTriviaQuestionDisruptorDrop,
  onSelectPlacedComponent,
  onResetView,
}) => {
  const LOG_LAYOUT_DEBUG = false;
  const logClickDebug = useCallback((label, payload = {}) => {
    if (!LOG_LAYOUT_DEBUG) {
      return;
    }
    console.debug(`[FlowCanvas] ${label}`, payload);
  }, []);
  const [activeStepId, setActiveStepId] = useState(null);
  const [activeGraphicIndex, setActiveGraphicIndex] = useState(null);
  const [dragOverDisruptorStepId, setDragOverDisruptorStepId] = useState(null);
  // force re-render when an inline diamond toggle mutates liveCustomisationData in-place
  const [, setDiamondToggleTick] = useState(0);

  const resolveStepItems = useCallback((step) => {
    const resolved = typeof getStepItems === 'function' ? getStepItems(step) : null;
    if (Array.isArray(resolved)) {
      return resolved.filter(item => item != null);
    }

    const cfg = step?.customisationConfig;
    const tabKey = cfg?.tabKey;
    const varKey = cfg?.varKey;
    const varName = cfg?.variables?.[0]?.variableName;
    const liveVals = liveCustomisationData && tabKey && varKey && varName
      ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
      : null;

    return Array.isArray(liveVals)
      ? liveVals.filter(item => item != null)
      : (step.populatedData?.variables?.[0]?.allValues || []).filter(item => item != null);
  }, [getStepItems, liveCustomisationData]);

  const getActiveNodeHierarchyDataLinks = useCallback((node) => {
    const links = [];
    let current = node;

    while (current) {
      const dataLink = String(
        current?.config?.groupSettings?.dataLink
        || current?.config?.dataLink
        || ''
      ).trim();

      if (dataLink) {
        links.push(dataLink);
      }

      current = current.parent || null;
    }

    return links;
  }, []);

  const getActiveNodeDataIndex = useCallback((node) => {
    let current = node;

    while (current) {
      if (Number.isInteger(current?.dataIndex) && current.dataIndex >= 0) {
        return current.dataIndex;
      }

      current = current.parent || null;
    }

    return null;
  }, []);

  useEffect(() => {
    const waveSteps = (Array.isArray(flow) ? flow : []).filter((step) => stepKind(step) === 'waveGraphics');

    if (!waveSteps.length) {
      return;
    }

    const normalizedFieldDataLink = typeof selectedFieldDataLink === 'string'
      ? selectedFieldDataLink.trim()
      : '';
    const activeNodeDataLinks = getActiveNodeHierarchyDataLinks(activeNode);
    const activeNodeDataIndex = getActiveNodeDataIndex(activeNode);

    const getSelectedWaveIndex = (variableDataLink) => {
      if (!variableDataLink) {
        return null;
      }

      if (normalizedFieldDataLink.startsWith(`${variableDataLink}.`)) {
        const indexToken = normalizedFieldDataLink.slice(variableDataLink.length + 1).split('.')[0];
        const parsedIndex = Number(indexToken);

        if (Number.isInteger(parsedIndex) && parsedIndex >= 0) {
          return { itemIndex: parsedIndex, fromFieldDataLink: true };
        }
      }

      const nodeMatchesVariable = activeNodeDataLinks.some((dataLink) => (
        dataLink === variableDataLink
        || dataLink.startsWith(`${variableDataLink}.`)
        || variableDataLink.startsWith(`${dataLink}.`)
      ));

      if (nodeMatchesVariable && Number.isInteger(activeNodeDataIndex) && activeNodeDataIndex >= 0) {
        return { itemIndex: activeNodeDataIndex, fromFieldDataLink: false };
      }

      return null;
    };

    const isItemVisibleInWaveStep = (item, step) => {
      if (!item || !step?.customisationConfig?.meta) {
        return false;
      }

      const teamFilter = step.customisationConfig.meta.teamFilter;
      const teamAssign = step.customisationConfig.meta.teamAssign ?? teamFilter;
      const handFilter = step.customisationConfig.meta.handFilter;

      if (teamFilter === undefined) {
        return true;
      }

      return (item.team === teamAssign || item.team === 2)
        && (handFilter == null || item.hand === handFilter);
    };

    let bestWaveSelection = null;

    waveSteps.forEach((step) => {
      const cfg = step.customisationConfig;
      const variableName = cfg?.variables?.[0]?.variableName;
      const variableDataLink = cfg?.tabKey && cfg?.varKey && variableName
        ? `${cfg.tabKey}.${cfg.varKey}.${variableName}`
        : null;
      const selectedWaveIndex = getSelectedWaveIndex(variableDataLink);

      if (!selectedWaveIndex) {
        return;
      }

      const waveValues = resolveStepItems(step);
      if (!Array.isArray(waveValues) || selectedWaveIndex.itemIndex >= waveValues.length) {
        return;
      }

      const selectedWaveItem = waveValues[selectedWaveIndex.itemIndex];
      if (!isItemVisibleInWaveStep(selectedWaveItem, step)) {
        return;
      }

      const teamAssign = cfg?.meta?.teamAssign ?? cfg?.meta?.teamFilter;
      const handFilter = cfg?.meta?.handFilter;
      let score = selectedWaveIndex.fromFieldDataLink ? 20 : 10;

      if (teamAssign !== undefined && selectedWaveItem?.team === teamAssign) {
        score += 3;
      }

      if (handFilter != null && selectedWaveItem?.hand === handFilter) {
        score += 2;
      }

      if (!bestWaveSelection || score > bestWaveSelection.score) {
        bestWaveSelection = {
          stepId: step.stepId,
          itemIndex: selectedWaveIndex.itemIndex,
          score,
        };
      }
    });

    if (bestWaveSelection) {
      setActiveStepId(bestWaveSelection.stepId);
      setActiveGraphicIndex(bestWaveSelection.itemIndex);
    }
  }, [
    activeNode,
    getActiveNodeDataIndex,
    getActiveNodeHierarchyDataLinks,
    resolveStepItems,
    selectedFieldDataLink,
    flow,
  ]);

  const getStackButtonExtraX = useCallback((step) => {
    if (stepKind(step) === 'videoStack') return 40;
    return 0;
  }, []);

  const onWheel = useCallback((e) => {
    const el = panAreaRef.current;
    if (!el || typeof onZoomChange !== 'function') return;

    e.preventDefault();

    const rect = el.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const zoomIntensity = e.ctrlKey ? 0.0025 : 0.0015;
    const scaleFactor = Math.exp(-e.deltaY * zoomIntensity);
    const nextZoom = Math.min(2, Math.max(0.5, parseFloat((zoom * scaleFactor).toFixed(3))));

    if (nextZoom === zoom) return;

    setOffset((current) => {
      const worldX = (mouseX - current.x) / zoom;
      const worldY = (mouseY - current.y) / zoom;
      return {
        x: mouseX - worldX * nextZoom,
        y: mouseY - worldY * nextZoom,
      };
    });

    onZoomChange(nextZoom);
  }, [zoom, onZoomChange]);

  const isBranchStackKind = useCallback((stepOrKind) => {
    const kind = typeof stepOrKind === 'string' ? stepOrKind : stepKind(stepOrKind);
    return kind === 'imageStack' || kind === 'particleImageStack' || kind === 'particleColourStack';
  }, []);

  const transformedFlow = useMemo(() => {
    const source = Array.isArray(flow) ? flow : [];
    const cloned = JSON.parse(JSON.stringify(source));
    const livePolls = liveCustomisationData?.setup?.pollData?.polls;
    const hasInlinePollTriggerPanels = false; // trigger panels moved to PollOutcomeNode

    const hidePollNode = cloned.find(step => step.stepId === 'hide_poll');
    const voteEndNode = cloned.find(step => step.stepId === 'vote_end_node');
    const predictiveOutcome = cloned.find(step => step.stepId === 'predictive_outcome');
    const standardOutcome = cloned.find(step => step.stepId === 'standard_outcome');
    const removalOutcome = cloned.find(step => step.stepId === 'removal_outcome');
    const pollEndedNode = cloned.find(step => step.stepId === 'poll_ended_node');
    const showPreviousNode = cloned.find(step => step.stepId === 'show_previous');
    const hidePreviousNode = cloned.find(step => step.stepId === 'hide_previous');

    if (predictiveOutcome?.customisationConfig?.meta) {
      predictiveOutcome.customisationConfig.meta.disruptorSectionLabel = 'Correct Answer Reveal Trigger';
      predictiveOutcome.customisationConfig.meta.hideDisruptorSection = true;
      predictiveOutcome.customisationConfig.meta.usePerPollSlots = true;
    }
    if (removalOutcome?.customisationConfig?.meta) {
      removalOutcome.customisationConfig.meta.disruptorSectionLabel = 'Fade Removed Answer Trigger';
      removalOutcome.customisationConfig.meta.hideDisruptorSection = true;
      removalOutcome.customisationConfig.meta.usePerPollSlots = true;
    }

    if (standardOutcome?.customisationConfig?.meta) {
      standardOutcome.customisationConfig.meta.hideDisruptorSection = true;
    }

    if (showPreviousNode?.customisationConfig?.meta) {
      showPreviousNode.customisationConfig.meta.sectionTitle = 'Show Previous Result';
    }

    if (hidePreviousNode?.customisationConfig?.meta) {
      hidePreviousNode.customisationConfig.meta.sectionTitle = 'Hide Previous Result';
    }

    const pollsNode = cloned.find(step => step.stepId === 'polls_node');
    if (pollsNode?.customisationConfig?.meta) {
      pollsNode.customisationConfig.meta.hasInlineTriggerPanels = hasInlinePollTriggerPanels;
    }

    const isLiveTriviaFlow = cloned.some(step => step?.customisationConfig?.meta?.triviaCards);
    const triviaEndedNode = cloned.find(step => step.stepId === 'trivia_ended_node');
    const hasLeaderboardTriggerNode = cloned.some(step => step.stepId === 'leaderboard_trigger_node');
    const hasLeaderboardClosedNode = cloned.some(step => step.stepId === 'leaderboard_closed_node');

    if (isLiveTriviaFlow && triviaEndedNode && !hasLeaderboardTriggerNode) {
      triviaEndedNode.connectsTo = ['leaderboard_trigger_node'];

      cloned.push({
        stepId: 'leaderboard_trigger_node',
        stepType: 'customisation',
        stepTitle: 'Leaderboard Trigger',
        stepDescription: 'Drop a leaderboard here, then set its type and score level.',
        customisationConfig: {
          tabKey: 'setup',
          varKey: 'leaderboards',
          meta: {
            leaderboardTrigger: true,
            disruptorSelectDataLink: 'setup.leaderboards.disruptor.featureID',
            allowMultiple: false,
            acceptedFeatureVarKeys: ['leaderboard'],
            acceptedFeatureLabel: 'leaderboard',
            activateAsNode: true,
            activateField: 'featureID',
          },
          displayAs: 'compact',
          displayMode: 'summary',
          variables: [
            {
              variableName: 'disruptor',
              displayLabel: 'Leaderboard',
              isRequired: false,
              allowMultiple: false,
            },
          ],
        },
        displayCondition: [],
        connectsTo: ['leaderboard_closed_node'],
        layout: {
          row: (triviaEndedNode.layout?.row ?? 13) + 1,
          column: triviaEndedNode.layout?.column ?? 4,
        },
      });
    }

    if (isLiveTriviaFlow && triviaEndedNode && !hasLeaderboardClosedNode) {
      cloned.push({
        stepId: 'leaderboard_closed_node',
        stepType: 'node',
        stepTitle: 'LEADERBOARD CLOSED',
        stepDescription: 'The leaderboard has now been hidden.',
        nodeConfig: {
          nodeStyle: 'standard',
        },
        displayCondition: [],
        connectsTo: [],
        layout: {
          row: (triviaEndedNode.layout?.row ?? 13) + 2,
          column: triviaEndedNode.layout?.column ?? 4,
        },
      });
    }

    const hasSyntheticVoteEndTrigger = cloned.some(step => step.stepId === 'vote_ended_global_trigger');
    if (voteEndNode && standardOutcome?.customisationConfig?.meta?.disruptorLink && !hasSyntheticVoteEndTrigger) {
      voteEndNode.connectsTo = ['vote_ended_global_trigger'];

      cloned.push({
        stepId: 'vote_ended_global_trigger',
        stepType: 'customisation',
        stepTitle: 'Vote Ended Trigger',
        stepDescription: 'Global disruptors that run when voting ends, regardless of poll type.',
        customisationConfig: {
          tabKey: 'triggerOptions',
          varKey: 'disruptorTriggers',
          meta: {
            disruptorSelectDataLink: standardOutcome.customisationConfig.meta.disruptorLink,
            allowMultiple: true,
          },
          displayAs: 'compact',
          displayMode: 'summary',
          variables: [
            {
              variableName: 'votingEnded',
              displayLabel: 'Vote Ended',
              isRequired: false,
              allowMultiple: true,
            },
          ],
        },
        displayCondition: [],
        connectsTo: ['predictive_outcome', 'standard_outcome', 'removal_outcome'],
        layout: {
          row: (voteEndNode.layout?.row ?? 4) + 0.5,
          column: voteEndNode.layout?.column ?? 4,
        },
      });
    }

    const hasPreviousResultHiddenNode = cloned.some(step => step.stepId === 'previous_result_hidden_node');
    if (hidePollNode && pollEndedNode && showPreviousNode && hidePreviousNode) {
      hidePollNode.connectsTo = ['poll_ended_node'];
      pollEndedNode.connectsTo = ['show_previous'];
      showPreviousNode.connectsTo = ['hide_previous'];
      hidePreviousNode.connectsTo = ['previous_result_hidden_node'];

      showPreviousNode.layout = {
        ...(showPreviousNode.layout || {}),
        row: (pollEndedNode.layout?.row ?? 8) + 1,
        column: pollEndedNode.layout?.column ?? 4,
      };

      hidePreviousNode.layout = {
        ...(hidePreviousNode.layout || {}),
        row: (pollEndedNode.layout?.row ?? 8) + 2,
        column: pollEndedNode.layout?.column ?? 4,
      };

      if (!hasPreviousResultHiddenNode) {
        cloned.push({
          stepId: 'previous_result_hidden_node',
          stepType: 'node',
          stepTitle: 'PREVIOUS RESULT HIDDEN',
          stepDescription: 'The previously shown poll result is now hidden.',
          nodeConfig: {
            nodeStyle: 'standard',
          },
          displayCondition: [],
          connectsTo: [],
          layout: {
            row: (pollEndedNode.layout?.row ?? 8) + 3,
            column: pollEndedNode.layout?.column ?? 4,
          },
        });
      }
    }

    return cloned;
  }, [flow, liveCustomisationData]);

  const assetFolder = featureInfo?.defaultAssetFolder || '';
  const campaignId  = campaignID;
  const liveTextHeadersEnabled = liveCustomisationData?.setup?.templates?.settings?.[0]?.addHeader !== false;

  // Resolve campaign feature instance id (campaign.features[i]._id) for hashtag save calls.
  // This matches existing hashtag CRUD in CampaignOverview/AddHashtag.
  const resolvedFeatureInstanceId = useMemo(() => {
    if (featureInfo?.featureInstanceId) return featureInfo.featureInstanceId;
    const varKey = featureInfo?.varKey;
    if (!varKey || !Array.isArray(campaignFeatures)) return null;
    const match = campaignFeatures.find(f => f?.feature?.varKey === varKey || f?.varKey === varKey);
    return match?._id || featureInfo?.featureId || match?.feature?._id || null;
  }, [featureInfo?.featureInstanceId, featureInfo?.featureId, featureInfo?.varKey, campaignFeatures]);

  // Feature template id (Features collection _id) — used as a fallback key for
  // default hashtags that were seeded with this id before instance-id storage was adopted.
  const featureTemplateId = featureInfo?.featureId || null;
  const ENABLE_FLOW_DEBUG_LOGS = false;
  if (ENABLE_FLOW_DEBUG_LOGS && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[FlowCanvas] featureInfo:', featureInfo, '| featureTemplateId:', featureTemplateId, '| resolvedFeatureInstanceId:', resolvedFeatureInstanceId);
  }

  // Used to focus the inline hashtag editor when + is clicked.
  const [hashtagFocus, setHashtagFocus] = useState({ stepId: null, nonce: 0 });
  const hashtagCommitInFlightRef = useRef(new Set());

  // Track draft editor heights per trigger stepId so layout reflows immediately
  // when a user adds/removes rows without waiting for the API save + refresh cycle.
  const [draftHeightOverrides, setDraftHeightOverrides] = useState({});
  const handleDraftHeightChange = useCallback((stepId, h) => {
    setDraftHeightOverrides(prev => (prev[stepId] === h ? prev : { ...prev, [stepId]: h }));
  }, []);

  // Track live poll-card stack heights so expanding/collapsing poll cards can
  // reflow sibling nodes in real time.
  const [pollCardHeightOverrides, setPollCardHeightOverrides] = useState({});
  const handlePollCardHeightChange = useCallback((stepId, h) => {
    setPollCardHeightOverrides(prev => {
      if (h == null) {
        if (prev[stepId] === undefined) return prev;
        const next = { ...prev };
        delete next[stepId];
        return next;
      }
      return prev[stepId] === h ? prev : { ...prev, [stepId]: h };
    });
  }, []);

  // Dev-only: helps debug displayCondition (Team vs VS) not updating.
  const lastModeRef = useRef(undefined);
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const raw = liveCustomisationData?.hands?.objects?.waveMode;
    const valObj = Array.isArray(raw) ? raw[0] : raw;
    const mode = valObj?.selectedMode;
    if (mode !== undefined && mode !== lastModeRef.current) {
      lastModeRef.current = mode;
      console.log('[FlowCanvas] waveMode.selectedMode =', mode);
    }
  }, [liveCustomisationData]);

  // ── Pan state ──────────────────────────────────────────────────────────────
  const [offset, setOffset]   = useState({ x: 0, y: 0 });
  const panAreaRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  // Track whether mouse actually moved (to distinguish click from drag)
  const didDrag = useRef(false);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    // Always clear stale drag state at the start of a new pointer cycle.
    didDrag.current = false;
    // Clicking any flow node/control should not start canvas panning.
    // This keeps add/info/delete handlers from being suppressed by drag detection.
    if (e.target?.closest?.('.fc-node')) {
      return;
    }
    drag.current = {
      active:  true,
      startX:  e.clientX,
      startY:  e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    didDrag.current = false;
    e.currentTarget.style.cursor = 'grabbing';
  }, [offset]);

  const onMouseMove = useCallback((e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      didDrag.current = true;
    }
    setOffset({ x: drag.current.originX + dx, y: drag.current.originY + dy });
  }, []);

  const onMouseUp = useCallback((e) => {
    drag.current.active = false;
    e.currentTarget.style.cursor = 'grab';
    // If a drag happened, keep suppression for the current click only,
    // then clear it so subsequent button clicks are not blocked.
    if (didDrag.current) {
      requestAnimationFrame(() => {
        didDrag.current = false;
      });
    }
  }, []);

  // Auto-pan: on the FIRST mount only, position the canvas so the root node
  // is near the left-centre of the viewport.  Subsequent flow updates (silent
  // hashtag refreshes, live-data edits) deliberately do NOT reset the pan so
  // the user stays where they scrolled to.
  const initialPanDoneRef = useRef(false);
  useEffect(() => {
    if (initialPanDoneRef.current) return; // already panned — preserve position
    const el = panAreaRef.current;
    if (!el) return;
    const { height } = el.getBoundingClientRect();
    if (height === 0) return; // not yet laid out — wait for next tick
    initialPanDoneRef.current = true;
    setOffset({
      x: PAD_X,                   // root node at canvas-x=PAD_X → appears 2*PAD_X from left
      y: height / 2 - centreY,    // spine vertically centred in viewport
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedFlow]); // dep kept so the effect retries if flow arrives after first render

  // ── Step visibility (displayCondition) ─────────────────────────────────
  // Must be an IIFE (not useMemo) because liveCustomisationData mutates in-place.
  // A step is hidden when its displayCondition array has entries AND any condition fails.
  const visibleFlow = (() => {
    const findFirstPrimitiveByKey = (root, key) => {
      const seen = new Set();
      const stack = [root];
      while (stack.length) {
        const cur = stack.pop();
        if (!cur || typeof cur !== 'object') continue;
        if (seen.has(cur)) continue;
        seen.add(cur);

        if (Object.prototype.hasOwnProperty.call(cur, key)) {
          const v = cur[key];
          if (v === null || v === undefined) {
            // keep searching
          } else if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
            return v;
          } else if (Array.isArray(v) && v.length > 0) {
            const first = v[0];
            if (first && typeof first === 'object' && Object.prototype.hasOwnProperty.call(first, key)) {
              const inner = first[key];
              if (typeof inner === 'string' || typeof inner === 'number' || typeof inner === 'boolean') return inner;
            }
          }
        }

        if (Array.isArray(cur)) {
          for (let i = cur.length - 1; i >= 0; i--) stack.push(cur[i]);
        } else {
          for (const v of Object.values(cur)) stack.push(v);
        }
      }
      return undefined;
    };

    return transformedFlow.filter(step => {
      // pollConditionType: hide predictive/removal outcome nodes when no polls of that type exist
      const pollCondType = step.customisationConfig?.meta?.pollConditionType;
      if (pollCondType) {
        const polls = liveCustomisationData?.setup?.pollData?.polls;
        if (Array.isArray(polls)) {
          const hasType = polls.some(p => p?.type === pollCondType);
          if (!hasType) return false;
        }
      }

      const conditions = step.displayCondition;
      if (!conditions || conditions.length === 0) return true;
      return conditions.every(cond => {
        // Preferred format: tabKey + varKey + varName locate the live setting array; `var` is the field within val[0].
        // Legacy format: may omit tabKey/varKey/varName; in that case fall back to searching liveCustomisationData.
        const hasPath = !!(cond.tabKey && cond.varKey && cond.varName && cond.var);

        let actual;
        if (hasPath) {
          // Path: liveCustomisationData[tabKey][varKey][varName] → array → [0][var]
          const raw = liveCustomisationData?.[cond.tabKey]?.[cond.varKey]?.[cond.varName];
          const valObj = Array.isArray(raw) ? raw[0] : raw;
          actual = valObj?.[cond.var];
        } else if (cond?.var) {
          actual = findFirstPrimitiveByKey(liveCustomisationData, cond.var);
        }

        // If the live value isn't available, we can't verify the condition.
        // Default to hiding the step to avoid showing Team-only nodes in VS mode.
        if (actual === undefined || actual === null) return false;
        if (typeof cond.val === 'boolean' && typeof actual === 'string') {
          const lowered = actual.trim().toLowerCase();
          if (lowered === 'true' || lowered === 'false') actual = lowered === 'true';
        }
        if (typeof cond.val === 'boolean' && typeof actual === 'number' && (actual === 0 || actual === 1)) {
          actual = actual === 1;
        }
        // eslint-disable-next-line eqeqeq
        if (cond.evalCondition === '==') return actual == cond.val;
        if (cond.evalCondition === '!=') return actual != cond.val;
        if (cond.evalCondition === '>')  return actual >  cond.val;
        if (cond.evalCondition === '<')  return actual <  cond.val;
        if (cond.evalCondition === '>=') return actual >= cond.val;
        if (cond.evalCondition === '<=') return actual <= cond.val;
        return true;
      });
    });
  })();
  const visibleStepIds = new Set(visibleFlow.map(s => s.stepId));

  // ── Layout ────────────────────────────────────────────────────────
  const { maxRow, minCol, maxCol } = useMemo(() => ({
    maxRow: Math.max(...visibleFlow.map(s => s.layout?.row    ?? 0)),
    minCol: Math.min(...visibleFlow.map(s => s.layout?.column ?? CENTER_COL)),
    maxCol: Math.max(...visibleFlow.map(s => s.layout?.column ?? CENTER_COL)),
  }), [visibleFlow]);

  const upSlots   = CENTER_COL - minCol;
  const downSlots = maxCol - CENTER_COL;
  // These three values must NOT be useMemo. addVal/removeVal mutate
  // liveCustomisationData in-place so the same object reference is seen
  // by memos -- they never re-run. Inline IIFEs always read fresh data.

  // Per-team stack half-heights from live data.
  const halfHByFilter = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'waveGraphics') return;
      const wf      = step.customisationConfig?.meta?.teamFilter;
      if (wf === undefined) return;
      const wAssign   = step.customisationConfig?.meta?.teamAssign ?? wf;
      const handFilter = step.customisationConfig?.meta?.handFilter;
      const tabKey = step.customisationConfig?.tabKey;
      const varKey = step.customisationConfig?.varKey;
      const varName = step.customisationConfig?.variables?.[0]?.variableName;
      const live   = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName] : null;
      const allVals = live || step.populatedData?.variables?.[0]?.allValues || [];
      const items   = allVals.filter(v => (v.team === wAssign || v.team === 2) && (handFilter == null || v.hand === handFilter));
      map[wf] = (Math.max(items.length, 1) * (VOTE_CARD_H + VOTE_CARD_GAP) - VOTE_CARD_GAP) / 2;
    });
    return map;
  })();

  const waveMaxHalf = Object.values(halfHByFilter).length
    ? Math.max(...Object.values(halfHByFilter))
    : 0;

  // Per-step stack half-heights for disruptor-trigger nodes.
  const disruptorHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'disruptorTrigger') return;
      const selectLink = step.customisationConfig?.meta?.disruptorSelectDataLink;
      if (!selectLink) return;
      const parts = selectLink.split('.');
      if (parts.length < 4) return;
      const valueKey = parts[parts.length - 1];
      const groupKeys = parts.slice(0, -1); // tab.group.varName
      const liveGroup = liveCustomisationData ? getPathValue(liveCustomisationData, groupKeys) : null;
      const allVals = Array.isArray(liveGroup)
        ? liveGroup
        : (step.populatedData?.variables?.[0]?.allValues || []);
      const ids = (Array.isArray(allVals) ? allVals : [])
        .map(v => (v && typeof v === 'object') ? v[valueKey] : null)
        .filter(v => v !== null && v !== undefined && String(v).trim().length > 0);
      const count = Math.max(ids.length, 1);
      const cardsH = count * (DISRUPTOR_CARD_H + DISRUPTOR_CARD_GAP) - DISRUPTOR_CARD_GAP;
      map[step.stepId] = (cardsH + DISRUPTOR_HEADER_H + DISRUPTOR_FOOTER_H) / 2;
    });
    return map;
  })();

  const disruptorMaxHalf = Object.values(disruptorHalfHByStepId).length
    ? Math.max(...Object.values(disruptorHalfHByStepId))
    : 0;

  // Dynamic halfH for pollCards nodes — cards are individually collapsible, use collapsed height.
  const POLL_CARDS_COLLAPSED_H = 58;
  const pollCardsMaxHalf = (() => {
    let maxH = 0;
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'pollCards') return;
      if (pollCardHeightOverrides[step.stepId] !== undefined) {
        maxH = Math.max(maxH, pollCardHeightOverrides[step.stepId] / 2);
        return;
      }
      const tabKey  = step.customisationConfig?.tabKey;
      const varKey  = step.customisationConfig?.varKey;
      const varName = step.customisationConfig?.variables?.[0]?.variableName;
      const live    = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName] : null;
      const allVals = live || step.populatedData?.variables?.[0]?.allValues || [];
      const count   = Math.max(allVals.length, 1);
      maxH = Math.max(maxH, (count * (POLL_CARDS_COLLAPSED_H + POLL_CARD_GAP) - POLL_CARD_GAP) / 2);
    });
    return maxH;
  })();

  const pollAudioHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'pollAudio') return;
      const slots  = step.customisationConfig?.variables || [];
      const tabKey = step.customisationConfig?.tabKey;
      const varKey = step.customisationConfig?.varKey;
      let h = POLL_AUDIO_HEADER_H + 52; // header + bottom pad
      slots.forEach((slot, i) => {
        h += POLL_AUDIO_SLOT_LBL_H;
        const liveGroup = liveCustomisationData?.[tabKey]?.[varKey];
        const arr = liveGroup ? liveGroup[slot.variableName] : null;
        const files = Array.isArray(arr) ? arr.filter(item => item != null) : [];
        const toggleEnabled = slot.toggleVarName
          ? liveGroup?.toggleSFX?.[0]?.[slot.toggleVarName] === true
          : true;
        const showSlotCTA = !toggleEnabled || files.length === 0 || (files.length === 1 && files[0]?.playSFX === false);
        if (showSlotCTA) {
          h += POLL_AUDIO_EMPTY_H;
        } else {
          h += files.length * POLL_AUDIO_FILE_H + (files.length - 1) * POLL_AUDIO_FILE_GAP;
          if (slot.allowMultiple) h += POLL_AUDIO_FILE_GAP + POLL_AUDIO_ADD_BTN_H;
        }
        if (i < slots.length - 1) h += POLL_AUDIO_SLOT_GAP;
      });
      if (slots.length === 0) h += POLL_AUDIO_EMPTY_H;
      map[step.stepId] = h / 2;
    });
    return map;
  })();

  const audioStackHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'audioStack') return;
      const allVals = resolveStepItems(step);
      const count = Math.max(allVals.length, 1);
      // Grid layout: 1-3 items stay single-column, 4 items use a 2x2 grid,
      // 5+ items use a 3-column grid.
      const isGridMode = count > 1;
      const gridCols = count > 1 ? 3 : 1;
      const gridRows = Math.max(isGridMode ? Math.ceil(count / gridCols) : count, 1);
      // Add TOP_PAD/2 (= 10) to match the downward content shift applied in AudioStackNode
      // which increases the bottom visual extent by that amount.
      map[step.stepId] = (gridRows * (AUDIO_CARD_H + AUDIO_CARD_GAP) - AUDIO_CARD_GAP) / 2 + 10;
    });
    return map;
  })();

  // Dynamic half-width for audioStack nodes based on the full rendered grid width.
  // Audio stacks should be centered in their layout slot.
  const audioStackHalfWByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'audioStack') return;
      const allVals = resolveStepItems(step);
      const count = Math.max(allVals.length, 1);
      const isGridMode = count > 1;
      const gridCols = count > 1 ? 3 : 1;
      const gridTotalW = isGridMode ? gridCols * AUDIO_CARD_W + (gridCols - 1) * AUDIO_CARD_GAP : AUDIO_CARD_W;
      map[step.stepId] = gridTotalW - AUDIO_CARD_W / 2;
    });
    return map;
  })();

  const pollOutcomeHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'pollOutcome') return;
      const meta = step.customisationConfig?.meta || {};
      const hideDisruptorSection = meta.hideDisruptorSection === true;
      const disruptorLink = meta.disruptorLink;
      const audioTabKey = meta.audioTabKey;
      const audioGroupKey = meta.audioGroupKey;
      const audioVarName = meta.audioVarName;
      const audioAllowMultiple = meta.audioAllowMultiple ?? true;
      const audioGroup = audioTabKey && audioGroupKey
        ? liveCustomisationData?.[audioTabKey]?.[audioGroupKey]
        : null;
      const disruptorCount = (() => {
        if (hideDisruptorSection || !disruptorLink) return 0;
        const parts = disruptorLink.split('.');
        const valueKey = parts[parts.length - 1];
        const groupKeys = parts.slice(0, -1);
        const liveGroup = liveCustomisationData ? getPathValue(liveCustomisationData, groupKeys) : null;
        const fallbackVals = step.populatedData?.variables?.[0]?.allValues || [];
        const sourceVals = Array.isArray(liveGroup) ? liveGroup : fallbackVals;
        return (Array.isArray(sourceVals) ? sourceVals : [])
          .filter(v => {
            const id = (v && typeof v === 'object') ? v[valueKey] : null;
            return id !== null && id !== undefined && String(id).trim().length > 0;
          }).length;
      })();
      const audioItems = Array.isArray(audioGroup?.[audioVarName])
        ? audioGroup[audioVarName].filter(item => item != null && (item?.audioFilename || item?.filename || item?.srcUrl))
        : [];
      const audioToggleVarName = meta.audioToggleVarName;
      const audioToggleEnabled = audioToggleVarName
        ? audioGroup?.toggleSFX?.[0]?.[audioToggleVarName] === true
        : true;
      const showAudioCTA = !audioToggleEnabled || audioItems.length === 0 || (audioItems.length === 1 && audioItems[0]?.playSFX === false);
      const audioSectionH = !showAudioCTA
        ? audioItems.length * POLL_AUDIO_FILE_H
          + (audioItems.length - 1) * POLL_AUDIO_FILE_GAP
          + (audioAllowMultiple ? POLL_AUDIO_FILE_GAP + POLL_AUDIO_ADD_BTN_H : 0)
        : POLL_AUDIO_EMPTY_H;
      const disruptorRowsCount = Math.max(disruptorCount, 1);
      const disruptorSlotH = hideDisruptorSection
        ? 0
        : (disruptorRowsCount * 54 + Math.max(disruptorRowsCount - 1, 0) * 8);

      // Per-poll slots mode (predictive / removal outcome nodes)
      const usePerPollSlots = meta.usePerPollSlots === true;
      const pollConditionTypeStr = (() => {
        if (meta.pollConditionType) return String(meta.pollConditionType).toLowerCase();
        const title = String(meta.sectionTitle || step.stepTitle || '').toLowerCase();
        if (title.includes('predictive')) return 'predictive';
        if (title.includes('removal')) return 'removal';
        return null;
      })();
      const livePolls = liveCustomisationData?.setup?.pollData?.polls;
      const matchingPollCount = usePerPollSlots && Array.isArray(livePolls) && pollConditionTypeStr
        ? livePolls.filter(p => (p?.type || '').toLowerCase() === pollConditionTypeStr).length
        : 0;
      // PER_POLL_TITLE_H=22, DISRUPTOR_ROW_H=54, PER_POLL_GAP=8 — must match PollOutcomeNode
      const perPollAreaH = usePerPollSlots
        ? (matchingPollCount > 0
            ? matchingPollCount * (22 + 54) + Math.max(0, matchingPollCount - 1) * 8
            : 54)
        : 0;

      const contentH = usePerPollSlots
        ? (32 + 10 + 18 + perPollAreaH + 10 + 1 + 10 + 18 + audioSectionH + 16)
        : (hideDisruptorSection
            ? (32 + 10 + 18 + audioSectionH + 16)
            : (32 + 10 + 18 + disruptorSlotH + 10 + 1 + 10 + 18 + audioSectionH + 16));
      const totalH = Math.max(POLL_OUTCOME_H, contentH);
      map[step.stepId] = totalH / 2;
    });
    return map;
  })();

  const imageStackHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'imageStack') return;
      const items = resolveStepItems(step);
      map[step.stepId] = (Math.max(items.length, 1) * (IMAGE_STACK_CARD_H + IMAGE_STACK_CARD_GAP) - IMAGE_STACK_CARD_GAP) / 2;
    });
    return map;
  })();

  const particleStackHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      const kind = stepKind(step);
      if (kind !== 'particleImageStack' && kind !== 'particleColourStack') return;
      const items = resolveStepItems(step);
      map[step.stepId] = (Math.max(items.length, 1) * (IMAGE_STACK_CARD_H + IMAGE_STACK_CARD_GAP) - IMAGE_STACK_CARD_GAP) / 2;
    });
    return map;
  })();

  const countdownStackHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      const kind = stepKind(step);
      if (kind !== 'countdownImageStack' && kind !== 'countdownTextStack') return;
      const items = resolveStepItems(step);
      map[step.stepId] = (Math.max(items.length, 1) * (COUNTDOWN_CARD_H + COUNTDOWN_CARD_GAP) - COUNTDOWN_CARD_GAP) / 2;
    });
    return map;
  })();

  const videoStackHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'videoStack') return;
      const items = resolveStepItems(step);
      map[step.stepId] = (Math.max(items.length, 1) * (VIDEO_CARD_H + VIDEO_CARD_GAP) - VIDEO_CARD_GAP) / 2;
    });
    return map;
  })();

  const presetTextHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'presetTextNode') return;
      const items = resolveStepItems(step);
      const cardH = liveTextHeadersEnabled ? PRESET_TEXT_CARD_H : PRESET_TEXT_CARD_H_NO_HEADER;
      map[step.stepId] = (Math.max(items.length, 1) * (cardH + PRESET_TEXT_CARD_GAP) - PRESET_TEXT_CARD_GAP) / 2;
    });
    return map;
  })();

  const puzzleStackHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'puzzleStack') return;
      const tabKey  = step.customisationConfig?.tabKey;
      const varKey  = step.customisationConfig?.varKey;
      const varName = step.customisationConfig?.variables?.[0]?.variableName;
      const live    = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName] : null;
      const allVals = live || step.populatedData?.variables?.[0]?.allValues || [];
      const count   = Math.max(allVals.length, 1);
      map[step.stepId] = (count * (PUZZLE_CARD_H + PUZZLE_CARD_GAP) - PUZZLE_CARD_GAP) / 2;
    });
    return map;
  })();

  const slotReelsHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'slotReels') return;
      const slotSettings = Array.isArray(liveCustomisationData?.setup?.globalSettings?.slotSettings)
        ? liveCustomisationData.setup.globalSettings.slotSettings[0] || {}
        : {};
      const reelCount = Math.max(1, Math.min(5, Number(slotSettings?.reels) || 3));
      let maxPanelContentH = 56;
      for (let index = 0; index < reelCount; index += 1) {
        const options = liveCustomisationData?.setup?.[`options${index + 1}`]?.options;
        const isOptInReel = !!slotSettings?.[`useOptInList_reel${index + 1}`];
        if (isOptInReel) {
          maxPanelContentH = Math.max(maxPanelContentH, 56);
          continue;
        }
        const itemCount = Array.isArray(options) ? (options.filter(item => item != null).length || 1) : 1;
        const itemsH = itemCount * (SLOT_REELS_ITEM_H + SLOT_REELS_ITEM_GAP) - SLOT_REELS_ITEM_GAP;
        const panelContentH = itemsH + 18 + 50;
        maxPanelContentH = Math.max(maxPanelContentH, panelContentH);
      }
      const totalH = SLOT_REELS_HEADER_H + 56 + maxPanelContentH + 24;
      map[step.stepId] = totalH / 2;
    });
    return map;
  })();

  const slotReelsHalfWByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'slotReels') return;
      const slotSettings = Array.isArray(liveCustomisationData?.setup?.globalSettings?.slotSettings)
        ? liveCustomisationData.setup.globalSettings.slotSettings[0] || {}
        : {};
      const reelCount = Math.max(1, Math.min(5, Number(slotSettings?.reels) || 3));
      const totalW = SLOT_REELS_NODE_PADDING * 2 + reelCount * SLOT_REELS_COL_W + (reelCount - 1) * SLOT_REELS_COL_GAP;
      map[step.stepId] = totalW / 2;
    });
    return map;
  })();

  const slotResultsHalfHByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'slotResults') return;
      const cfg = step.customisationConfig;
      const varName = cfg?.variables?.[0]?.variableName;
      const liveVals = cfg?.tabKey && cfg?.varKey && varName
        ? liveCustomisationData?.[cfg.tabKey]?.[cfg.varKey]?.[varName]
        : null;
      const rows = Array.isArray(liveVals)
        ? liveVals.filter(item => item != null)
        : (step.populatedData?.variables?.[0]?.allValues || []).filter(item => item != null);
      const count = Math.max(rows.length, 1);
      const totalH = SLOT_RESULTS_HEADER_H + count * (SLOT_RESULTS_ROW_H + SLOT_RESULTS_ROW_GAP) - SLOT_RESULTS_ROW_GAP + SLOT_RESULTS_ADD_H + 18;
      map[step.stepId] = totalH / 2;
    });
    return map;
  })();

  const renderedHalfH = (step) => {
    if (stepKind(step) === 'pollCards' && pollCardHeightOverrides[step.stepId] !== undefined) {
      return pollCardHeightOverrides[step.stepId] / 2;
    }
    if (stepKind(step) === 'imageStack' && imageStackHalfHByStepId[step.stepId] !== undefined) {
      return imageStackHalfHByStepId[step.stepId];
    }
    if ((stepKind(step) === 'particleImageStack' || stepKind(step) === 'particleColourStack') && particleStackHalfHByStepId[step.stepId] !== undefined) {
      return particleStackHalfHByStepId[step.stepId];
    }
    if ((stepKind(step) === 'countdownImageStack' || stepKind(step) === 'countdownTextStack') && countdownStackHalfHByStepId[step.stepId] !== undefined) {
      return countdownStackHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'videoStack' && videoStackHalfHByStepId[step.stepId] !== undefined) {
      return videoStackHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'presetTextNode' && presetTextHalfHByStepId[step.stepId] !== undefined) {
      return presetTextHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'audioStack' && audioStackHalfHByStepId[step.stepId] !== undefined) {
      return audioStackHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'pollAudio' && pollAudioHalfHByStepId[step.stepId] !== undefined) {
      return pollAudioHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'pollOutcome' && pollOutcomeHalfHByStepId[step.stepId] !== undefined) {
      return pollOutcomeHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'goalsNode') return GOALS_NODE_H / 2;
    if (stepKind(step) === 'puzzleStack' && puzzleStackHalfHByStepId[step.stepId] !== undefined) {
      return puzzleStackHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'slotReels' && slotReelsHalfHByStepId[step.stepId] !== undefined) {
      return slotReelsHalfHByStepId[step.stepId];
    }
    if (stepKind(step) === 'slotResults' && slotResultsHalfHByStepId[step.stepId] !== undefined) {
      return slotResultsHalfHByStepId[step.stepId];
    }
    // Use live-data-aware disruptor heights so expanding disruptor panels
    // push neighbours down correctly.
    if (stepKind(step) === 'disruptorTrigger' && disruptorHalfHByStepId[step.stepId] !== undefined) {
      return disruptorHalfHByStepId[step.stepId];
    }
    return halfH(step);
  };

  const pollAudioMaxHalf = Object.values(pollAudioHalfHByStepId).length
    ? Math.max(...Object.values(pollAudioHalfHByStepId))
    : 0;

  const imageStackMaxHalf = Object.values(imageStackHalfHByStepId).length
    ? Math.max(...Object.values(imageStackHalfHByStepId))
    : 0;

  const particleStackMaxHalf = Object.values(particleStackHalfHByStepId).length
    ? Math.max(...Object.values(particleStackHalfHByStepId))
    : 0;

  const countdownStackMaxHalf = Object.values(countdownStackHalfHByStepId).length
    ? Math.max(...Object.values(countdownStackHalfHByStepId))
    : 0;

  const videoStackMaxHalf = Object.values(videoStackHalfHByStepId).length
    ? Math.max(...Object.values(videoStackHalfHByStepId))
    : 0;

  const presetTextMaxHalf = Object.values(presetTextHalfHByStepId).length
    ? Math.max(...Object.values(presetTextHalfHByStepId))
    : 0;

  const audioStackMaxHalf = Object.values(audioStackHalfHByStepId).length
    ? Math.max(...Object.values(audioStackHalfHByStepId))
    : 0;

  const pollOutcomeMaxHalf = Object.values(pollOutcomeHalfHByStepId).length
    ? Math.max(...Object.values(pollOutcomeHalfHByStepId))
    : 0;

  const puzzleStackMaxHalf = Object.values(puzzleStackHalfHByStepId).length
    ? Math.max(...Object.values(puzzleStackHalfHByStepId))
    : 0;

  const slotReelsMaxHalf = Object.values(slotReelsHalfHByStepId).length
    ? Math.max(...Object.values(slotReelsHalfHByStepId))
    : 0;

  const slotResultsMaxHalf = Object.values(slotResultsHalfHByStepId).length
    ? Math.max(...Object.values(slotResultsHalfHByStepId))
    : 0;

  const nodeMaxH    = Math.max(
    CIRCLE_R,
    CARD_H / 2,
    PARTICLE_SETTINGS_H / 2,
    COUNTDOWN_SETTINGS_H / 2,
    VIDEO_SETTINGS_H / 2,
    COUNTDOWN_TIMER_H / 2,
    waveMaxHalf,
    disruptorMaxHalf,
    WINNER_NODE_H / 2,
    pollCardsMaxHalf,
    pollAudioMaxHalf,
    pollOutcomeMaxHalf,
    imageStackMaxHalf,
    particleStackMaxHalf,
    countdownStackMaxHalf,
    videoStackMaxHalf,
    presetTextMaxHalf,
    audioStackMaxHalf,
    GOALS_NODE_H / 2,
    puzzleStackMaxHalf,
    slotReelsMaxHalf,
    slotResultsMaxHalf,
    DIAMOND_H / 2,
    OUTCOME_H / 2
  );

  // Per-step actual half-widths for GoalsNode (which can be wider than CARD_W).
  const goalsHalfWByStepId = (() => {
    const map = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'goalsNode') return;
      const tabKey  = step.customisationConfig?.tabKey;
      const varKey  = step.customisationConfig?.varKey;
      const varName = step.customisationConfig?.variables?.[0]?.variableName;
      const liveVals = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
        : null;
      const rawGoals = Array.isArray(liveVals)
        ? liveVals.filter(Boolean)
        : (step.populatedData?.variables?.[0]?.allValues || []).filter(Boolean);
      const count = Math.max(rawGoals.length, 1);
      map[step.stepId] = goalsNodeHalfW(count);
    });
    return map;
  })();

  // Compute the actual rendered height of a trigger step's node, mirroring TriggerNode's nodeH logic.
  // A trigger step that has configured hashtags will be taller than TRIG_H.
  const triggerNodeH = (trigStep) => {
    if (!trigStep) return TRIG_H;
    // If the user is actively editing, use the live draft height so layout reflows immediately.
    if (draftHeightOverrides[trigStep.stepId] !== undefined) return draftHeightOverrides[trigStep.stepId];
    const config = trigStep.triggerConfig || {};
    const triggers = trigStep.configuredTriggers || [];
    const linkedCommands = Array.isArray(config.linkedCommands) ? config.linkedCommands.filter(Boolean) : [];
    const primaryCommand = linkedCommands[0] || triggers?.[0]?.command || null;
    const isHashtagNode = /hashtag/i.test(trigStep.stepTitle || '') || /hashtag/i.test(trigStep.stepDescription || '');
    const showEditor = isHashtagNode && config.triggerType === 'viewer' && !!primaryCommand;
    if (!showEditor) return TRIG_H;
    const primaryHashtags = (() => {
      if (!primaryCommand) return [];
      const all = triggers
        .filter(tt => tt.command === primaryCommand)
        .flatMap(tt => (tt.hashtags || []).map(h => (typeof h === 'string' ? h : h?.hashtag)).filter(Boolean));
      return Array.from(new Set(all));
    })();
    const tagCount = Math.max(primaryHashtags.length, 1);
    return Math.max(TRIG_H, tagCount * 28 + 28);
  };

  const MIN_BETWEEN = HASH_GAP + TRIG_H + 16;
  // Collect all visible waveGraphics steps sorted by teamFilter so we can space them
  // deterministically (2 teams in VS mode, 4 teams in Team mode).
  const wgSteps = visibleFlow
    .filter(s => stepKind(s) === 'waveGraphics' && s.customisationConfig?.meta?.teamFilter !== undefined)
    .sort((a, b) => a.customisationConfig.meta.teamFilter - b.customisationConfig.meta.teamFilter);

  // Content-driven spacing: each adjacent gap is based on the *two* teams' stack heights.
  // This prevents a tall stack in one team from inflating spacing for every team.
  const wgHalfHs = wgSteps.map(s => {
    const wf = s.customisationConfig?.meta?.teamFilter;
    return (wf !== undefined && wf !== null) ? (halfHByFilter[wf] ?? CIRCLE_R) : CIRCLE_R;
  });

  // Per-wgStep: the actual height their child hashtag trigger node will render at.
  // Used so adjacent teams are spaced far enough apart when hashtag nodes are tall.
  const wgHashNodeHs = wgSteps.map(s => {
    const hashStepId = s.connectsTo?.[0];
    const hashStep   = hashStepId ? visibleFlow.find(vs => vs.stepId === hashStepId) : null;
    return triggerNodeH(hashStep);
  });

  // Gap between adjacent waveGraphics teams: must accommodate the LOWER edge of the upper
  // team's hashtag node AND the UPPER edge of the lower team's card stack.
  const wgGaps = wgHalfHs.slice(0, -1).map((h, i) => {
    const upperHashH = wgHashNodeHs[i];       // height of upper team's hashtag node
    const lowerH     = wgHalfHs[i + 1] ?? CIRCLE_R;
    // The upper hashtag node extends from: teamY + upperHalfH + HASH_GAP (top of node)
    //   to: teamY + upperHalfH + HASH_GAP + upperHashH (bottom of node)
    // The lower team's stack top is at: lowerTeamY - lowerH
    // Require lowerTeamY - lowerH >= teamY + upperHalfH + HASH_GAP + upperHashH + padding
    // → gap between team centres >= upperH + HASH_GAP + upperHashH + padding + lowerH
    return h + HASH_GAP + upperHashH + 16 + lowerH;
  });

  const teamSpread = wgGaps.reduce((sum, g) => sum + g, 0); // total Y distance from first to last team centre
  const topHalf = wgHalfHs.length ? (wgHalfHs[0] ?? CIRCLE_R) : (waveMaxHalf || CIRCLE_R);
  const bottomHalf = wgHalfHs.length ? (wgHalfHs[wgHalfHs.length - 1] ?? CIRCLE_R) : (waveMaxHalf || CIRCLE_R);
  const bottomHashH = wgHashNodeHs.length ? wgHashNodeHs[wgHashNodeHs.length - 1] : TRIG_H;

  // Pre-compute grid extents from actual per-step rendered heights.
  const _GRID_GAP = 16;

  // Branch groups: maximal contiguous row ranges that contain at least one
  // off-spine node. Each group gets its own independent Y layout so that
  // separate expansions of the spine don't force each other's column heights.
  const branchGroups = (() => {
    const rowsWithBranch = new Set();
    visibleFlow.forEach(step => {
      if ((step.layout?.column ?? CENTER_COL) !== CENTER_COL)
        rowsWithBranch.add(step.layout?.row ?? 0);
    });
    const sorted = [...rowsWithBranch].sort((a, b) => a - b);
    const groups = [];
    sorted.forEach(row => {
      if (groups.length === 0 || row > groups[groups.length - 1].maxRow + 1) {
        groups.push({ minRow: row, maxRow: row });
      } else {
        groups[groups.length - 1].maxRow = row;
      }
    });
    return groups;
  })();

  const branchGroupIndexByRow = (() => {
    const map = {};
    branchGroups.forEach((g, i) => {
      for (let r = g.minRow; r <= g.maxRow; r++) map[r] = i;
    });
    return map;
  })();

  const maxGridUpwardH = (() => {
    let max = 0;
    branchGroups.forEach(group => {
      const colMaxH = {};
      let spineHalfH = 0;
      visibleFlow.forEach(step => {
        const c = step.layout?.column ?? CENTER_COL;
        const r = step.layout?.row ?? 0;
        if (r < group.minRow || r > group.maxRow) return;
        if (c === CENTER_COL) { spineHalfH = Math.max(spineHalfH, renderedHalfH(step)); return; }
        if (c >= CENTER_COL) return;
        colMaxH[c] = Math.max(colMaxH[c] ?? 0, renderedHalfH(step) * 2);
      });
      // Start from the top edge of the spine node so off-spine cells don't overlap it.
      // Use a larger margin when the spine is taller than a standard card.
      const upSpineGap = spineHalfH > CARD_H / 2 ? _GRID_GAP * 2 : _GRID_GAP;
      let upH = spineHalfH;
      Object.keys(colMaxH).map(Number).sort((a, b) => b - a).forEach((c, i) => {
        upH += (i === 0 ? upSpineGap : _GRID_GAP) + colMaxH[c];
      });
      max = Math.max(max, upH);
    });
    return max;
  })();
  const maxGridDownwardH = (() => {
    let max = 0;
    branchGroups.forEach(group => {
      const colMaxH = {};
      let spineHalfH = 0;
      visibleFlow.forEach(step => {
        const c = step.layout?.column ?? CENTER_COL;
        const r = step.layout?.row ?? 0;
        if (r < group.minRow || r > group.maxRow) return;
        if (c === CENTER_COL) { spineHalfH = Math.max(spineHalfH, renderedHalfH(step)); return; }
        if (c <= CENTER_COL) return;
        colMaxH[c] = Math.max(colMaxH[c] ?? 0, renderedHalfH(step) * 2);
      });
      // Start from the bottom edge of the spine node.
      // Use a larger margin when the spine is taller than a standard card.
      const downSpineGap = spineHalfH > CARD_H / 2 ? _GRID_GAP * 2 : _GRID_GAP;
      let downH = spineHalfH;
      Object.keys(colMaxH).map(Number).sort((a, b) => a - b).forEach((c, i) => {
        downH += (i === 0 ? downSpineGap : _GRID_GAP) + colMaxH[c];
      });
      max = Math.max(max, downH);
    });
    return max;
  })();

  // Spine nodes (col = CENTER_COL) that expand vertically (e.g. audioStack)
  // are not captured by maxGridUpwardH/maxGridDownwardH. Track their half-heights
  // separately so centreY and svgH are tall enough to accommodate them.
  const spineMaxHalfH = (() => {
    let max = 0;
    visibleFlow.forEach(step => {
      if ((step.layout?.column ?? CENTER_COL) !== CENTER_COL) return;
      const k = stepKind(step);
      const h = renderedHalfH(step);
      // Add wrapper padding for stack nodes (wrapperPadY = 12 in AudioStackNode / VideoStackNode)
      // audioStack also has a WRAPPER_HEADER_H = 36px header band when stepTitle is set, so add that too.
      const extra = k === 'audioStack' ? 50 : k === 'videoStack' ? 14 : 0;
      max = Math.max(max, h + extra);
    });
    return max;
  })();

  // centreY: Y of main spine nodes. Large enough that the topmost branch
  // does not clip above PAD_Y regardless of actual rendered node heights.
  const centreY = Math.max(
    PAD_Y + maxGridUpwardH,
    PAD_Y + spineMaxHalfH,
    PAD_Y + topHalf + teamSpread / 2
  );

  const resetViewToInitial = useCallback(() => {
    const el = panAreaRef.current;
    if (!el) return;
    const { height } = el.getBoundingClientRect();
    setOffset({
      x: PAD_X,
      y: height / 2 - centreY,
    });
    if (typeof onZoomChange === 'function') onZoomChange(1);
  }, [centreY, onZoomChange]);

  useEffect(() => {
    if (typeof onResetView === 'function') {
      onResetView(resetViewToInitial);
    }
  }, [onResetView, resetViewToInitial]);
  // svgH: tall enough for the bottommost branch + its hashtag trigger.
  const svgH = Math.max(
    centreY + maxGridDownwardH + PAD_Y,
    centreY + spineMaxHalfH + PAD_Y,
    centreY + teamSpread / 2 + bottomHalf + HASH_GAP + bottomHashH + PAD_Y
  );
  const svgW    = PAD_X * 2 + (maxRow + 1) * ROW_STEP + CARD_W;

  const stepMap = useMemo(() => Object.fromEntries(visibleFlow.map(s => [s.stepId, s])), [visibleFlow]);

  // Map each step to its parent (the step that connects TO it)
  const parentMap = useMemo(() => {
    const map = {};
    visibleFlow.forEach(from => {
      (from.connectsTo || []).filter(id => visibleStepIds.has(id)).forEach(toId => { map[toId] = from; });
    });
    return map;
  }, [visibleFlow]);

  const incomingParentsMap = useMemo(() => {
    const map = {};
    visibleFlow.forEach(from => {
      (from.connectsTo || []).filter(id => visibleStepIds.has(id)).forEach(toId => {
        if (!map[toId]) map[toId] = [];
        map[toId].push(from);
      });
    });
    return map;
  }, [visibleFlow, visibleStepIds]);

  const multiImageStackTargetIds = useMemo(() => {
    const set = new Set();
    Object.entries(incomingParentsMap).forEach(([childId, parents]) => {
      const count = parents.filter(parent => stepKind(parent) === 'imageStack').length;
      if (count >= 2) set.add(childId);
    });
    return set;
  }, [incomingParentsMap]);

  const multiImageStackParentIds = useMemo(() => {
    const counts = {};
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'imageStack') return;
      const parentId = parentMap[step.stepId]?.stepId;
      if (!parentId) return;
      counts[parentId] = (counts[parentId] ?? 0) + 1;
    });

    return new Set(Object.entries(counts)
      .filter(([, count]) => count >= 2)
      .map(([parentId]) => parentId));
  }, [visibleFlow, parentMap]);

  // Plain IIFE (not memo) -- always reflects current live item counts.
  const yOverrides = (() => {
    const map = {};

    // Place teams using the variable gaps computed above.
    // First team is centred at (centreY - teamSpread/2), then we add per-pair gaps.
    let oy = centreY - teamSpread / 2;
    wgSteps.forEach((step, i) => {
      const wf = step.customisationConfig.meta.teamFilter;
      map[step.stepId] = oy;
      const parent = parentMap[step.stepId];
      if (parent) map[parent.stepId] = oy;
      const hashId = step.connectsTo?.[0];
      if (hashId) {
        const myHalfH = halfHByFilter[wf] ?? CIRCLE_R;
        map[hashId] = oy + myHalfH + HASH_GAP + TRIG_H / 2;
      }

      const gap = wgGaps[i];
      if (gap !== undefined) oy += gap;
    });
    // ── Grid-based Y placement ────────────────────────────────────────────────
    // Nodes are grouped into (layout.row, layout.column) cells. Within each
    // layout.row group:
    //   • column === CENTER_COL  → centreY (spine nodes)
    //   • column  < CENTER_COL  → stacked upward from centreY (closest col first)
    //   • column  > CENTER_COL  → stacked downward from centreY (closest col first)
    // Multiple nodes sharing the same cell are spread symmetrically within it.
    const GRID_GAP = 16;
    const wgPlacedIds = new Set(Object.keys(map)); // wgSteps already placed above

    // Build cell map: (row, col) → { row, col, steps[] }
    const cells = {};
    visibleFlow.forEach(step => {
      if (wgPlacedIds.has(step.stepId)) return;
      const r = step.layout?.row ?? 0;
      const c = step.layout?.column ?? CENTER_COL;
      const key = `${r}|${c}`;
      if (!cells[key]) cells[key] = { row: r, col: c, steps: [] };
      cells[key].steps.push(step);
    });

    // Total vertical height of a cell (sum of node diameters + inner gaps)
    const cellTotalH = (steps) =>
      steps.reduce((sum, s) => sum + renderedHalfH(s) * 2, 0)
      + Math.max(0, steps.length - 1) * GRID_GAP;

    // Distribute nodes within a cell symmetrically around cellCY
    const placeCell = (steps, cellCY) => {
      if (steps.length === 1) {
        map[steps[0].stepId] = cellCY;
        return;
      }
      const sorted = [...steps].sort((a, b) => ny(a, 0) - ny(b, 0));
      const heights = sorted.map(s => renderedHalfH(s) * 2);
      const totalH = heights.reduce((sum, h) => sum + h, 0) + (sorted.length - 1) * GRID_GAP;
      let y = cellCY - totalH / 2;
      sorted.forEach((step, i) => {
        map[step.stepId] = y + heights[i] / 2;
        y += heights[i] + GRID_GAP;
      });
    };

    // Per-branch-group column Y: each contiguous expansion of the spine gets
    // its own independent Y layout. Nodes sharing a column within the same
    // branch group align vertically; separate expansions are independent.
    const groupColYMaps = branchGroups.map(group => {
      const slotHByCol = {};
      // Track max spine halfH within this group so above/below cells start
      // from the actual top/bottom edges of the spine node, not from centreY.
      let spineHalfInGroup = 0;
      let hasSlotReelsSpine = false;
      let slotReelsAboveCol = null; // col < CENTER_COL that contains a slotReels node
      Object.values(cells).forEach(cell => {
        if (cell.row < group.minRow || cell.row > group.maxRow) return;
        if (cell.col === CENTER_COL) {
          spineHalfInGroup = Math.max(spineHalfInGroup, cellTotalH(cell.steps) / 2);
          if (cell.steps.some(step => stepKind(step) === 'slotReels')) hasSlotReelsSpine = true;
          return;
        }
        slotHByCol[cell.col] = Math.max(slotHByCol[cell.col] ?? 0, cellTotalH(cell.steps));
        if (cell.col < CENTER_COL && cell.steps.some(step => stepKind(step) === 'slotReels')) {
          slotReelsAboveCol = cell.col;
        }
      });
      const colY = {};
      // When the spine node is taller than a standard card, use a larger gap
      // so adjacent branch nodes don't hug the edge of the spine rect.
      const spineExtraGap = spineHalfInGroup > CARD_H / 2 ? GRID_GAP : 0;
      const slotReelsBottomGap = hasSlotReelsSpine ? 200 : 0;
      let topEdge = centreY - spineHalfInGroup;
      Object.keys(slotHByCol).map(Number).filter(c => c < CENTER_COL).sort((a, b) => b - a).forEach((col, i) => {
        const h = slotHByCol[col];
        topEdge -= (i === 0 ? GRID_GAP + spineExtraGap : GRID_GAP * 5) + h;
        colY[col] = topEdge + h / 2;
      });
      // When slotReels lives in an above-center column its tall body can reach
      // close to centreY; push below-center columns down past its actual bottom
      // edge so they don't visually touch the reel node.
      let bottomEdge = centreY + spineHalfInGroup;
      if (slotReelsAboveCol !== null && colY[slotReelsAboveCol] !== undefined) {
        const reelsBottom = colY[slotReelsAboveCol] + slotHByCol[slotReelsAboveCol] / 2;
        bottomEdge = Math.max(bottomEdge, reelsBottom + 80);
      }
      Object.keys(slotHByCol).map(Number).filter(c => c > CENTER_COL).sort((a, b) => a - b).forEach((col, i) => {
        const h = slotHByCol[col];
        const cy = bottomEdge + (i === 0 ? GRID_GAP + spineExtraGap + slotReelsBottomGap : GRID_GAP * 5) + h / 2;
        bottomEdge = cy + h / 2;
        colY[col] = cy;
      });
      return colY;
    });

    // Place every cell: spine cells at centreY, off-spine cells at their
    // branch group's column Y (or centreY if the cell has no group).
    Object.values(cells).forEach(cell => {
      if (cell.col === CENTER_COL) { placeCell(cell.steps, centreY); return; }
      const gi = branchGroupIndexByRow[cell.row] ?? -1;
      placeCell(cell.steps, gi >= 0 ? (groupColYMaps[gi][cell.col] ?? centreY) : centreY);
    });

    // Snap addButton controls to their child's final Y so the + button sits at
    // the visual midpoint of its connected stack node.
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'addButton') return;
      const child = (step.connectsTo || []).map(id => stepMap[id])
        .find(s => s && map[s.stepId] !== undefined);
      if (child) map[step.stepId] = map[child.stepId];
    });



    return map;
  })();

  const imageStackBranchMidYByParent = (() => {
    const groups = {};
    visibleFlow.forEach(step => {
      if (!isBranchStackKind(step)) return;
      const parent = parentMap[step.stepId];
      if (!parent) return;
      const pid = parent.stepId;
      if (!groups[pid]) groups[pid] = [];
      groups[pid].push(step);
    });

    const map = {};
    Object.entries(groups).forEach(([pid, group]) => {
      const parent = stepMap[pid];
      const parentY = yOverrides[pid] ?? (parent?.layout ? ny(parent, centreY) : centreY);
      if (group.length < 2) {
        map[pid] = parentY;
        return;
      }
      const sorted = [...group].sort((a, b) => (yOverrides[a.stepId] ?? centreY) - (yOverrides[b.stepId] ?? centreY));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const firstCenterY = yOverrides[first.stepId] ?? centreY;
      const lastCenterY = yOverrides[last.stepId] ?? centreY;
      map[pid] = (firstCenterY + lastCenterY) / 2;
    });
    return map;
  })();

  // ── Longest-path row assignment ───────────────────────────────────────────
  // Compute the display column (X depth) for every step using the LONGEST path
  // from the root, not the shortest (BFS). This ensures that nodes reachable by
  // multiple routes (e.g. Vote End, reachable via both ingame_audio and polls_node)
  // are placed one column to the RIGHT of the deepest upstream node, rather than
  // the shallowest. Without this, Vote End would land at the same column as
  // polls_node (both at BFS depth 2), leaving no horizontal gap for the exit line.
  const bfsRows = useMemo(() => {
    const depths = {};
    // Find root: a step that no other step connects to
    const allTargets = new Set(visibleFlow.flatMap(s => s.connectsTo || []));
    const root = visibleFlow.find(s => !allTargets.has(s.stepId));
    if (!root) return depths;

    // Topological sort via Kahn's algorithm (handles DAG with converging paths)
    const inDegree = {};
    visibleFlow.forEach(s => { inDegree[s.stepId] = 0; });
    visibleFlow.forEach(s => {
      (s.connectsTo || []).forEach(id => {
        if (id in inDegree) inDegree[id] = (inDegree[id] || 0) + 1;
      });
    });
    const topoQueue = visibleFlow.filter(s => inDegree[s.stepId] === 0).map(s => s.stepId);
    const topoOrder = [];
    const tempIn = { ...inDegree };
    while (topoQueue.length) {
      const id = topoQueue.shift();
      topoOrder.push(id);
      const s = stepMap[id];
      (s?.connectsTo || []).forEach(nextId => {
        if (!(nextId in tempIn)) return;
        tempIn[nextId] -= 1;
        if (tempIn[nextId] === 0) topoQueue.push(nextId);
      });
    }

    // Relax in topological order: each node gets max(parent depth + 1)
    visibleFlow.forEach(s => { depths[s.stepId] = 0; });
    topoOrder.forEach(id => {
      const s = stepMap[id];
      (s?.connectsTo || []).forEach(nextId => {
        if (!(nextId in depths)) return;
        const candidate = (depths[id] ?? 0) + 1;
        if (candidate > (depths[nextId] ?? 0)) {
          depths[nextId] = candidate;
        }
      });
    });

    return depths;
  }, [visibleFlow, stepMap]);

  // X-position overrides for every step, derived purely from BFS depth.
  // Connector and node renderers use these automatically.
  const xOverrides = useMemo(() => {
    const map = {};
    const nodesByDepth = {};
    Object.entries(bfsRows).forEach(([stepId, depth]) => {
      if (!nodesByDepth[depth]) nodesByDepth[depth] = [];
      const step = stepMap[stepId];
      if (step) nodesByDepth[depth].push(step);
    });

    const depths = Object.keys(nodesByDepth).map(Number);
    const maxDepth = depths.length ? Math.max(...depths) : 0;

    const getRenderedLeftExtent = (step) => {
      if (!step) return CARD_W / 2;
      if (stepKind(step) === 'audioStack') return AUDIO_CARD_W / 2;
      if (stepKind(step) === 'slotReels' && slotReelsHalfWByStepId[step.stepId] !== undefined) return slotReelsHalfWByStepId[step.stepId];
      return halfW(step);
    };
    
    const getRenderedRightExtent = (step) => {
      if (!step) return CARD_W / 2;
      if (stepKind(step) === 'audioStack' && audioStackHalfWByStepId[step.stepId] !== undefined) {
        return audioStackHalfWByStepId[step.stepId];
      }
      if (stepKind(step) === 'slotReels' && slotReelsHalfWByStepId[step.stepId] !== undefined) {
        return slotReelsHalfWByStepId[step.stepId];
      }
      return halfW(step);
    };

    // Compute each depth's widest half-width footprint from rendered node geometry.
    const getDepthExtents = (depth) => {
      const steps = nodesByDepth[depth] || [];
      if (!steps.length) return { left: CARD_W / 2, right: CARD_W / 2 };

      const left = Math.max(...steps.map(s => getRenderedLeftExtent(s)));
      const right = Math.max(...steps.map(s => getRenderedRightExtent(s)));
      return { left, right };
    };

    const depthX = { 0: nx({ layout: { row: 0 } }) };
    const DEPTH_GAP = 140; // ensure enough room for Custom AudioStack exits (34px) + button clusters
    for (let depth = 1; depth <= maxDepth; depth += 1) {
      const prev = getDepthExtents(depth - 1);
      const curr = getDepthExtents(depth);
      const minStep = prev.right + curr.left + DEPTH_GAP;
      depthX[depth] = depthX[depth - 1] + Math.max(ROW_STEP, minStep);
    }

    Object.entries(bfsRows).forEach(([stepId, depth]) => {
      map[stepId] = depthX[depth] ?? nx({ layout: { row: depth } });
    });
    // waveGraphics steps render their child trigger (hashtag) at the same X column
    // as themselves — the hashtag sits below the card stack, not one column to the right.
    // Override those children to share the parent waveGraphics X, then propagate
    // downstream so the gap to Vote End is tight (same logic as zapperObjects).
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'waveGraphics') return;
      const waveX = map[step.stepId];
      if (waveX === undefined) return;
      (step.connectsTo || []).forEach(childId => {
        const child = stepMap[childId];
        if (child && stepKind(child) === 'trigger') {
          map[childId] = waveX;
          // Propagate: pull every downstream step 1 column closer
          const pullQ = [{ id: childId, x: waveX }];
          const seen  = new Set([childId]);
          while (pullQ.length) {
            const { id, x } = pullQ.shift();
            const s = stepMap[id];
            if (!s) continue;
            (s.connectsTo || []).forEach(nextId => {
              if (seen.has(nextId)) return;
              seen.add(nextId);
              map[nextId] = x + ROW_STEP;
              pullQ.push({ id: nextId, x: x + ROW_STEP });
            });
          }
        }
      });
    });
    // zapperObjects also renders its child trigger (zap_hashtag) inline at the same X column.
    // Because the trigger is collapsed into the objects column (no own column), all downstream
    // steps must be pulled 1 column closer so the gap to Vote End is tight.
    visibleFlow.forEach(step => {
      if (stepKind(step) !== 'zapperObjects') return;
      const zapX = map[step.stepId];
      if (zapX === undefined) return;
      (step.connectsTo || []).forEach(childId => {
        const child = stepMap[childId];
        if (child && stepKind(child) === 'trigger') {
          map[childId] = zapX;
          // Propagate: set every downstream step to zapX + relativeDepth * ROW_STEP
          const pullQ = [{ id: childId, x: zapX }];
          const seen  = new Set([childId]);
          while (pullQ.length) {
            const { id, x } = pullQ.shift();
            const s = stepMap[id];
            if (!s) continue;
            (s.connectsTo || []).forEach(nextId => {
              if (seen.has(nextId)) return;
              seen.add(nextId);
              map[nextId] = x + ROW_STEP;
              pullQ.push({ id: nextId, x: x + ROW_STEP });
            });
          }
        }
      });
    });

    // Poll cards may render inline side trigger panels (predictive / removal poll types).
    // pollCards exit stem sits at polls_node_X + CARD_W/2 + 18. With polls_node now
    // at BFS depth 4 (X=1360) and vote_end at depth 5 (X=1680), the clearance from
    // the exit stem (1478) to vote_end left edge (1622) is already 144px — no extra
    // push required. Setting extraShift=0 keeps the connector short.
    const pollCardsStep = visibleFlow.find(step => stepKind(step) === 'pollCards');
    if (pollCardsStep) {
      const extraShift = 0;
      const seen = new Set();
      const queue = [...(pollCardsStep.connectsTo || [])];
      while (queue.length) {
        const stepId = queue.shift();
        if (!stepId || seen.has(stepId)) continue;
        seen.add(stepId);
        const step = stepMap[stepId];
        if (!step) continue;
        map[stepId] = (map[stepId] ?? (step.layout ? nx(step) : 0)) + extraShift;
        (step.connectsTo || []).forEach(nextId => {
          if (!seen.has(nextId)) queue.push(nextId);
        });
      }
    }

    // Horizontally align the pollAudio panel with the related pollCards column so
    // the audio panel sits directly above the poll panels. Keep this as an X
    // override on the node itself so connectors remain attached to the moved card.
    visibleFlow.forEach(audioStep => {
      if (stepKind(audioStep) !== 'pollAudio') return;
      const audioParent = parentMap[audioStep.stepId];
      if (!audioParent) return;

      let relatedPollStep = visibleFlow.find(
        s => s.stepId !== audioStep.stepId
          && parentMap[s.stepId]?.stepId === audioParent.stepId
          && stepKind(s) === 'pollCards'
      ) ?? null;

      if (!relatedPollStep) {
        const relaySibling = visibleFlow.find(
          s => s.stepId !== audioStep.stepId
            && parentMap[s.stepId]?.stepId === audioParent.stepId
            && stepKind(s) === 'circle'
            && (s.connectsTo || []).some(cid => {
              const child = stepMap[cid];
              return child && stepKind(child) === 'pollCards';
            })
        );
        if (relaySibling) {
          relatedPollStep = (relaySibling.connectsTo || [])
            .map(cid => stepMap[cid])
            .find(child => child && stepKind(child) === 'pollCards') ?? null;
        }
      }

      if (!relatedPollStep) return;
      if (map[relatedPollStep.stepId] === undefined) return;
      map[audioStep.stepId] = map[relatedPollStep.stepId];
    });

    // GoalsNode can be significantly wider than CARD_W when many goals are configured.
    // Shift all downstream nodes to the right so the connector from GoalsNode exits
    // cleanly past the actual right edge of the rendered goals strip.
    // Use populatedData (seeder defaults) here so the useMemo stays deterministic;
    // fine-grained live adjustments are handled via fromXAnchorOverride in the render.
    visibleFlow.forEach(goalsStep => {
      if (stepKind(goalsStep) !== 'goalsNode') return;
      const tabKey  = goalsStep.customisationConfig?.tabKey;
      const varKey  = goalsStep.customisationConfig?.varKey;
      const varName = goalsStep.customisationConfig?.variables?.[0]?.variableName;
      const liveVals = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
        : null;
      const rawGoals = Array.isArray(liveVals)
        ? liveVals.filter(Boolean)
        : (goalsStep.populatedData?.variables?.[0]?.allValues || []).filter(Boolean);
      const count = Math.max(rawGoals.length, 1);
      const actualHalfW = goalsNodeHalfW(count);
      const standardHalfW = CARD_W / 2;
      const extraShift = Math.max(0, actualHalfW - standardHalfW);

      if (extraShift > 0) {
        // Keep the goals node visually anchored on its left side as it grows,
        // then push everything downstream far enough right to preserve the exit gap.
        map[goalsStep.stepId] = (map[goalsStep.stepId] ?? 0) + extraShift;

        const seen = new Set();
        const queue = [...(goalsStep.connectsTo || [])];
        while (queue.length) {
          const sid = queue.shift();
          if (!sid || seen.has(sid)) continue;
          seen.add(sid);
          const s = stepMap[sid];
          if (!s) continue;
          map[sid] = (map[sid] ?? (s.layout ? nx(s) : 0)) + extraShift * 2;
          (s.connectsTo || []).forEach(nextId => {
            if (!seen.has(nextId)) queue.push(nextId);
          });
        }
      }
    });

    // Compact the Progress Goals chain so the info button, add button and goals
    // node sit much closer together. This prevents the goals node from being
    // pushed too far right while preserving the downstream ordering.
    visibleFlow.forEach(infoStep => {
      if (stepKind(infoStep) !== 'infoButton') return;

      const addStep = (infoStep.connectsTo || [])
        .map(id => stepMap[id])
        .find(step => step && stepKind(step) === 'addButton');
      if (!addStep) return;

      const goalsStep = (addStep.connectsTo || [])
        .map(id => stepMap[id])
        .find(step => step && stepKind(step) === 'goalsNode');
      if (!goalsStep) return;

      const tabKey  = goalsStep.customisationConfig?.tabKey;
      const varKey  = goalsStep.customisationConfig?.varKey;
      const varName = goalsStep.customisationConfig?.variables?.[0]?.variableName;
      const liveVals = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
        : null;
      const rawGoals = Array.isArray(liveVals)
        ? liveVals.filter(Boolean)
        : (goalsStep.populatedData?.variables?.[0]?.allValues || []).filter(Boolean);
      const goalCount = Math.max(rawGoals.length, 1);
      const actualHalfW = goalsNodeHalfW(goalCount);

      const parentStep = parentMap[infoStep.stepId];
      const parentX = parentStep ? (map[parentStep.stepId] ?? nx(parentStep)) : null;
      const COMPACT_INFO_GAP = 54;
      const parentRadius = parentStep ? halfW(parentStep) : CIRCLE_R;
      const infoX = parentX != null
        ? parentX + parentRadius + COMPACT_INFO_GAP + PLUS_R
        : (map[infoStep.stepId] ?? nx(infoStep));
      const oldGoalsX = map[goalsStep.stepId] ?? nx(goalsStep);

      const COMPACT_BUTTON_GAP = 62;
      const COMPACT_GOALS_GAP = 22;

      const compactAddX = infoX + COMPACT_BUTTON_GAP;
      const compactGoalsX = compactAddX + PLUS_R + COMPACT_GOALS_GAP + actualHalfW;

      const descendantShift = compactGoalsX - oldGoalsX;

      map[infoStep.stepId] = infoX;
      map[addStep.stepId] = compactAddX;
      map[goalsStep.stepId] = compactGoalsX;

      if (descendantShift !== 0) {
        const seen = new Set();
        const queue = [...(goalsStep.connectsTo || [])];
        while (queue.length) {
          const sid = queue.shift();
          if (!sid || seen.has(sid)) continue;
          seen.add(sid);
          const s = stepMap[sid];
          if (!s) continue;
          map[sid] = (map[sid] ?? (s.layout ? nx(s) : 0)) + descendantShift;
          (s.connectsTo || []).forEach(nextId => {
            if (!seen.has(nextId)) queue.push(nextId);
          });
        }
      }
    });

    // PresetTextNode is 480px wide (halfW=240) vs CARD_W=200 (halfW=100). With
    // ROW_STEP=320 that only leaves 22px clearance to the next node. Shift the
    // preset node right by the excess halfW so its left edge stays anchored, then
    // push all descendants by 2× the excess to preserve the standard exit gap.
    visibleFlow.forEach(presetStep => {
      if (stepKind(presetStep) !== 'presetTextNode') return;
      const extraShift = PRESET_TEXT_W / 2 - CARD_W / 2;
      if (extraShift <= 0) return;
      map[presetStep.stepId] = (map[presetStep.stepId] ?? (presetStep.layout ? nx(presetStep) : 0)) + extraShift;
      const seen = new Set();
      const queue = [...(presetStep.connectsTo || [])];
      while (queue.length) {
        const sid = queue.shift();
        if (!sid || seen.has(sid)) continue;
        seen.add(sid);
        const s = stepMap[sid];
        if (!s) continue;
        map[sid] = (map[sid] ?? (s.layout ? nx(s) : 0)) + extraShift * 2;
        (s.connectsTo || []).forEach(nextId => {
          if (!seen.has(nextId)) queue.push(nextId);
        });
      }
    });

    // Center the Goal Reached Trigger on the GoalsNode so it sits neatly below it.
    {
      const goalsStepX = visibleFlow.find(s => s.stepId === 'goals_node' || stepKind(s) === 'goalsNode');
      const goalReachedStepX = visibleFlow.find(s => s.stepId === 'goal_reached_disruptor');
      if (goalsStepX && goalReachedStepX) {
        map[goalReachedStepX.stepId] = map[goalsStepX.stepId] ?? nx(goalsStepX);
      }
    }

    // Sub Bomb should use its declared row/column grid directly.
    // Avoid mixing BFS-based X placement with bespoke per-node overrides.
    {
      const isSubBombFlow = visibleFlow.some(step => String(step?.stepId || '').startsWith('sb_'));
      if (isSubBombFlow) {
        visibleFlow.forEach(step => {
          if (!String(step?.stepId || '').startsWith('sb_')) return;
          if (!step.layout) return;
          map[step.stepId] = nx(step);
        });
      }
    }

    return map;
  }, [bfsRows, visibleFlow, stepMap, liveCustomisationData]);

  // Render-time heartbeat debug: this runs on every FlowCanvas render so
  // `globalThis.__FLOW_AUDIO_DEBUG` is always available, even if an effect log
  // is filtered, short-circuited, or inspected from a different console context.
  {
    const audioSteps = visibleFlow.filter(step => stepKind(step) === 'audioStack');
    const renderPayload = {
      phase: 'render-heartbeat',
      visibleStepCount: visibleFlow.length,
      audioStackCount: audioSteps.length,
      audioStepIds: audioSteps.map(step => step.stepId),
      href: typeof window !== 'undefined' ? window.location?.href : null,
      timestamp: Date.now(),
    };

    if (typeof globalThis !== 'undefined') {
      globalThis.__FLOW_AUDIO_DEBUG = renderPayload;
      globalThis.__FLOW_DUMP_AUDIO_DEBUG = () => {
        const detailed = {
          ...renderPayload,
          phase: 'render-heartbeat-manual-dump',
          rows: audioSteps.map(step => ({
            stepId: step.stepId,
            row: step.layout?.row ?? 0,
            column: step.layout?.column ?? 0,
            depth: bfsRows[step.stepId] ?? 0,
            rawRowX: Math.round(nx(step)),
            resolvedX: Math.round(xOverrides[step.stepId] ?? nx(step)),
            resolvedY: Math.round(yOverrides[step.stepId] ?? 0),
            renderedHalfH: renderedHalfH(step),
            audioStackHalfW: audioStackHalfWByStepId[step.stepId],
          })),
          spineHalfInGroup2: centreY,
          yOverrides: yOverrides,
        };
        globalThis.__FLOW_AUDIO_DEBUG = detailed;
        console.error('[FlowCanvas][RenderHeartbeat] manual dump', detailed);
        return detailed;
      };
    }
  }

  const imageStackMergeAnchorByChildId = (() => {
    const map = {};

    // Helper: walk parentMap upward from `stepId`, return ordered array of ancestor stepIds.
    const getAncestorIds = (stepId) => {
      const ids = [];
      let cur = parentMap[stepId];
      while (cur) { ids.push(cur.stepId); cur = parentMap[cur.stepId]; }
      return ids;
    };

    Object.entries(incomingParentsMap).forEach(([childId, parents]) => {
      const imageParents = parents.filter(parent => isBranchStackKind(parent));
      if (imageParents.length < 2) return;

      const sorted = [...imageParents].sort((a, b) => (yOverrides[a.stepId] ?? centreY) - (yOverrides[b.stepId] ?? centreY));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];

      // anchorY: Y of the lowest common ancestor of all imageStack branches.
      // For image overlay this is start_audio_node (centreY), keeping End Audio Trigger
      // horizontally inline with Audio (start audio). Falls back to stack midpoint.
      const firstAncestors = getAncestorIds(first.stepId);
      const otherAncestorSets = sorted.slice(1).map(p => new Set(getAncestorIds(p.stepId)));
      const lcaStep = firstAncestors
        .map(id => stepMap[id])
        .find(s => s && otherAncestorSets.every(set => set.has(s.stepId)));
      const firstCenterY = yOverrides[first.stepId] ?? centreY;
      const lastCenterY  = yOverrides[last.stepId] ?? centreY;
      const anchorY = lcaStep
        ? (yOverrides[lcaStep.stepId] ?? ny(lcaStep, centreY))
        : (firstCenterY + lastCenterY) / 2;

      const sharedParent = parentMap[first.stepId] ?? null;
      const stepX = xOverrides[first.stepId] ?? nx(first);
      const cardLeftX = stepX - CARD_W / 2;
      const parentX = sharedParent ? (xOverrides[sharedParent.stepId] ?? nx(sharedParent)) : null;
      const parentRadius = (sharedParent?.nodeConfig?.nodeStyle === 'infoButton' || sharedParent?.nodeConfig?.nodeStyle === 'addButton') ? PLUS_R : CIRCLE_R;
      const sourceX = parentX != null ? parentX + parentRadius : cardLeftX - BRANCH_STEM;
      const anchorX = Math.max(sourceX + BRANCH_STEM, (sourceX + cardLeftX) / 2);

      const queue = [childId];
      const seen = new Set();
      while (queue.length) {
        const id = queue.shift();
        if (!id || seen.has(id)) continue;
        seen.add(id);

        const step = stepMap[id];
        if (!step || isBranchStackKind(step)) continue;
        map[id] = { x: anchorX, y: anchorY };
        // Stop propagating at circle/outcome spine nodes: their children have
        // independent layout (e.g. end_audio + end_disruptor under confetti_active_node)
        // and must not be forced to the merge anchor Y.
        const k = stepKind(step);
        if (k === 'circle' || k === 'outcome') continue;
        (step.connectsTo || []).forEach(nextId => {
          if (!seen.has(nextId)) queue.push(nextId);
        });
      }
    });

    return map;
  })();

  const connectorYOverrides = useMemo(() => {
    const map = { ...yOverrides };
    Object.entries(imageStackMergeAnchorByChildId).forEach(([stepId, anchor]) => {
      map[stepId] = anchor.y;
    });

    visibleFlow.forEach(step => {
      const kind = stepKind(step);
      const isMainSpineStep = (step.layout?.column ?? CENTER_COL) === CENTER_COL
        && (kind === 'circle' || kind === 'card' || kind === 'particleSettings' || kind === 'particleTimer' || kind === 'outcome' || kind === 'countdownSettings' || kind === 'countdownTimer' || kind === 'videoSettings' || kind === 'subBombDisplaySettings' || kind === 'subBombSpeedControl');
      if (!isMainSpineStep) return;
      // Do not reset circles that connect (directly or via one relay) to a pollCards
      // node — those were intentionally repositioned by the BELOW_AUDIO_GAP pass
      // and resetting them here would disconnect them from the shifted polls stack.
      const connectsToPollCards = (step.connectsTo || []).some(cid => {
        const child = stepMap[cid];
        return child && stepKind(child) === 'pollCards';
      });
      if (connectsToPollCards) return;
      map[step.stepId] = ny(step, centreY);  // pin to centreY — layout passes must not move spine nodes
    });

    return map;
  }, [yOverrides, imageStackMergeAnchorByChildId, visibleFlow, centreY]);

  useEffect(() => {
    if (!LOG_LAYOUT_DEBUG) return;
    if (!visibleFlow.length) return;

    const nodesByDepth = {};
    Object.entries(bfsRows).forEach(([stepId, depth]) => {
      const step = stepMap[stepId];
      if (!step) return;
      if (!nodesByDepth[depth]) nodesByDepth[depth] = [];
      nodesByDepth[depth].push(step);
    });

    const renderedHalfWForStep = (step) => {
      if (!step) return CARD_W / 2;
      if (stepKind(step) === 'audioStack' && audioStackHalfWByStepId[step.stepId] !== undefined) return audioStackHalfWByStepId[step.stepId];
      if (stepKind(step) === 'goalsNode' && goalsHalfWByStepId[step.stepId] !== undefined) return goalsHalfWByStepId[step.stepId];
      if (stepKind(step) === 'slotReels' && slotReelsHalfWByStepId[step.stepId] !== undefined) return slotReelsHalfWByStepId[step.stepId];
      return halfW(step);
    };

    const depthEntries = Object.keys(nodesByDepth)
      .map(Number)
      .sort((a, b) => a - b)
      .map(depth => {
        const steps = nodesByDepth[depth] || [];
        const centerX = steps.length ? (xOverrides[steps[0].stepId] ?? nx(steps[0])) : nx({ layout: { row: depth } });
        const maxHalfW = steps.length ? Math.max(...steps.map(renderedHalfWForStep)) : CARD_W / 2;
        return {
          depth,
          centerX,
          maxHalfW,
          stepIds: steps.map(step => step.stepId).join(', '),
        };
      });

    const depthBands = depthEntries.map((entry, index) => {
      const prev = depthEntries[index - 1] ?? null;
      const next = depthEntries[index + 1] ?? null;
      const left = prev ? (prev.centerX + entry.centerX) / 2 : Math.max(0, entry.centerX - ROW_STEP / 2);
      const right = next ? (entry.centerX + next.centerX) / 2 : entry.centerX + ROW_STEP / 2;
      return {
        depth: entry.depth,
        left: Math.round(left),
        centerX: Math.round(entry.centerX),
        right: Math.round(right),
        laneWidth: Math.round(right - left),
        maxNodeWidth: Math.round(entry.maxHalfW * 2),
        stepIds: entry.stepIds,
      };
    });

    const rowGuides = Array.from({ length: maxRow + 1 }, (_, row) => ({
      row,
      rowX: Math.round(nx({ layout: { row } })),
    }));

    const nodePositions = visibleFlow.map(step => {
      const resolvedX = xOverrides[step.stepId] ?? nx(step);
      const rawRowX = nx(step);
      const resolvedY = connectorYOverrides[step.stepId] ?? yOverrides[step.stepId] ?? ny(step, centreY);
      return {
        stepId: step.stepId,
        kind: stepKind(step),
        layoutRow: step.layout?.row ?? 0,
        layoutColumn: step.layout?.column ?? CENTER_COL,
        bfsDepth: bfsRows[step.stepId] ?? 0,
        rawRowX: Math.round(rawRowX),
        resolvedX: Math.round(resolvedX),
        deltaFromRowX: Math.round(resolvedX - rawRowX),
        renderedWidth: Math.round(renderedHalfWForStep(step) * 2),
        resolvedY: Math.round(resolvedY),
      };
    });

    const audioStacks = visibleFlow
      .filter(step => stepKind(step) === 'audioStack')
      .map(step => {
        const variableName = step.customisationConfig?.variables?.[0]?.variableName;
        const tabKey = step.customisationConfig?.tabKey;
        const varKey = step.customisationConfig?.varKey;
        const liveVals = liveCustomisationData && tabKey && varKey && variableName
          ? liveCustomisationData?.[tabKey]?.[varKey]?.[variableName]
          : null;
        const allVals = Array.isArray(liveVals)
          ? liveVals.filter(item => item != null)
          : (step.populatedData?.variables?.[0]?.allValues || []).filter(item => item != null);
        const count = Math.max(allVals.length, 1);
        const isGridMode = count > 1;
        const gridCols = count > 1 ? 3 : 1;
        const gridRows = Math.max(isGridMode ? Math.ceil(count / gridCols) : count, 1);
        const gridTotalW = isGridMode ? gridCols * AUDIO_CARD_W + (gridCols - 1) * AUDIO_CARD_GAP : AUDIO_CARD_W;
        const resolvedX = xOverrides[step.stepId] ?? nx(step);
        const rawRowX = nx(step);
        const parent = parentMap[step.stepId] ?? null;
        const next = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
        const parentX = parent ? (xOverrides[parent.stepId] ?? nx(parent)) : null;
        const nextX = next ? (xOverrides[next.stepId] ?? nx(next)) : null;
        const halfWRendered = audioStackHalfWByStepId[step.stepId] ?? halfW(step);
        const cardLeftX = resolvedX - gridTotalW / 2;
        const wrapperLeftX = cardLeftX - 14;
        const wrapperRightX = wrapperLeftX + gridTotalW + 28;
        const band = depthBands.find(item => item.depth === (bfsRows[step.stepId] ?? 0));
        return {
          stepId: step.stepId,
          layoutRow: step.layout?.row ?? 0,
          bfsDepth: bfsRows[step.stepId] ?? 0,
          count,
          gridCols,
          gridRows,
          rawRowX: Math.round(rawRowX),
          resolvedX: Math.round(resolvedX),
          deltaFromRowX: Math.round(resolvedX - rawRowX),
          renderedWidth: Math.round(halfWRendered * 2),
          gridWidth: Math.round(gridTotalW),
          cardLeftX: Math.round(cardLeftX),
          wrapperLeftX: Math.round(wrapperLeftX),
          wrapperRightX: Math.round(wrapperRightX),
          parentStepId: parent?.stepId ?? null,
          parentX: parentX != null ? Math.round(parentX) : null,
          nextStepId: next?.stepId ?? null,
          nextX: nextX != null ? Math.round(nextX) : null,
          bandCenterX: band?.centerX ?? null,
          bandWidth: band?.laneWidth ?? null,
        };
      });

    const audioRelations = audioStacks.map(item => {
      const parentNode = nodePositions.find(node => node.stepId === item.parentStepId);
      const nextNode = nodePositions.find(node => node.stepId === item.nextStepId);
      return {
        stepId: item.stepId,
        parentStepId: item.parentStepId,
        parentResolvedX: parentNode?.resolvedX ?? null,
        audioResolvedX: item.resolvedX,
        nextStepId: item.nextStepId,
        nextResolvedX: nextNode?.resolvedX ?? null,
        parentToAudioDx: parentNode ? item.resolvedX - parentNode.resolvedX : null,
        audioToNextDx: nextNode ? nextNode.resolvedX - item.resolvedX : null,
      };
    });

    const debugPayload = {
      centreY: Math.round(centreY),
      visibleNodeCount: visibleFlow.length,
      audioStackCount: audioStacks.length,
      depthBands,
      rowGuides,
      nodePositions,
      audioStacks,
      audioRelations,
    };

    if (typeof window !== 'undefined') {
      window.__FLOW_LAYOUT_DEBUG = debugPayload;
      window.__FLOW_AUDIO_DEBUG = { audioStacks, audioRelations };
    }

    console.error('[FlowCanvas][LayoutDebug] snapshot ready', {
      centreY: debugPayload.centreY,
      visibleNodeCount: debugPayload.visibleNodeCount,
      audioStackCount: debugPayload.audioStackCount,
    });
    console.error('[FlowCanvas][LayoutDebug] run window.__FLOW_AUDIO_DEBUG or window.__FLOW_LAYOUT_DEBUG in console');
    console.table(audioStacks);
    console.table(audioRelations);
    console.table(depthBands);
  }, [LOG_LAYOUT_DEBUG, visibleFlow, bfsRows, stepMap, xOverrides, yOverrides, connectorYOverrides, audioStackHalfWByStepId, goalsHalfWByStepId, slotReelsHalfWByStepId, parentMap, liveCustomisationData, centreY, maxRow]);

  // ─── Sub Bomb layout diagnostics ─────────────────────────────────────────────
  // Runs at component top-level after all layout values are computed.
  useEffect(() => {
    const ENABLE_SUBBOMB_DEBUG = false;
    if (!ENABLE_SUBBOMB_DEBUG) return;

    const isSubBombFlow = visibleFlow.some(step => String(step?.stepId || '').startsWith('sb_'));
    console.log('[SubBomb Debug] effect fired, isSubBombFlow:', isSubBombFlow, 'steps:', visibleFlow.length);

    const publishAudioDebug = (payload) => {
      if (typeof globalThis !== 'undefined') globalThis.__FLOW_AUDIO_DEBUG = payload;
      if (typeof window !== 'undefined') {
        window.__FLOW_AUDIO_DEBUG = payload;
        try {
          if (window.self && window.self !== window) window.self.__FLOW_AUDIO_DEBUG = payload;
          if (window.top && window.top !== window) window.top.__FLOW_AUDIO_DEBUG = payload;
          if (window.parent && window.parent !== window) window.parent.__FLOW_AUDIO_DEBUG = payload;
        } catch (_) {
          // Cross-origin frame access can throw; base window/globalThis assignment above still works.
        }
      }
    };

    const publishAudioDebugError = (payload) => {
      if (typeof globalThis !== 'undefined') globalThis.__FLOW_AUDIO_DEBUG_ERROR = payload;
      if (typeof window !== 'undefined') {
        window.__FLOW_AUDIO_DEBUG_ERROR = payload;
        try {
          if (window.self && window.self !== window) window.self.__FLOW_AUDIO_DEBUG_ERROR = payload;
          if (window.top && window.top !== window) window.top.__FLOW_AUDIO_DEBUG_ERROR = payload;
          if (window.parent && window.parent !== window) window.parent.__FLOW_AUDIO_DEBUG_ERROR = payload;
        } catch (_) {
          // Cross-origin frame access can throw; base window/globalThis assignment above still works.
        }
      }
    };

    publishAudioDebug({
      phase: 'effect-entered',
      isSubBombFlow,
      visibleStepCount: visibleFlow.length,
      timestamp: Date.now(),
      href: typeof window !== 'undefined' ? window.location?.href : null,
    });
    publishAudioDebugError(null);

    try {
      const audioSteps = visibleFlow.filter(step => stepKind(step) === 'audioStack');
      const kindCounts = visibleFlow.reduce((acc, step) => {
        const kind = stepKind(step);
        acc[kind] = (acc[kind] || 0) + 1;
        return acc;
      }, {});

      console.error('[SubBomb Debug] audio preflight', {
        audioStackCount: audioSteps.length,
        audioStepIds: audioSteps.map(step => step.stepId),
        kindCounts,
      });

      const audioDebugRows = audioSteps.map(step => {
        const variableName = step.customisationConfig?.variables?.[0]?.variableName;
        const tabKey = step.customisationConfig?.tabKey;
        const varKey = step.customisationConfig?.varKey;
        const liveVals = liveCustomisationData && tabKey && varKey && variableName
          ? liveCustomisationData?.[tabKey]?.[varKey]?.[variableName]
          : null;
        const allVals = Array.isArray(liveVals)
          ? liveVals.filter(item => item != null)
          : (step.populatedData?.variables?.[0]?.allValues || []).filter(item => item != null);
        const count = Math.max(allVals.length, 1);
        const isGridMode = count > 1;
        const gridCols = count > 1 ? 3 : 1;
        const gridRows = Math.max(isGridMode ? Math.ceil(count / gridCols) : count, 1);
        const gridTotalW = isGridMode ? gridCols * AUDIO_CARD_W + (gridCols - 1) * AUDIO_CARD_GAP : AUDIO_CARD_W;
        const resolvedX = xOverrides[step.stepId] ?? nx(step);
        const rawRowX = nx(step);
        const parent = parentMap[step.stepId] ?? null;
        const next = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
        const parentX = parent ? (xOverrides[parent.stepId] ?? nx(parent)) : null;
        const nextX = next ? (xOverrides[next.stepId] ?? nx(next)) : null;
        return {
          stepId: step.stepId,
          row: step.layout?.row ?? 0,
          depth: bfsRows[step.stepId] ?? 0,
          count,
          gridCols,
          gridRows,
          rawRowX: Math.round(rawRowX),
          resolvedX: Math.round(resolvedX),
          deltaFromRowX: Math.round(resolvedX - rawRowX),
          gridWidth: Math.round(gridTotalW),
          renderedWidth: Math.round((audioStackHalfWByStepId[step.stepId] ?? halfW(step)) * 2),
          parentStepId: parent?.stepId ?? null,
          parentX: parentX != null ? Math.round(parentX) : null,
          nextStepId: next?.stepId ?? null,
          nextX: nextX != null ? Math.round(nextX) : null,
        };
      });

      const audioRelationRows = audioDebugRows.map(row => ({
        stepId: row.stepId,
        parentStepId: row.parentStepId,
        parentToAudioDx: row.parentX != null ? row.resolvedX - row.parentX : null,
        nextStepId: row.nextStepId,
        audioToNextDx: row.nextX != null ? row.nextX - row.resolvedX : null,
        deltaFromRowX: row.deltaFromRowX,
      }));

      publishAudioDebug({
        phase: 'snapshot-ready',
        isSubBombFlow,
        visibleStepCount: visibleFlow.length,
        audioStackCount: audioDebugRows.length,
        audioStacks: audioDebugRows,
        audioRelations: audioRelationRows,
        kindCounts,
        timestamp: Date.now(),
        href: typeof window !== 'undefined' ? window.location?.href : null,
      });

      if (typeof globalThis !== 'undefined') {
        globalThis.__FLOW_DUMP_AUDIO_DEBUG = () => {
          const payload = {
            phase: 'manual-dump',
            isSubBombFlow,
            visibleStepCount: visibleFlow.length,
            audioStackCount: audioDebugRows.length,
            audioStacks: audioDebugRows,
            audioRelations: audioRelationRows,
            kindCounts,
            timestamp: Date.now(),
            href: typeof window !== 'undefined' ? window.location?.href : null,
          };
          publishAudioDebug(payload);
          console.error('[SubBomb Debug] manual audio dump', payload);
          console.table(audioDebugRows);
          console.table(audioRelationRows);
          return payload;
        };
      }

      console.error('[SubBomb Debug] audio layout snapshot', {
        audioStackCount: audioDebugRows.length,
        audioStepIds: audioDebugRows.map(row => row.stepId),
      });
      console.table(audioDebugRows);
      console.table(audioRelationRows);
    } catch (error) {
      publishAudioDebugError({
        message: error?.message || String(error),
        stack: error?.stack || null,
        href: typeof window !== 'undefined' ? window.location?.href : null,
        timestamp: Date.now(),
      });
      console.error('[SubBomb Debug] audio layout debug failed', error);
    }

    if (!isSubBombFlow) return;

    const resolveDebugX = (step) => {
      const kind = stepKind(step);
      let x = xOverrides[step.stepId] ?? nx(step);
      if (kind === 'videoStack') x += getStackButtonExtraX(step);
      if (kind === 'circle') {
        const par = parentMap[step.stepId] ?? null;
        const parKind = par ? stepKind(par) : null;
        if (parKind === 'videoStack') x += getStackButtonExtraX(par);
      }
      return x;
    };

    const entries = visibleFlow.map(step => {
      const kind = stepKind(step);
      const mergedAnchorY = imageStackMergeAnchorByChildId[step.stepId]?.y;
      const isMainSpineStep = (step.layout?.column ?? CENTER_COL) === CENTER_COL
        && (kind === 'circle' || kind === 'card' || kind === 'particleSettings' || kind === 'particleTimer'
          || kind === 'outcome' || kind === 'countdownSettings' || kind === 'countdownTimer' || kind === 'videoSettings' || kind === 'subBombDisplaySettings' || kind === 'subBombSpeedControl');
      const y = isMainSpineStep
        ? Math.max(mergedAnchorY ?? yOverrides[step.stepId] ?? ny(step, centreY), ny(step, centreY))
        : (mergedAnchorY ?? yOverrides[step.stepId] ?? ny(step, centreY));
      const x = resolveDebugX(step);
      const halfWidth = halfW(step);
      const halfHeight = renderedHalfH(step);
      return {
        stepId: step.stepId,
        title: step.stepTitle,
        kind,
        row: step.layout?.row ?? null,
        col: step.layout?.column ?? null,
        x: Math.round(x),
        y: Math.round(y),
        left: Math.round(x - halfWidth),
        right: Math.round(x + halfWidth),
        top: Math.round(y - halfHeight),
        bottom: Math.round(y + halfHeight),
        w: Math.round(halfWidth * 2),
        h: Math.round(halfHeight * 2),
      };
    });

    const overlaps = [];
    for (let i = 0; i < entries.length; i += 1) {
      for (let j = i + 1; j < entries.length; j += 1) {
        const a = entries[i];
        const b = entries[j];
        const ox = Math.min(a.right, b.right) - Math.max(a.left, b.left);
        const oy = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
        if (ox > 0 && oy > 0) overlaps.push({ a: a.stepId, b: b.stepId, overlapX: Math.round(ox), overlapY: Math.round(oy) });
      }
    }

    console.groupCollapsed('[SubBomb Debug] layout snapshot — ' + entries.length + ' nodes');
    console.table(entries);
    if (overlaps.length) {
      console.warn('[SubBomb Debug] OVERLAPS (' + overlaps.length + ')');
      console.table(overlaps);
    } else {
      console.info('[SubBomb Debug] no overlaps');
    }
    console.groupEnd();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFlow, xOverrides, yOverrides, imageStackMergeAnchorByChildId, centreY]);
  // ─────────────────────────────────────────────────────────────────────────────

  const hideSubBombExplosionBypassConnector =
    visibleStepIds.has('sb_explosion_items_node') ||
    visibleStepIds.has('sb_explosion_audio_grid_node');

  const connections = useMemo(() => {
    const list = [];
    visibleFlow.forEach(from => {
      (from.connectsTo || []).forEach(toId => {
        const toStep = stepMap[toId];
        if (!toStep) return;
        // Skip connections involving waveGraphics or zapperObjects steps —
        // custom connectors with branch labels and + circles are drawn in renderNode
        if (stepKind(toStep) === 'waveGraphics') return;
        if (stepKind(from)   === 'waveGraphics') return;
        if (stepKind(toStep) === 'zapperObjects') return;
        if (stepKind(from)   === 'zapperObjects') return;
        if (stepKind(toStep) === 'imageStack' || stepKind(toStep) === 'particleImageStack' || stepKind(toStep) === 'particleColourStack') return;
        if (stepKind(from) === 'imageStack' && multiImageStackTargetIds.has(toId)) return;
        // puzzleStack draws its own entry connector; skip generic entry only.
        // Exit connector (puzzleStack → next) uses generic logic so cards connect to vote_end_node.
        if (stepKind(toStep) === 'puzzleStack') return;
        // pollCards draws its own branching connector both ways; skip generic for both ends.
        if (stepKind(toStep) === 'pollCards' || stepKind(from) === 'pollCards') return;
        // pollAudio: suppress the exit (from) when this pollAudio is a sibling of pollCards
        // under the same parent — pollCards draws the merged exit stem.
        // The entry connector (to pollAudio) is kept so the line from Poll Data → audio shows.
        // start_audio → vote_start_node is NOT a pollCards sibling, so it is also kept.
        const hasPollCardsSibling = (step) => {
          const p = parentMap[step.stepId];
          if (!p) return false;
          // Direct sibling is pollCards
          const directMatch = visibleFlow.some(
            s => s.stepId !== step.stepId
              && parentMap[s.stepId]?.stepId === p.stepId
              && stepKind(s) === 'pollCards'
          );
          if (directMatch) return true;
          // Sibling's child is pollCards (poll_data_node → polls_node pattern)
          const siblings = visibleFlow.filter(
            s => s.stepId !== step.stepId && parentMap[s.stepId]?.stepId === p.stepId
          );
          return siblings.some(sib =>
            (sib.connectsTo || []).some(cid => {
              const child = stepMap[cid];
              return child && stepKind(child) === 'pollCards';
            })
          );
        };
        if (stepKind(from) === 'pollAudio' && hasPollCardsSibling(from)) return;
        // Sub-bomb: only hide the direct decision bypass into sb_active_node when
        // the explosion branch is actually visible. If explosions are disabled,
        // this direct connector is the only valid path and must remain visible.
        if (hideSubBombExplosionBypassConnector && from.stepId === 'sb_allow_explosions_decision' && toId === 'sb_active_node') return;
        list.push({ from, to: toStep });
      });
    });
    return list;
  }, [visibleFlow, stepMap, parentMap, multiImageStackTargetIds, multiImageStackParentIds, hideSubBombExplosionBypassConnector]);

  const hideLiveTextResponsesBypassConnector = (() => {
    const decisionStep = stepMap['lt_responses_decision'];
    const dataPath = decisionStep?.nodeConfig?.decisionDataPath;
    if (!dataPath) return false;

    // Match DiamondNode exactly so the badge state and connector state stay in sync.
    const keys = dataPath.split('.');
    let target = liveCustomisationData;
    for (let i = 0; i < keys.length - 1; i += 1) {
      if (target == null) {
        target = null;
        break;
      }
      const key = keys[i];
      target = Number.isNaN(Number(key)) ? target[key] : target[parseInt(key, 10)];
    }
    const leaf = keys[keys.length - 1];
    const valObj = Array.isArray(target) ? target[0] : target;
    const val = valObj?.[leaf];

    // DiamondNode defaults to enabled when data hasn't loaded yet.
    const isEnabled = (val !== undefined && val !== null) ? !!val : true;
    return isEnabled === true;
  })();

  // Merge connector groups: when 2+ sources at the same layout.row all connect to
  // the same target, draw a shared vertical spine + individual horizontal branches
  // instead of crossing Z-shaped connectors (which look like zig-zags).
  const mergeConnectorGroups = useMemo(() => {
    const byTarget = {};
    connections.forEach(c => {
      if (c.from.stepId === 'lt_responses_decision' && c.to.stepId === 'lt_complete_node') return;
      if (hideSubBombExplosionBypassConnector && c.from.stepId === 'sb_allow_explosions_decision' && c.to.stepId === 'sb_active_node') return;
      if (stepKind(c.from) === 'presetTextNode') return;
      if (c.to.stepId === 'lt_complete_node') return;
      // videoStack normally draws its own exit connectors, so exclude it from shared
      // merge groups. audioStack participates so neighbouring branches can route
      // around the rendered audio block instead of cutting through it.
      if (stepKind(c.from) === 'videoStack' && c.to.stepId !== 'sb_active_node') return;
      // imageStack nodes normally branch off the spine — exclude them so their side-branch Y
      // doesn't create a long crossing horizontal in the merge connector geometry.
      // Sub Bomb is the exception: Explosion Images should also merge into sb_active_node.
      if ((stepKind(c.from) === 'imageStack' || stepKind(c.from) === 'particleImageStack' || stepKind(c.from) === 'particleColourStack') && c.to.stepId !== 'sb_active_node') return;
      const key = c.to.stepId;
      if (!byTarget[key]) byTarget[key] = [];
      byTarget[key].push(c);
    });
    return Object.values(byTarget)
      .filter(group => group.length >= 2)
      .map(group => {
        const to = group[0].to;
        const targetY   = connectorYOverrides[to.stepId] ?? ny(to, centreY);
        const targetX   = xOverrides[to.stepId] ?? nx(to);
        const toBaseX   = stepKind(to) === 'audioStack'
          ? targetX - AUDIO_CARD_W / 2 - 14
          : targetX - halfW(to);
        const mergeX    = toBaseX - 42;
        const sources   = group.map(c => {
          const from = c.from;
          const fromKind   = stepKind(from);
          const fromYBase  = connectorYOverrides[from.stepId] ?? ny(from, centreY);
          const fromX      = xOverrides[from.stepId] ?? nx(from);
          // videoStack is rendered with an extra X shift for its button cluster.
          const stackExtraX = fromKind === 'videoStack' ? getStackButtonExtraX(from) : 0;
          const fromHalfW = (fromKind === 'audioStack' && audioStackHalfWByStepId[from.stepId] !== undefined)
            ? audioStackHalfWByStepId[from.stepId]
            : ((fromKind === 'slotReels' && slotReelsHalfWByStepId[from.stepId] !== undefined)
              ? slotReelsHalfWByStepId[from.stepId]
              : halfW(from));
          const fromY = fromKind === 'audioStack'
            ? fromYBase + renderedHalfH(from) - AUDIO_CARD_H / 2
            : fromYBase;
          const fromRightX = fromKind === 'audioStack'
            ? fromX + fromHalfW + 14
            : fromX + stackExtraX + fromHalfW;
          return { fromY, fromRightX };
        });
        const rightMostSourceX = Math.max(...sources.map(s => s.fromRightX));
        const desiredMergeX = rightMostSourceX + 34;
        const clampedMergeX = Math.min(toBaseX - 12, Math.max(desiredMergeX, rightMostSourceX + 16));
        const spineTopY    = Math.min(...sources.map(s => s.fromY));
        const spineBottomY = Math.max(...sources.map(s => s.fromY));
        return { group, sources, to, targetY, toBaseX, mergeX: clampedMergeX, spineTopY, spineBottomY };
      });
  }, [connections, connectorYOverrides, xOverrides, centreY, hideSubBombExplosionBypassConnector, getStackButtonExtraX, audioStackHalfWByStepId, slotReelsHalfWByStepId]);

  const mergeConnectionKeys = useMemo(() => {
    const set = new Set();
    mergeConnectorGroups.forEach(g =>
      g.group.forEach(c => set.add(`${c.from.stepId}--${c.to.stepId}`))
    );
    return set;
  }, [mergeConnectorGroups]);

  const outgoingBranchGroups = useMemo(() => {
    const bySource = {};
    connections.forEach(c => {
      if (hideLiveTextResponsesBypassConnector && c.from.stepId === 'lt_responses_decision' && c.to.stepId === 'lt_complete_node') return;
      if (hideSubBombExplosionBypassConnector && c.from.stepId === 'sb_allow_explosions_decision' && c.to.stepId === 'sb_active_node') return;
      const key = c.from.stepId;
      if (!bySource[key]) bySource[key] = [];
      bySource[key].push(c);
    });

    return Object.values(bySource)
      .filter(group => {
        if (group.length < 2) return false;
        const fromKind = stepKind(group[0].from);
        if (fromKind === 'imageStack' || fromKind === 'particleImageStack' || fromKind === 'particleColourStack' || fromKind === 'puzzleStack' || fromKind === 'pollCards') return false;
        // videoStack draws its own entry connector. audioStack still uses the shared
        // branch stem, but renders the final segment into its own control cluster.
        if (group.some(c => stepKind(c.to) === 'videoStack')) return false;
        return true;
      })
      .map(group => {
        const from = group[0].from;
        const fromY = connectorYOverrides[from.stepId] ?? ny(from, centreY);
        const fromX = xOverrides[from.stepId] ?? nx(from);
        const sourceRightX = stepKind(from) === 'audioStack'
          ? fromX + (audioStackHalfWByStepId[from.stepId] ?? halfW(from)) + 14
          : fromX + halfW(from);

        const children = group.map(c => {
          const to = c.to;
          const toY = connectorYOverrides[to.stepId] ?? ny(to, centreY);
          const toX = xOverrides[to.stepId] ?? nx(to);
          const childLeftX = stepKind(to) === 'audioStack'
            ? toX - AUDIO_CARD_W / 2 - 14
            : toX - halfW(to);
          return { c, toY, childLeftX };
        });

        const minChildLeftX = Math.min(...children.map(ch => ch.childLeftX));
        const desiredBranchX = sourceRightX + 44;
        const branchX = Math.min(minChildLeftX - 14, desiredBranchX);
        const spineTopY = Math.min(fromY, ...children.map(ch => ch.toY));
        const spineBottomY = Math.max(fromY, ...children.map(ch => ch.toY));

        return { from, fromY, sourceRightX, branchX, spineTopY, spineBottomY, children, group };
      });
  }, [connections, connectorYOverrides, xOverrides, centreY, hideLiveTextResponsesBypassConnector, hideSubBombExplosionBypassConnector, audioStackHalfWByStepId]);

  const audioSharedBranchXByStepId = useMemo(() => {
    const map = {};
    outgoingBranchGroups.forEach(group => {
      group.children.forEach(child => {
        if (stepKind(child.c.to) !== 'audioStack') return;
        map[child.c.to.stepId] = group.branchX;
      });
    });
    return map;
  }, [outgoingBranchGroups]);

  const outgoingBranchConnectionKeys = useMemo(() => {
    const set = new Set();
    outgoingBranchGroups.forEach(g =>
      g.group.forEach(c => set.add(`${c.from.stepId}--${c.to.stepId}`))
    );
    return set;
  }, [outgoingBranchGroups]);

  const renderedGenericConnectorCounts = useMemo(() => {
    const incoming = {};
    const outgoing = {};

    connections.forEach(c => {
      const key = `${c.from.stepId}--${c.to.stepId}`;
      if (mergeConnectionKeys.has(key)) return;
      if (outgoingBranchConnectionKeys.has(key)) return;
      if (stepKind(c.from) === 'presetTextNode' || stepKind(c.to) === 'presetTextNode') return;
      if (stepKind(c.from) === 'videoStack' || stepKind(c.to) === 'videoStack' || stepKind(c.from) === 'audioStack' || stepKind(c.to) === 'audioStack') return;
      if (hideLiveTextResponsesBypassConnector && c.from.stepId === 'lt_responses_decision' && c.to.stepId === 'lt_complete_node') return;
      if (hideSubBombExplosionBypassConnector && c.from.stepId === 'sb_allow_explosions_decision' && c.to.stepId === 'sb_active_node') return;

      incoming[c.to.stepId] = (incoming[c.to.stepId] || 0) + 1;
      outgoing[c.from.stepId] = (outgoing[c.from.stepId] || 0) + 1;
    });

    return { incoming, outgoing };
  }, [
    connections,
    mergeConnectionKeys,
    outgoingBranchConnectionKeys,
    hideLiveTextResponsesBypassConnector,
    hideSubBombExplosionBypassConnector,
  ]);

  function handleNodeClick(step) {
    // Only fire click if mouse didn't drag
    if (didDrag.current) {
      logClickDebug('handleNodeClick-blocked', { stepId: step?.stepId, kind: stepKind(step) });
      return;
    }
    logClickDebug('handleNodeClick-fire', { stepId: step?.stepId, kind: stepKind(step) });
    setActiveStepId(step.stepId);
    setActiveGraphicIndex(null);
    // opensFeatureSettings is set in the seeder (e.g. "Settings") for any circle node
    // that should open a specific settings panel on the right when clicked.
    // In practice we've also got legacy DB feature definitions that may be missing
    // opensFeatureSettings for the start node. Keep "WAVE FEATURE START" working
    // by treating start_node as implicitly opening Settings.
    const isTopRowStartNode = (step?.layout?.row ?? null) === 0;
    const isCustomisationCard = step?.stepType === 'customisation';
    const isDecisionNode = stepKind(step) === 'decision';
    if (onActivateNode && (isCustomisationCard || ((stepKind(step) === 'circle' || isDecisionNode) && (step.opensFeatureSettings || isTopRowStartNode)))) {
      onActivateNode(step, null);
    } else {
      // Card/other nodes don't open a right panel — clear any previously open one
      onClearSelection?.();
      onStepClick && onStepClick(step);
    }
  }

  function handleGraphicItemClick(step, graphicIndex) {
    // Open the existing Wave Graphics settings panel via NodeEditor's activateNode
    if (didDrag.current) return;
    setActiveStepId(step.stepId);
    setActiveGraphicIndex(graphicIndex);
    if (onActivateNode) {
      onActivateNode(step, graphicIndex);
    } else {
      onStepClick && onStepClick(step);
    }
  }

  function handleAddGraphicClick(step) {
    if (didDrag.current) return;
    if (onActivateNode) {
      // Pass null so handleFlowGraphicActivate opens the group node (which has the Add button),
      // rather than resolving to a specific existing item.
      onActivateNode(step, null);
    } else {
      onStepClick && onStepClick(step);
    }
  }

  function getPrimaryAudioSlotPayload(step, extra = {}) {
    const firstSlot = step?.customisationConfig?.variables?.[0] ?? null;
    const slotVarName = firstSlot?.variableName ?? null;
    const toggleVarName = firstSlot?.toggleVarName || slotVarName;
    if (!slotVarName) return null;
    return {
      slotVarName,
      fileIndex: null,
      toggleVarName,
      ...extra,
    };
  }

  function handleAudioGroupInfoClick(step) {
    if (didDrag.current) {
      logClickDebug('handleAudioGroupInfoClick-blocked', { stepId: step?.stepId });
      return;
    }
    logClickDebug('handleAudioGroupInfoClick-fire', { stepId: step?.stepId });
    setActiveStepId(step.stepId);
    setActiveGraphicIndex(null);
    if (!onActivateNode) return;

    if (step?.customisationConfig?.meta?.pollAudio || step?.customisationConfig?.meta?.audioStack) {
      const payload = getPrimaryAudioSlotPayload(step, { selectionMode: 'group' });
      onActivateNode(step, payload);
      return;
    }

    onActivateNode(step, null);
  }

  function handleAudioAddClick(step) {
    if (didDrag.current) {
      logClickDebug('handleAudioAddClick-blocked', { stepId: step?.stepId });
      return;
    }
    logClickDebug('handleAudioAddClick-fire', { stepId: step?.stepId });

    if (step?.customisationConfig?.meta?.pollAudio || step?.customisationConfig?.meta?.audioStack) {
      const payload = getPrimaryAudioSlotPayload(step);
      if (!payload) {
        logClickDebug('handleAudioAddClick-no-payload', { stepId: step?.stepId });
        return;
      }
      logClickDebug('handleAudioAddClick-payload-ready', {
        stepId: step?.stepId,
        payload,
        hasOnActivateNode: !!onActivateNode,
      });
      setActiveStepId(step.stepId);
      setActiveGraphicIndex({ slotVarName: payload.slotVarName, fileIndex: null });
      if (onActivateNode) {
        onActivateNode(step, payload);
      } else {
        logClickDebug('handleAudioAddClick-missing-onActivateNode', { stepId: step?.stepId });
      }
      return;
    }

    handleAddGraphicClick(step);
  }

  function handleRemovePuzzleSolutionImage(step, graphicIndex) {
    if (didDrag.current) return;
    const cfg = step?.customisationConfig;
    const tabKey = cfg?.tabKey;
    const varKey = cfg?.varKey;
    const varName = cfg?.variables?.[0]?.variableName;
    if (!tabKey || !varKey || !varName) return;

    const targetItem = liveCustomisationData?.[tabKey]?.[varKey]?.[varName]?.[graphicIndex];
    if (targetItem) {
      targetItem.solutionImageIsPuzzleImage = true;
      setDiamondToggleTick(t => t + 1);
    }

    onFlowBoolChange?.(tabKey, varKey, varName, graphicIndex, 'solutionImageIsPuzzleImage', true);
  }

  function handleActivatePuzzleSolutionImage(step, graphicIndex) {
    if (didDrag.current) return;
    const cfg = step?.customisationConfig;
    const tabKey = cfg?.tabKey;
    const varKey = cfg?.varKey;
    const varName = cfg?.variables?.[0]?.variableName;
    if (!tabKey || !varKey || !varName) return;

    // 1. Mutate liveCustomisationData in-place so the FlowCanvas card re-renders
    //    immediately (shows the custom solution section instead of the CTA).
    const targetItem = liveCustomisationData?.[tabKey]?.[varKey]?.[varName]?.[graphicIndex];
    if (targetItem) targetItem.solutionImageIsPuzzleImage = false;
    setDiamondToggleTick(t => t + 1);

    // 2. Persist the bool change to NodeEditor's customisationVars state + save.
    onFlowBoolChange?.(tabKey, varKey, varName, graphicIndex, 'solutionImageIsPuzzleImage', false);

    // 3. Open the solution image sidebar panel after the state update has settled.
    //    setTimeout(0) lets React process the setCustomisationVars from step 2
    //    before activateNode runs, so the sidebar opens with solutionImageIsPuzzleImage=false.
    setTimeout(() => {
      setActiveStepId(step.stepId);
      setActiveGraphicIndex(graphicIndex);
      if (onActivateNode) onActivateNode(step, { itemIndex: graphicIndex, field: 'solutionImage' });
    }, 0);
  }

  function handleDisruptorActivate(step) {
    if (didDrag.current) return;
    setActiveStepId(step.stepId);
    setActiveGraphicIndex(null);
    if (step?.customisationConfig?.meta?.activateAsNode && onActivateNode) {
      onActivateNode(step, {
        itemIndex: 0,
        field: step?.customisationConfig?.meta?.activateField || 'featureID',
      });
      return;
    }
    if (onActivateDisruptor) {
      onActivateDisruptor(step);
    } else {
      handleNodeClick(step);
    }
  }

  function handleDisruptorDrop(step, e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDisruptorStepId(null);
    if (onDropDisruptor) {
      onDropDisruptor(step, e);
    }
  }

  async function commitHashtags({ step, command, raw, currentHashtags, nextHashtags }) {
    const inFlightKey = `${campaignID || ''}:${resolvedFeatureInstanceId || ''}:${command || ''}`;
    if (hashtagCommitInFlightRef.current.has(inFlightKey)) return;

    try {
      hashtagCommitInFlightRef.current.add(inFlightKey);
      const campaignId = campaignID;
      const featureId = resolvedFeatureInstanceId;
      if (!campaignId || !featureId || !command) return;

      const uniqueNext = Array.isArray(nextHashtags)
        ? Array.from(new Set(nextHashtags.filter(Boolean)))
        : Array.from(
            new Set(
              (raw || '')
                .split(/[\s,]+/g)
                .map(s => s.trim())
                .filter(Boolean)
            )
          );
      const uniquePrev = Array.from(new Set((currentHashtags || []).filter(Boolean)));

      const toRemove = uniquePrev.filter(h => !uniqueNext.includes(h));
      const toAdd = uniqueNext.filter(h => !uniquePrev.includes(h));

      // Nothing to do.
      if (toRemove.length === 0 && toAdd.length === 0) return;

      const param = (step?.configuredTriggers || []).find(t => t.command === command)?.param ?? null;

      for (const hashtag of toRemove) {
        await axios.post(deleteHashTagRoute, {
          campaignID: campaignId,
          featureID: featureId,
          featureTemplateID: featureTemplateId,
          command,
          param,
          hashtag: { hashtag },
        });
      }

      for (const hashtag of toAdd) {
        await axios.post(addHashTagRoute, {
          campaignID: campaignId,
          featureID: featureId,
          featureTemplateID: featureTemplateId,
          command,
          param,
          commandParam: param,
          hashtag: { hashtag, cooldown: 0, restricted: false },
        });
      }

      if ((toRemove.length > 0 || toAdd.length > 0) && typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (err) {
      console.error('[FlowCanvas] commitHashtags failed', {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
    } finally {
      hashtagCommitInFlightRef.current.delete(inFlightKey);
    }
  }

  function renderNode(step) {
    const isSelected = activeStepId === step.stepId;
    const kind = stepKind(step);
    // Spacer nodes are invisible — they exist only to pad BFS depth so chains of
    // unequal length still converge at the same column.
    if (kind === 'spacer') return null;
    const props = { step, selected: isSelected, onClick: () => handleNodeClick(step), centreY };
    // Extra X shift applied to standalone stack nodes to make room for the i + add buttons
    // on the incoming connector. Downstream nodes (circles) whose parent is a standalone stack
    // must receive the same shift so the exit connector isn't clipped.
    // X position comes from xOverrides (BFS depth), so all nodes at the same
    // depth land in the same column automatically — works for any future flow.
    const mergedAnchorY = imageStackMergeAnchorByChildId[step.stepId]?.y;
    const isMainSpineStep = (step.layout?.column ?? CENTER_COL) === CENTER_COL
      && (kind === 'circle' || kind === 'card' || kind === 'particleSettings' || kind === 'particleTimer' || kind === 'outcome' || kind === 'countdownSettings' || kind === 'countdownTimer' || kind === 'videoSettings' || kind === 'subBombDisplaySettings' || kind === 'subBombSpeedControl')
      && kind !== 'leaderboardSettings'
      && kind !== 'leaderboardData'
      && kind !== 'leaderboardStartAudio'
      // Exclude circles that connect directly to pollCards — those are intentionally
      // repositioned by BELOW_AUDIO_GAP and must render at their pushed Y so that
      // the PollCardsNode entry connector originates from the correct position.
      && !(kind === 'circle' && (step.connectsTo || []).some(cid => {
        const child = stepMap[cid];
        return child && stepKind(child) === 'pollCards';
      }));
    const resolvedNodeY = isMainSpineStep
      ? ny(step, centreY)  // spine nodes are always exactly centreY — never moved by layout passes
      : (mergedAnchorY ?? yOverrides[step.stepId]);

    if (kind === 'circle') {
      const circleParent = parentMap[step.stepId] ?? null;
      const circleParentKind = circleParent ? stepKind(circleParent) : null;
      const circleExtraX = (circleParentKind === 'videoStack') ? getStackButtonExtraX(circleParent) : 0;
      return <CircleNode key={step.stepId} {...props}
        overrideX={(xOverrides[step.stepId] ?? nx(step)) + circleExtraX}
        overrideY={resolvedNodeY} />;
    }
    if (kind === 'infoButton') {
      const targetStep = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
      return <InfoButtonNode key={step.stepId} {...props}
        onClick={() => {
          if (didDrag.current) {
            logClickDebug('infoButton-click-blocked', { stepId: step?.stepId, targetStepId: targetStep?.stepId });
            return;
          }
          logClickDebug('infoButton-click-fire', { stepId: step?.stepId, targetStepId: targetStep?.stepId });
          setActiveStepId(step.stepId);
          setActiveGraphicIndex(null);
          if (targetStep && (targetStep.customisationConfig?.meta?.pollAudio || targetStep.customisationConfig?.meta?.audioStack)) {
            handleAudioGroupInfoClick(targetStep);
          } else if (onActivateNode) {
            onActivateNode(targetStep || step, null);
          } else {
            onStepClick && onStepClick(targetStep || step);
          }
        }}
        overrideX={xOverrides[step.stepId]}
        overrideY={mergedAnchorY ?? yOverrides[step.stepId]} />;
    }
    if (kind === 'slotReelsCount') {
      return <SlotReelsCountNode key={step.stepId} {...props}
        overrideX={xOverrides[step.stepId]}
        overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
        liveCustomisationData={liveCustomisationData}
        onFlowFieldChange={onFlowFieldChange} />;
    }
    if (kind === 'slotReels') {
      return <SlotReelsNode key={step.stepId} {...props}
        overrideX={xOverrides[step.stepId]}
        overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
        liveCustomisationData={liveCustomisationData}
        activeGraphicIndex={isSelected ? activeGraphicIndex : null}
        onGraphicClick={(payload) => handleGraphicItemClick(step, payload)}
        onFlowFieldChange={onFlowFieldChange}
        campaignID={campaignID}
        campaignFeatures={campaignFeatures}
        onSlotOptionDisruptorDrop={onSlotOptionDisruptorDrop}
        onSlotOptionDisruptorRemove={onSlotOptionDisruptorRemove}
        onSlotOptionRemove={onSlotOptionRemove} />;
    }
    if (kind === 'slotResults') {
      return <SlotResultsNode key={step.stepId} {...props}
        overrideX={xOverrides[step.stepId]}
        overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
        liveCustomisationData={liveCustomisationData}
        campaignID={campaignID}
        campaignFeatures={campaignFeatures}
        activeGraphicIndex={isSelected && typeof activeGraphicIndex === 'number' ? activeGraphicIndex : null}
        onGraphicClick={(payload) => handleGraphicItemClick(step, payload)}
        onFlowFieldChange={onFlowFieldChange}
        onSlotResultAdd={onSlotResultAdd}
        onSlotResultRemove={onSlotResultRemove}
        onSlotResultDisruptorDrop={onSlotResultDisruptorDrop}
        onSlotResultDisruptorRemove={onSlotResultDisruptorRemove} />;
    }
    if (kind === 'decision') {
      return (
        <DiamondNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => handleNodeClick(step)}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          onToggle={() => {
            const dataPath = step.nodeConfig?.decisionDataPath;
            if (!dataPath || !liveCustomisationData) return;
            // dataPath format: "tabKey.varKey.varName.index.field"
            const keys = dataPath.split('.');
            // Traverse to the parent of the leaf for in-place mutation
            let target = liveCustomisationData;
            for (let i = 0; i < keys.length - 2; i++) {
              if (target == null) return;
              const k = keys[i];
              target = isNaN(k) ? target[k] : target[parseInt(k, 10)];
            }
            const parentKey = keys[keys.length - 2];
            const leafKey   = keys[keys.length - 1];
            const parent = isNaN(parentKey)
              ? target?.[parentKey]
              : target?.[parseInt(parentKey, 10)];
            if (!parent || !(leafKey in parent)) return;
            const newVal = !parent[leafKey];
            // 1. Mutate in-place for immediate visual feedback in FlowCanvas
            parent[leafKey] = newVal;
            setDiamondToggleTick(t => t + 1);
            // 2. Persist via the NodeEditor customisationVars chain
            // keys: [tabKey, varKey, varName, index, field]
            if (keys.length >= 5 && onFlowBoolChange) {
              const [tabKey, varKey, varName, idxStr, field] = keys.slice(-5);
              const itemIndex = isNaN(idxStr) ? 0 : parseInt(idxStr, 10);
              onFlowBoolChange(tabKey, varKey, varName, itemIndex, field, newVal);
            }
          }}
        />
      );
    }
    if (kind === 'outcome') {
      return (
        <OutcomeNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => handleNodeClick(step)}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
        />
      );
    }
    if (kind === 'particleTimer') {
      return (
        <ParticleTimerNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) {
              logClickDebug('particleTimer-click-blocked', { stepId: step?.stepId });
              return;
            }
            logClickDebug('particleTimer-click-fire', { stepId: step?.stepId });
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
          onFieldChange={(field, value) => {
            if (!liveCustomisationData) return;
            const settingsArr = liveCustomisationData?.particleOptions?.global?.settings;
            if (!Array.isArray(settingsArr)) return;
            const item = (settingsArr[0] ||= {});
            item[field] = value;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.('particleOptions', 'global', 'settings', 0, field, value);
          }}
        />
      );
    }
    if (kind === 'particleSettings') {
      return (
        <ParticleSettingsNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
          onFieldChange={(field, value) => {
            const cfg = step?.customisationConfig;
            const tabKey = cfg?.tabKey;
            const varKey = cfg?.varKey;
            const varName = cfg?.variables?.[0]?.variableName;
            if (!liveCustomisationData || !tabKey || !varKey || !varName) return;

            const arr = liveCustomisationData?.[tabKey]?.[varKey]?.[varName];
            if (!Array.isArray(arr)) return;
            const item = (arr[0] ||= {});
            item[field] = value;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(tabKey, varKey, varName, 0, field, value);
          }}
        />
      );
    }
    if (kind === 'infoBoxNode') {
      return (
        <InfoBoxNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
          onFieldFocus={(fieldSettings) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex({ fieldSettings });
            if (onActivateNode) onActivateNode(step, { fieldSettings });
          }}
          onFieldChange={(varName, field, value) => {
            const cfg    = step?.customisationConfig;
            const tabKey = cfg?.tabKey;
            const varKey = cfg?.varKey;
            if (!liveCustomisationData || !tabKey || !varKey || !varName) return;
            const arr = liveCustomisationData?.[tabKey]?.[varKey]?.[varName];
            if (!Array.isArray(arr)) return;
            const item = (arr[0] ||= {});
            item[field] = value;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(tabKey, varKey, varName, 0, field, value);
          }}
        />
      );
    }
    if (kind === 'statusScreenImageNode') {
      return (
        <StatusScreenImageNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          assetFolder={assetFolder}
          campaignId={campaignId}
          item={resolveStepItems(step)[0] ?? null}
        />
      );
    }
    if (kind === 'animatedOverlayMainView') {
      return (
        <AnimatedOverlayMainViewNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
          onFieldChange={(field, value) => {
            const cfg = step?.customisationConfig;
            const tabKey = cfg?.tabKey;
            const varKey = cfg?.varKey;
            const varName = cfg?.variables?.[0]?.variableName;
            if (!liveCustomisationData || !tabKey || !varKey || !varName) return;
            const arr = liveCustomisationData?.[tabKey]?.[varKey]?.[varName];
            if (!Array.isArray(arr)) return;
            const item = (arr[0] ||= {});
            item[field] = value;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(tabKey, varKey, varName, 0, field, value);
          }}
        />
      );
    }
    if (kind === 'countdownSettings') {
      return (
        <CountdownSettingsNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
          onFieldChange={(field, value) => {
            const cfg     = step?.customisationConfig;
            const tabKey  = cfg?.tabKey;
            const varKey  = cfg?.varKey;
            const varName = cfg?.variables?.[0]?.variableName;
            if (!liveCustomisationData || !tabKey || !varKey || !varName) return;
            const arr = liveCustomisationData?.[tabKey]?.[varKey]?.[varName];
            if (!Array.isArray(arr)) return;
            const item = (arr[0] ||= {});
            item[field] = value;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(tabKey, varKey, varName, 0, field, value);
          }}
        />
      );
    }
    if (kind === 'countdownTimer') {
      return (
        <CountdownTimerNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
          onFieldChange={(field, value) => {
            const cfg     = step?.customisationConfig;
            const tabKey  = cfg?.tabKey;
            const varKey  = cfg?.varKey;
            const varName = cfg?.variables?.[0]?.variableName;
            if (!liveCustomisationData || !tabKey || !varKey || !varName) return;
            const arr = liveCustomisationData?.[tabKey]?.[varKey]?.[varName];
            if (!Array.isArray(arr)) return;
            const item = (arr[0] ||= {});
            item[field] = value;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(tabKey, varKey, varName, 0, field, value);
          }}
        />
      );
    }
    if (kind === 'videoSettings') {
      return (
        <VideoSettingsNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId] ?? nx(step)}
          overrideY={resolvedNodeY}
          liveFeatureData={liveFeatureData}
          liveCustomisationData={liveCustomisationData}
          onFieldChange={(field, value) => {
            if (liveCustomisationData) {
              if (!liveCustomisationData.controls) liveCustomisationData.controls = {};
              if (!liveCustomisationData.controls.controls) liveCustomisationData.controls.controls = {};
              liveCustomisationData.controls.controls[field] = value;
            }
            setDiamondToggleTick(t => t + 1);
            onVideoSettingsChange?.(field, value);
          }}
        />
      );
    }
    if (kind === 'countdownImageStack') {
      const cdCfg     = step.customisationConfig;
      const cdTabKey  = cdCfg?.tabKey;
      const cdVarKey  = cdCfg?.varKey;
      const cdVarName = cdCfg?.variables?.[0]?.variableName;
      return (
        <CountdownImageStackNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          selectedIndex={isSelected ? activeGraphicIndex : null}
          onClick={(idx) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(idx ?? null);
            if (onActivateNode) onActivateNode(step, idx ?? null);
          }}
          onReorder={(fromIdx, toIdx) => {
            const arr = liveCustomisationData?.[cdTabKey]?.[cdVarKey]?.[cdVarName];
            if (!cdTabKey || !cdVarKey || !cdVarName || toIdx < 0 || !Array.isArray(arr) || toIdx >= arr.length) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(cdTabKey, cdVarKey, cdVarName, fromIdx, '_reorder_', toIdx);
          }}
          onRemove={(idx) => {
            if (!cdTabKey || !cdVarKey || !cdVarName) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(cdTabKey, cdVarKey, cdVarName, idx, '_delete_', null);
          }}
          onCardFieldChange={(cardIdx, field, value) => {
            if (!cdTabKey || !cdVarKey || !cdVarName) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(cdTabKey, cdVarKey, cdVarName, cardIdx, field, value);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
        />
      );
    }
    if (kind === 'countdownTextStack') {
      const cdCfg     = step.customisationConfig;
      const cdTabKey  = cdCfg?.tabKey;
      const cdVarKey  = cdCfg?.varKey;
      const cdVarName = cdCfg?.variables?.[0]?.variableName;
      return (
        <CountdownTextStackNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          selectedIndex={isSelected ? activeGraphicIndex : null}
          onClick={(idx) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(idx ?? null);
            if (onActivateNode) onActivateNode(step, idx ?? null);
          }}
          onReorder={(fromIdx, toIdx) => {
            const arr = liveCustomisationData?.[cdTabKey]?.[cdVarKey]?.[cdVarName];
            if (!cdTabKey || !cdVarKey || !cdVarName || toIdx < 0 || !Array.isArray(arr) || toIdx >= arr.length) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(cdTabKey, cdVarKey, cdVarName, fromIdx, '_reorder_', toIdx);
          }}
          onRemove={(idx) => {
            if (!cdTabKey || !cdVarKey || !cdVarName) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(cdTabKey, cdVarKey, cdVarName, idx, '_delete_', null);
          }}
          onCardFieldChange={(cardIdx, field, value) => {
            if (!cdTabKey || !cdVarKey || !cdVarName) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(cdTabKey, cdVarKey, cdVarName, cardIdx, field, value);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={resolvedNodeY}
          liveCustomisationData={liveCustomisationData}
        />
      );
    }
    if (kind === 'addButton') {
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepY = yOverrides[step.stepId] ?? ny(step, centreY);
      const aY    = mergedAnchorY ?? stepY;
      const targetStep = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
      return (
        <g
          key={step.stepId}
          className="fc-node fc-node--add-button"
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (targetStep && stepKind(targetStep) === 'presetTextNode' && onAddPreset) {
              onAddPreset(targetStep);
            } else if (targetStep && (stepKind(targetStep) === 'audioStack' || stepKind(targetStep) === 'pollAudio')) {
              handleAudioAddClick(targetStep);
            } else if (onActivateNode) {
              onActivateNode(targetStep || step, null);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          {step.stepTitle && (
            <text
              x={stepX} y={aY - PLUS_R - 8}
              textAnchor="middle" fontSize="9.5" fontWeight="600" fill={C.green}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >{step.stepTitle}</text>
          )}
          <circle cx={stepX} cy={aY} r={PLUS_R} fill={isSelected ? C.sel : C.green} stroke="none" />
          <text
            x={stepX} y={aY + 1}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="16" fontWeight="300" fill="#fff"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >+</text>
        </g>
      );
    }
    if (kind === 'trigger') {
      // Hashtag triggers that are direct children of a waveGraphics or zapperObjects step
      // are rendered inline inside those fragments (with a live-computed Y).
      // Skip them here to avoid a stale-Y duplicate rendering.
      const trigParent = parentMap[step.stepId];
      if (trigParent && (
        stepKind(trigParent) === 'waveGraphics' ||
        stepKind(trigParent) === 'zapperObjects'
      )) return null;
      const trigX = xOverrides[step.stepId] ?? nx(step);
      const trigY = mergedAnchorY ?? yOverrides[step.stepId] ?? ny(step, centreY);
      const isStandaloneHashtagTrigger = /hashtag/i.test(step.stepTitle || '') || /hashtag/i.test(step.stepDescription || '');
      const parentX = trigParent ? (xOverrides[trigParent.stepId] ?? nx(trigParent)) : null;
      const plusX = (parentX != null)
        ? ((parentX + halfW(trigParent)) + (trigX - TRIG_W / 2)) / 2
        : trigX - TRIG_W / 2 - PLUS_R - 10;
      const focusHashtagNode = () => {
        if (didDrag.current) return;
        setActiveStepId(step.stepId);
        setHashtagFocus(cur => ({ stepId: step.stepId, nonce: cur.nonce + 1 }));
      };
      return (
        <React.Fragment key={step.stepId}>
          {isStandaloneHashtagTrigger && (
            <g
              className="fc-node fc-node--hashtag-add"
              onClick={focusHashtagNode}
              style={{ cursor: 'pointer' }}
            >
              <text
                x={plusX} y={trigY - PLUS_R - 8}
                textAnchor="middle" fontSize="9.5" fontWeight="600" fill={C.green}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >Add Hashtag</text>
              <circle cx={plusX} cy={trigY} r={PLUS_R} fill={C.green} stroke="none" />
              <text
                x={plusX} y={trigY + 1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="16" fontWeight="300" fill="#fff"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >+</text>
            </g>
          )}
          <TriggerNode
            {...props}
            overrideX={trigX}
            overrideY={trigY}
            editable={!!campaignID && !!resolvedFeatureInstanceId}
            onCommitHashtags={commitHashtags}
            focusNonce={hashtagFocus.stepId === step.stepId ? hashtagFocus.nonce : 0}
          />
        </React.Fragment>
      );
    }
    if (step.stepId === 'lt_show_header_settings') {
      return (
        <LiveTextSettingsNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => handleNodeClick(step)}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          onToggleHeaders={(nextVal) => {
            onFlowBoolChange?.('setup', 'templates', 'settings', 0, 'addHeader', nextVal);
          }}
        />
      );
    }
    if (kind === 'subBombDisplaySettings') {
      return (
        <SubBombDisplaySettingsNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          assetFolder={assetFolder}
          campaignId={campaignID}
          onFieldChange={(varName, field, value) => {
            const cfg = step?.customisationConfig;
            const tabKey = cfg?.tabKey;
            const varKey = cfg?.varKey;
            if (!tabKey || !varKey || !varName) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(tabKey, varKey, varName, 0, field, value);
          }}
        />
      );
    }
    if (kind === 'subBombSpeedControl') {
      return (
        <SubBombSpeedNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          onClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          onFieldChange={(field, value) => {
            const cfg = step?.customisationConfig;
            const tabKey = cfg?.tabKey;
            const varKey = cfg?.varKey;
            const varName = cfg?.variables?.[0]?.variableName;
            if (!tabKey || !varKey || !varName) return;
            setDiamondToggleTick(t => t + 1);
            onFlowFieldChange?.(tabKey, varKey, varName, 0, field, value);
          }}
        />
      );
    }
    if (kind === 'card')         return <CardNode key={step.stepId} {...props}
      overrideX={xOverrides[step.stepId]}
      overrideY={mergedAnchorY ?? yOverrides[step.stepId]} />;
    if (kind === 'disruptorTrigger') {
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepY = yOverrides[step.stepId] ?? ny(step, centreY);
      return (
        <DisruptorNode key={step.stepId}
          step={step} stepX={stepX} stepY={stepY}
          isSelected={isSelected}
          isDragOver={dragOverDisruptorStepId === step.stepId}
          liveCustomisationData={liveCustomisationData}
          campaignFeatures={campaignFeatures}
          onActivate={
            () => {
              handleDisruptorActivate(step);
            }
          }
          onSelectItem={onSelectPlacedComponent}
          onDragOver={(e) => { e.preventDefault(); setDragOverDisruptorStepId(step.stepId); }}
          onDragLeave={() => setDragOverDisruptorStepId(cur => cur === step.stepId ? null : cur)}
          onDrop={(e) => handleDisruptorDrop(step, e)}
          onRemoveDisruptor={onRemoveDisruptor}
          featureInfo={featureInfo}
        />
      );
    }
    if (kind === 'leaderboardTrigger') {
      return (
        <LeaderboardTriggerNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          isDragOver={dragOverDisruptorStepId === step.stepId}
          onActivate={() => handleDisruptorActivate(step)}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOverDisruptorStepId(step.stepId);
          }}
          onDragLeave={() => setDragOverDisruptorStepId((current) => (current === step.stepId ? null : current))}
          onDrop={(event) => handleDisruptorDrop(step, event)}
          onRemove={() => onFlowFieldChange?.('setup', 'leaderboards', 'disruptor', 0, 'featureID', '')}
          onFieldChange={(field, value) => onFlowFieldChange?.('setup', 'leaderboards', 'disruptor', 0, field, value)}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          campaignFeatures={campaignFeatures}
        />
      );
    }
    if (kind === 'leaderboardStartAudio') {
      return (
        <LeaderboardStartAudioNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          onActivateMusic={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            const payload = {
              groupVarKey: 'leaderboardSoundscape',
              slotVarName: 'leaderboardMusic',
              selectionMode: 'group',
              toggleVarName: 'playMusic',
            };
            setActiveGraphicIndex(payload);
            if (onActivateNode) onActivateNode(step, payload);
          }}
          onActivateStartAudio={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            const payload = {
              groupVarKey: 'triggerSFX',
              slotVarName: 'start',
              selectionMode: 'group',
              toggleVarName: 'playStartAudio',
            };
            setActiveGraphicIndex(payload);
            if (onActivateNode) onActivateNode(step, payload);
          }}
        />
      );
    }
    if (kind === 'leaderboardSettings') {
      return (
        <LeaderboardSettingsNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          onActivate={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          onFieldChange={(field, value) => onFlowFieldChange?.('leaderboard', 'settings', 'setup', 0, field, value)}
        />
      );
    }
    if (kind === 'leaderboardData') {
      return (
        <LeaderboardDataNode
          key={step.stepId}
          step={step}
          selected={isSelected}
          centreY={centreY}
          overrideX={xOverrides[step.stepId]}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          liveCustomisationData={liveCustomisationData}
          campaignId={campaignID}
          assetFolder={assetFolder}
          selectedFieldDataLink={selectedFieldDataLink}
          activeEntryField={isSelected ? activeGraphicIndex : null}
          onActivate={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          onAddEntry={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          onEntryFieldChange={(itemIndex, field, value) => onFlowFieldChange?.('leaderboard', 'leaderboardData', 'entries', itemIndex, field, value)}
          onDeleteEntry={(itemIndex) => onFlowFieldChange?.('leaderboard', 'leaderboardData', 'entries', itemIndex, '_delete_', null)}
          onActivateEntryField={(itemIndex, field) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            const activeInfo = { itemIndex, field };
            setActiveGraphicIndex(activeInfo);
            if (onActivateNode) onActivateNode(step, activeInfo);
          }}
        />
      );
    }
    if (kind === 'waveWinner') {
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepY = yOverrides[step.stepId] ?? ny(step, centreY);
      return (
        <WaveWinnerNode key={step.stepId}
          step={step} stepX={stepX} stepY={stepY}
          isSelected={isSelected}
          isDragOver={dragOverDisruptorStepId === step.stepId}
          liveCustomisationData={liveCustomisationData}
          campaignID={campaignID}
          assetFolder={assetFolder}
          campaignFeatures={campaignFeatures}
          onActivate={() => {
            const tf = step.customisationConfig?.meta?.teamWinnerFilter ?? 0;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, tf);
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOverDisruptorStepId(step.stepId); }}
          onDragLeave={() => setDragOverDisruptorStepId(c => c === step.stepId ? null : c)}
          onDrop={(e) => handleDisruptorDrop(step, e)}
          onRemoveDisruptor={onRemoveDisruptor}
        />
      );
    }
    if (kind === 'waveGraphics') {
      const wCfgTabKey  = step.customisationConfig?.tabKey;
      const wCfgVarKey  = step.customisationConfig?.varKey;
      const wCfgVarName = step.customisationConfig?.variables?.[0]?.variableName;
      const liveVals = liveCustomisationData && wCfgTabKey && wCfgVarKey && wCfgVarName
        ? liveCustomisationData?.[wCfgTabKey]?.[wCfgVarKey]?.[wCfgVarName] : null;
      const wVals   = liveVals || step.populatedData?.variables?.[0]?.allValues || [];
      const wFilter    = step.customisationConfig?.meta?.teamFilter;
      const wAssign    = step.customisationConfig?.meta?.teamAssign ?? wFilter;
      const wHandFilter = step.customisationConfig?.meta?.handFilter;
      const wItems  = wFilter !== undefined ? wVals.filter(v => (v.team === wAssign || v.team === 2) && (wHandFilter == null || v.hand === wHandFilter)) : wVals;

      const stepX      = xOverrides[step.stepId] ?? nx(step);
      const parentStep = parentMap[step.stepId];
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const teamCY     = yOverrides[step.stepId] ?? (parentStep ? ny(parentStep, centreY) : ny(step, centreY));

      const count  = Math.max(wItems.length, 1);
      const totalH = count * (VOTE_CARD_H + VOTE_CARD_GAP) - VOTE_CARD_GAP;

      const hashStepId = step.connectsTo?.[0];
      const hashStep   = hashStepId ? stepMap[hashStepId] : null;
      const hashNodeX  = hashStep ? (xOverrides[hashStep.stepId] ?? nx(hashStep)) : null;
      // hashCY is where the TriggerNode wire connects. TriggerNode anchors its top at y - TRIG_H/2
      // (header height, not full expanded height), so offset by TRIG_H/2 so the node top starts
      // right below the card stack and grows downward without overlapping the next team's cards.
      const hashCY     = hashStep ? (teamCY + totalH / 2 + HASH_GAP + TRIG_H / 2) : null;

      return (
        <WaveGraphicsNode key={step.stepId}
          step={step} stepX={stepX} teamCY={teamCY}
          parentStepX={parentStepX}
          hashStep={hashStep} hashCY={hashCY} hashNodeX={hashNodeX}
          wVals={wVals} wItems={wItems}
          activeStepId={activeStepId} activeGraphicIndex={activeGraphicIndex}
          assetFolder={assetFolder} campaignId={campaignId}
          onGraphicClick={(absIdx) => handleGraphicItemClick(step, absIdx)}
          onAddGraphicClick={() => { if (!didDrag.current) handleAddGraphicClick(step); }}
          onHashtagAddClick={() => {
            if (didDrag.current) return;
            if (hashStep?.stepId) {
              setActiveStepId(hashStep.stepId);
              setHashtagFocus(cur => ({ stepId: hashStep.stepId, nonce: cur.nonce + 1 }));
            }
          }}
          onHashtagNodeClick={() => handleNodeClick(hashStep)}
          canEditHashtags={!!campaignID && !!resolvedFeatureInstanceId}
          onCommitHashtags={commitHashtags}
          hashtagFocusNonce={hashStep?.stepId && hashtagFocus.stepId === hashStep.stepId ? hashtagFocus.nonce : 0}
          onRemoveGraphic={onRemoveGraphic}
          onDraftHeightChange={hashStep ? (h) => handleDraftHeightChange(hashStep.stepId, h) : undefined}
        />
      );
    }
    if (kind === 'zapperObjects') {
      const cfg       = step.customisationConfig;
      const tabKey    = cfg?.tabKey;
      const varKey    = cfg?.varKey;
      const varName   = cfg?.variables?.[0]?.variableName;
      const liveVals  = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName] : null;
      const items     = liveVals || step.populatedData?.variables?.[0]?.allValues || [];

      const stepX       = xOverrides[step.stepId] ?? nx(step);
      const stepCY      = yOverrides[step.stepId] ?? ny(step, centreY);
      const parentStep  = parentMap[step.stepId];
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const hashStepId = step.connectsTo?.[0];
      const hashStep   = hashStepId ? stepMap[hashStepId] : null;
      const hashNodeX  = hashStep ? (xOverrides[hashStep.stepId] ?? nx(hashStep)) : null;
      const count      = Math.max(items.length, 1);
      const totalH     = count * (VOTE_CARD_H + VOTE_CARD_GAP) - VOTE_CARD_GAP;
      const hashCY     = hashStep ? (stepCY + totalH / 2 + HASH_GAP + TRIG_H / 2) : null;
      return (
        <ZapperObjectsNode key={step.stepId}
          step={step} stepX={stepX} stepCY={stepCY}
          parentStepX={parentStepX}
          hashStep={hashStep}
          hashCY={hashCY}
          hashNodeX={hashNodeX}
          items={items}
          activeStepId={activeStepId}
          activeGraphicIndex={activeGraphicIndex}
          assetFolder={assetFolder}
          campaignId={campaignId}
          onObjectClick={(fileIndex) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(fileIndex);
            if (onActivateNode) onActivateNode(step, fileIndex);
          }}
          onAddObjectClick={() => {
            if (!didDrag.current) handleAddGraphicClick(step);
          }}
          onHashtagAddClick={() => {
            if (didDrag.current) return;
            if (hashStep?.stepId) {
              setActiveStepId(hashStep.stepId);
              setHashtagFocus(cur => ({ stepId: hashStep.stepId, nonce: cur.nonce + 1 }));
            }
          }}
          onHashtagNodeClick={() => handleNodeClick(hashStep)}
          canEditHashtags={!!campaignID && !!resolvedFeatureInstanceId}
          onCommitHashtags={commitHashtags}
          hashtagFocusNonce={hashStep?.stepId && hashtagFocus.stepId === hashStep.stepId ? hashtagFocus.nonce : 0}
          onRemoveObject={(nodeStep, fileIndex) => {
            if (!didDrag.current && onRemoveGraphic) onRemoveGraphic(nodeStep, fileIndex);
          }}
          onDraftHeightChange={hashStep ? (h) => handleDraftHeightChange(hashStep.stepId, h) : undefined}
        />
      );
    }
    if (kind === 'imageStack') {
      const cfg = step.customisationConfig;
      const varKey = cfg?.varKey;
      const isOverlayStack = varKey === 'overlayImageFiles';
      const behaviourRaw = String(liveCustomisationData?.customImageOverlay?.imageSettings?.triggerBehaviour?.[0]?.behaviour || '').toLowerCase();
      const behaviourLabel = !isOverlayStack && behaviourRaw === 'random'
        ? 'Random'
        : !isOverlayStack && behaviourRaw === 'sequence'
          ? 'Sequential'
          : null;
      const items = resolveStepItems(step);

      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepCY = yOverrides[step.stepId] ?? ny(step, centreY);
      const parentStep = parentMap[step.stepId] ?? null;
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const parentStepY = parentStep
        ? (imageStackBranchMidYByParent[parentStep.stepId] ?? yOverrides[parentStep.stepId] ?? ny(parentStep, centreY))
        : null;
      const visualStyle = step.stepId === 'sb_explosion_items_node' ? 'wave' : 'default';

      return (
        <ImageStackNode
          key={step.stepId}
          step={step}
          stepX={stepX}
          stepCY={stepCY}
          parentStep={parentStep}
          parentStepX={parentStepX}
          parentStepY={parentStepY}
          items={items}
          activeStepId={activeStepId}
          activeGraphicIndex={activeGraphicIndex}
          assetFolder={assetFolder}
          campaignId={campaignId}
          onImageClick={(idx) => handleGraphicItemClick(step, idx)}
          onAddImageClick={() => { if (!didDrag.current) handleAddGraphicClick(step); }}
          onImageFieldChange={onImageFieldChange}
          onRemoveImage={(idx) => {
            if (didDrag.current) return;
            if (onRemoveGraphic) onRemoveGraphic(step, idx);
          }}
          visualStyle={visualStyle}
          typeLabel={isOverlayStack ? 'Overlay Image' : 'Main Image'}
          behaviourLabel={behaviourLabel}
          showPositionInputs={isOverlayStack}
        />
      );
    }
    if (kind === 'particleImageStack' || kind === 'particleColourStack') {
      const items = resolveStepItems(step);
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepCY = yOverrides[step.stepId] ?? ny(step, centreY);
      const parentStep = parentMap[step.stepId] ?? null;
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const parentStepY = parentStep
        ? (imageStackBranchMidYByParent[parentStep.stepId] ?? yOverrides[parentStep.stepId] ?? ny(parentStep, centreY))
        : null;
      const variant = kind === 'particleColourStack' ? 'colour' : 'image';
      return (
        <ParticleStackNode
          key={step.stepId}
          step={step}
          stepX={stepX}
          stepCY={stepCY}
          parentStep={parentStep}
          parentStepX={parentStepX}
          parentStepY={parentStepY}
          items={items}
          activeStepId={activeStepId}
          activeGraphicIndex={activeGraphicIndex}
          assetFolder={assetFolder}
          campaignId={campaignId}
          onItemClick={(idx) => handleGraphicItemClick(step, idx)}
          onAddItemClick={() => { if (!didDrag.current) handleAddGraphicClick(step); }}
          onRemoveItem={(idx) => {
            if (didDrag.current) return;
            if (onRemoveGraphic) onRemoveGraphic(step, idx);
          }}
          variant={variant}
          titleBase={variant === 'colour' ? 'Colour' : 'Custom Image'}
          showFireworksVariancePreview={variant === 'colour' && step?.customisationConfig?.varKey === 'fireworksSettings'}
          typeLabel={variant === 'colour' ? 'Confetti colour' : 'Confetti image'}
        />
      );
    }
    if (kind === 'pollAudio') {
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const parentStep = parentMap[step.stepId] ?? null;
      const nextStep = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
      return (
        <PollAudioNode key={step.stepId}
          step={step} stepX={stepX} centreY={centreY}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          isSelected={isSelected}
          liveCustomisationData={liveCustomisationData}
          activeSlotVarName={activeAudioSlotVarName}
          assetFolder={assetFolder}
          campaignId={campaignId}
          parentStepX={parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null}
          parentStepY={parentStep ? (connectorYOverrides[parentStep.stepId] ?? yOverrides[parentStep.stepId] ?? ny(parentStep, centreY)) : null}
          parentHalfW={parentStep ? halfW(parentStep) : null}
          nextStep={nextStep}
          nextStepX={nextStep ? (xOverrides[nextStep.stepId] ?? nx(nextStep)) : null}
          nextStepY={nextStep ? (connectorYOverrides[nextStep.stepId] ?? yOverrides[nextStep.stepId] ?? ny(nextStep, centreY)) : null}
          nextStepHalfW={nextStep ? halfW(nextStep) : null}
          onActivate={() => {
            if (didDrag.current) return;
            // If the primary slot has a single disabled file, treat an outer-card
            // click the same as clicking the CTA — re-enables in one click instead
            // of needing to click the card body then the small CTA box separately.
            const _cfg = step.customisationConfig;
            const _primarySlot = _cfg?.variables?.[0];
            const _arr = _primarySlot
              ? liveCustomisationData?.[_cfg.tabKey]?.[_cfg.varKey]?.[_primarySlot.variableName]
              : null;
            const _isCTA = Array.isArray(_arr)
              ? (_arr.length === 0 || (_arr.length === 1 && _arr[0]?.playSFX === false))
              : true;
            if (_isCTA && _primarySlot) {
              handleAudioAddClick(step);
              return;
            }
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          onActivateFile={({ slotVarName, fileIndex, toggleVarName }) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex({ slotVarName, fileIndex });
            // pass slot info so NodeEditor navigates to the correct sub-group
            if (onActivateNode) onActivateNode(step, { slotVarName, fileIndex, toggleVarName });
          }}
          onRemoveFile={({ slotVarName, fileIndex, toggleVarName }) => {
            if (didDrag.current) return;
            if (onRemoveGraphic) onRemoveGraphic(step, { slotVarName, fileIndex, toggleVarName });
          }}
          onInfoClick={() => {
            logClickDebug('pollAudio-info-click', { stepId: step?.stepId });
            handleAudioGroupInfoClick(step);
          }}
          onAddItemClick={() => {
            logClickDebug('pollAudio-add-click', { stepId: step?.stepId });
            handleAudioAddClick(step);
          }}
          addButtonLabel={step.customisationConfig?.meta?.addButtonLabel || 'Add Audio'}
          standaloneLayout
        />
      );
    }
    if (kind === 'audioStack') {
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepCY = yOverrides[step.stepId] ?? ny(step, centreY);
      const parentStep = parentMap[step.stepId] ?? null;
      const nextStep = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
      const firstSlot = step.customisationConfig?.variables?.[0] ?? null;
      const items = resolveStepItems(step);
      const audioSiblingGroup = parentStep
        ? visibleFlow
          .filter(s => parentMap[s.stepId]?.stepId === parentStep.stepId && stepKind(s) === 'audioStack')
          .sort((a, b) => (yOverrides[a.stepId] ?? ny(a, centreY)) - (yOverrides[b.stepId] ?? ny(b, centreY)))
        : [step];
      const audioExitGroup = parentStep && nextStep
        ? audioSiblingGroup.filter(s => s.connectsTo?.[0] === nextStep.stepId)
        : [step];
      const entryLeader = audioSiblingGroup[0] ?? step; // kept for sharedEntryBottomY
      const exitLeader = audioExitGroup[0] ?? step;
      const inMergeGroup = nextStep ? mergeConnectionKeys.has(`${step.stepId}--${nextStep.stepId}`) : false;
      const sharedEntryBottomY = null; // each sibling draws its own entry independently
      const sharedExitBottomY = audioExitGroup.length > 1 && exitLeader.stepId === step.stepId
        ? Math.max(
          ...audioExitGroup.map(s => {
            const siblingCY = yOverrides[s.stepId] ?? ny(s, centreY);
            return siblingCY + renderedHalfH(s) - AUDIO_CARD_H / 2;
          }),
          nextStep ? (connectorYOverrides[nextStep.stepId] ?? yOverrides[nextStep.stepId] ?? ny(nextStep, centreY)) : -Infinity
        )
        : null;
      const nextStepComputedX = (() => {
        if (!nextStep) return null;
        const nextBaseX = xOverrides[nextStep.stepId] ?? nx(nextStep);
        return nextBaseX;
      })();
      return (
        <AudioStackNode key={step.stepId}
          step={step}
          stepX={stepX}
          stepCY={stepCY}
          parentStep={parentStep}
          parentStepHalfW={parentStep
            ? (stepKind(parentStep) === 'audioStack'
              ? ((audioStackHalfWByStepId[parentStep.stepId] ?? halfW(parentStep)) + 14)
              : halfW(parentStep))
            : null}
          parentStepX={parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null}
          parentStepY={parentStep ? (connectorYOverrides[parentStep.stepId] ?? yOverrides[parentStep.stepId] ?? ny(parentStep, centreY)) : null}
          nextStep={nextStep}
          nextStepX={nextStepComputedX}
          nextStepY={nextStep ? (connectorYOverrides[nextStep.stepId] ?? yOverrides[nextStep.stepId] ?? ny(nextStep, centreY)) : null}
          nextStepHalfW={nextStep
            ? (stepKind(nextStep) === 'audioStack' ? (AUDIO_CARD_W / 2 + 14) : halfW(nextStep))
            : null}
          sharedBranchX={audioSharedBranchXByStepId[step.stepId] ?? null}
          items={items}
          activeStepId={activeStepId}
          activeGraphicIndex={activeStepId === step.stepId ? activeGraphicIndex : null}
          hideSourceTrunk={false}
          hideExitTrunk={inMergeGroup || (audioExitGroup.length > 1 && exitLeader.stepId !== step.stepId)}
          sharedEntryBottomY={sharedEntryBottomY}
          sharedExitBottomY={sharedExitBottomY}
          assetFolder={assetFolder}
          campaignId={campaignId}
          slotVarName={firstSlot?.variableName || null}
          onAudioClick={(fileIndex) => {
            if (didDrag.current) return;
            const slotVarName = firstSlot?.variableName;
            const toggleVarName = firstSlot?.toggleVarName || slotVarName;
            if (!slotVarName) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex({ slotVarName, fileIndex });
            if (onActivateNode) onActivateNode(step, { slotVarName, fileIndex, toggleVarName });
          }}
          onRemoveAudio={(fileIndex) => {
            const slotVarName = firstSlot?.variableName;
            const toggleVarName = firstSlot?.toggleVarName || slotVarName;
            if (!slotVarName) return;
            const wasDragging = didDrag.current;
            didDrag.current = false;
            console.log('[FlowCanvas] audioStack remove request', {
              stepId: step.stepId,
              fileIndex,
              slotVarName,
              toggleVarName,
              wasDragging,
            });
            if (onRemoveGraphic) onRemoveGraphic(step, { slotVarName, fileIndex, toggleVarName });
          }}
          onInfoClick={() => {
            logClickDebug('audioStack-info-click', { stepId: step?.stepId });
            handleAudioGroupInfoClick(step);
          }}
          onAddItemClick={() => {
            didDrag.current = false;
            logClickDebug('audioStack-add-click', { stepId: step?.stepId });
            handleAudioAddClick(step);
          }}
          addButtonLabel={step.customisationConfig?.meta?.addButtonLabel || 'Add Audio'}
          standaloneLayout
        />
      );
    }
    if (kind === 'pollCards') {
      const stepX      = xOverrides[step.stepId] ?? nx(step);
      const parentStep = parentMap[step.stepId];
      const nextStep = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const parentStepY = parentStep ? (yOverrides[parentStep.stepId] ?? ny(parentStep, centreY)) : null;
      // Use the yOverrides value (which the sibling-pusher may have set to clear audio),
      // falling back to parentStepY so the stack centres on Vote Start by default.
      const pollCardsY = yOverrides[step.stepId] ?? parentStepY ?? centreY;
      // Find the pollAudio that merges into the exit stem:
      // First check direct sibling (same parent as polls_node),
      // then check uncle (sibling of polls_node's parent, e.g. ingame_audio under vote_start_node).
      const grandparentStep = parentStep ? parentMap[parentStep.stepId] : null;
      const audioSibling = (() => {
        if (parentStep) {
          const direct = visibleFlow.find(
            s => s.stepId !== step.stepId
              && parentMap[s.stepId]?.stepId === parentStep.stepId
              && stepKind(s) === 'pollAudio'
          );
          if (direct) return direct;
        }
        // Uncle: pollAudio that is a sibling of parentStep (poll_data_node) under grandparent
        if (grandparentStep) {
          return visibleFlow.find(
            s => parentMap[s.stepId]?.stepId === grandparentStep.stepId
              && stepKind(s) === 'pollAudio'
          ) ?? null;
        }
        return null;
      })();
      const _audioSibX = audioSibling
        ? (xOverrides[audioSibling.stepId] ?? (audioSibling.layout ? nx(audioSibling) : null))
        : null;
      const audioSiblingRightX = _audioSibX != null ? _audioSibX + CARD_W / 2 : null;
      const audioSiblingMidY = audioSibling
        ? (yOverrides[audioSibling.stepId] ?? (audioSibling.layout ? ny(audioSibling, centreY) : centreY))
        : null;
      const isTriviaCards = step.customisationConfig?.meta?.triviaCards === true;
      if (isTriviaCards) {
        return (
          <TriviaCardsNode key={step.stepId}
            step={step}
            stepX={stepX}
            centreY={centreY}
            overrideY={pollCardsY}
            isSelected={isSelected}
            activeTriviaIndex={isSelected ? activeGraphicIndex : null}
            liveCustomisationData={liveCustomisationData}
            campaignFeatures={campaignFeatures}
            parentStepX={parentStepX}
            parentStepY={parentStepY}
            nextStepX={nextStep ? (xOverrides[nextStep.stepId] ?? nx(nextStep)) : null}
            nextStepY={nextStep ? (yOverrides[nextStep.stepId] ?? ny(nextStep, centreY)) : null}
            nextStepHalfW={nextStep ? halfW(nextStep) : null}
            audioSiblingRightX={audioSiblingRightX}
            audioSiblingMidY={audioSiblingMidY}
            assetFolder={featureInfo?.defaultAssetFolder}
            campaignId={campaignID}
            selectedFieldDataLink={selectedFieldDataLink}
            onHeightChange={(h) => handlePollCardHeightChange(step.stepId, h)}
            onRemoveTrivia={(roundVarKey, questionIdx) => {
              if (didDrag.current) return;
              if (onRemoveGraphic) onRemoveGraphic(step, { roundVarKey, questionIdx });
            }}
            onTriviaClick={(roundVarKey, questionIdx) => {
              if (didDrag.current) return;
              setActiveStepId(step.stepId);
              const activeInfo = { roundVarKey, questionIdx };
              setActiveGraphicIndex(activeInfo);
              if (onActivateNode) onActivateNode(step, activeInfo);
            }}
            onTriviaFieldActivate={(roundVarKey, questionIdx, field) => {
              if (didDrag.current) return;
              console.log('[TriviaFieldDebug][FlowCanvas] onTriviaFieldActivate', {
                stepId: step.stepId,
                roundVarKey,
                questionIdx,
                field,
              });
              setActiveStepId(step.stepId);
              const activeInfo = { roundVarKey, questionIdx, field };
              setActiveGraphicIndex(activeInfo);
              if (onActivateNode) onActivateNode(step, activeInfo);
            }}
            onAddTriviaClick={(roundVarKey) => {
              if (didDrag.current) return;
              setActiveStepId(step.stepId);
              const addInfo = { roundVarKey, add: true };
              setActiveGraphicIndex(addInfo);
              if (onActivateNode) onActivateNode(step, addInfo);
            }}
            onAddRoundClick={() => {
              if (didDrag.current) return;
              const currentRoundsCount = liveCustomisationData?.setup?.triviaData?.triviaSetup?.[0]?.roundsCount ?? 1;
              const nextRoundsCount = Math.min(currentRoundsCount + 1, 10);
              if (nextRoundsCount === currentRoundsCount) return;

              const triviaSetup = liveCustomisationData?.setup?.triviaData?.triviaSetup?.[0];
              if (triviaSetup) triviaSetup.roundsCount = nextRoundsCount;
              setDiamondToggleTick((tick) => tick + 1);
              onFlowFieldChange?.('setup', 'triviaData', 'triviaSetup', 0, 'roundsCount', nextRoundsCount);

              const roundInfo = { roundVarKey: `triviaData${nextRoundsCount}`, roundClick: true };
              setActiveStepId(step.stepId);
              setActiveGraphicIndex(roundInfo);
              if (onActivateNode) {
                setTimeout(() => onActivateNode(step, roundInfo), 0);
              }
            }}
            onRoundClick={(roundVarKey) => {
              if (didDrag.current) return;
              setActiveStepId(step.stepId);
              const roundInfo = { roundVarKey, roundClick: true };
              setActiveGraphicIndex(roundInfo);
              if (onActivateNode) onActivateNode(step, roundInfo);
            }}
            onQuestionDisruptorDrop={(roundVarKey, questionIdx, event) => {
              if (didDrag.current) return;
              onTriviaQuestionDisruptorDrop?.(step, roundVarKey, questionIdx, event);
            }}
            onQuestionFieldChange={(roundVarKey, questionIdx, field, value) => {
              const targetQuestion = liveCustomisationData?.setup?.[roundVarKey]?.questions?.[questionIdx];
              if (field === '_addAnswer_') {
                if (!targetQuestion) return;

                let nextAnswerIndex = null;
                for (let index = 1; index <= 10; index += 1) {
                  const answerKey = `answer${index}`;
                  const currentValue = targetQuestion?.[answerKey];
                  if (currentValue === null || currentValue === undefined || String(currentValue).trim() === '') {
                    nextAnswerIndex = index;
                    break;
                  }
                }

                if (nextAnswerIndex == null) return;

                const answerKey = `answer${nextAnswerIndex}`;
                const defaultValue = `Answer ${nextAnswerIndex}`;
                targetQuestion[answerKey] = defaultValue;

                if (!targetQuestion.correctAnswer) {
                  targetQuestion.correctAnswer = answerKey;
                  onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, 'correctAnswer', answerKey);
                }

                setDiamondToggleTick((tick) => tick + 1);
                onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, answerKey, defaultValue);
                return;
              }

              if (field === '_deleteAnswer_') {
                if (!targetQuestion || !value) return;

                const deleteIndex = Number(String(value).replace('answer', ''));
                if (!Number.isInteger(deleteIndex) || deleteIndex < 1 || deleteIndex > 10) return;

                for (let index = deleteIndex; index < 10; index += 1) {
                  const nextAnswerKey = `answer${index + 1}`;
                  const nextImageKey = `answerImage${index + 1}`;
                  const answerKey = `answer${index}`;
                  const imageKey = `answerImage${index}`;

                  targetQuestion[answerKey] = targetQuestion?.[nextAnswerKey] ?? '';
                  targetQuestion[imageKey] = targetQuestion?.[nextImageKey] ?? null;

                  onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, answerKey, targetQuestion[answerKey]);
                  onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, imageKey, targetQuestion[imageKey]);
                }

                targetQuestion.answer10 = '';
                targetQuestion.answerImage10 = null;
                onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, 'answer10', '');
                onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, 'answerImage10', null);

                const currentCorrectAnswer = targetQuestion.correctAnswer;
                let nextCorrectAnswer = currentCorrectAnswer;

                if (currentCorrectAnswer === `answer${deleteIndex}`) {
                  nextCorrectAnswer = null;
                  for (let index = 1; index <= 10; index += 1) {
                    const answerKey = `answer${index}`;
                    if (targetQuestion?.[answerKey] && String(targetQuestion[answerKey]).trim() !== '') {
                      nextCorrectAnswer = answerKey;
                      break;
                    }
                  }
                } else if (typeof currentCorrectAnswer === 'string') {
                  const currentIndex = Number(String(currentCorrectAnswer).replace('answer', ''));
                  if (Number.isInteger(currentIndex) && currentIndex > deleteIndex) {
                    nextCorrectAnswer = `answer${currentIndex - 1}`;
                  }
                }

                targetQuestion.correctAnswer = nextCorrectAnswer;
                onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, 'correctAnswer', nextCorrectAnswer);
                setDiamondToggleTick((tick) => tick + 1);
                return;
              }

              if (targetQuestion) targetQuestion[field] = value;
              setDiamondToggleTick((tick) => tick + 1);

              if (typeof value === 'boolean') {
                onFlowBoolChange?.('setup', roundVarKey, 'questions', questionIdx, field, value);
                return;
              }

              onFlowFieldChange?.('setup', roundVarKey, 'questions', questionIdx, field, value);
            }}
          />
        );
      }
      return (
        <PollCardsNode key={step.stepId}
          step={step} stepX={stepX} centreY={centreY}
          overrideY={pollCardsY}
          isSelected={isSelected}
          activePollIndex={isSelected && typeof activeGraphicIndex === 'number' ? activeGraphicIndex : null}
          liveCustomisationData={liveCustomisationData}
          campaignFeatures={campaignFeatures}
          parentStepX={parentStepX}
          parentStepY={parentStepY}
          nextStepX={nextStep ? (xOverrides[nextStep.stepId] ?? nx(nextStep)) : null}
          nextStepY={nextStep ? (yOverrides[nextStep.stepId] ?? ny(nextStep, centreY)) : null}
          nextStepHalfW={nextStep ? halfW(nextStep) : null}
          audioSiblingRightX={audioSiblingRightX}
          audioSiblingMidY={audioSiblingMidY}
          onHeightChange={(h) => handlePollCardHeightChange(step.stepId, h)}
          onQuestionChange={(pollIndex, value) => onPollQuestionChange?.(step, pollIndex, value)}
          onAnswerChange={(pollIndex, answerIndex, value) => onPollAnswerChange?.(step, pollIndex, answerIndex, value)}
          onAddAnswer={(pollIndex) => onPollAnswerAdd?.(step, pollIndex)}
          onRemoveAnswer={(pollIndex, answerIndex) => onPollAnswerRemove?.(step, pollIndex, answerIndex)}
          onRemovePoll={(pollIndex) => {
            if (didDrag.current) return;
            if (onRemoveGraphic) onRemoveGraphic(step, pollIndex);
          }}
          onPollClick={(idx) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(idx);
            if (onActivateNode) onActivateNode(step, idx);
          }}
          onAddPollClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
        />
      );
    }
    if (kind === 'pollOutcome') {
      const stepX = xOverrides[step.stepId] ?? nx(step);
      return (
        <PollOutcomeNode key={step.stepId}
          step={step} stepX={stepX} centreY={centreY}
          overrideY={mergedAnchorY ?? yOverrides[step.stepId]}
          isSelected={isSelected}
          isDragOver={dragOverDisruptorStepId === step.stepId}
          liveCustomisationData={liveCustomisationData}
          campaignFeatures={campaignFeatures}
          activeAudioSlotVarName={activeAudioSlotVarName}
          onActivate={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, undefined);
          }}
          onActivateAudio={({ slotVarName, fileIndex }) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex({ slotVarName, fileIndex });
            const syntheticStep = {
              ...step,
              customisationConfig: {
                ...step.customisationConfig,
                tabKey: step.customisationConfig?.meta?.audioTabKey,
                varKey: step.customisationConfig?.meta?.audioGroupKey,
                variables: [{ variableName: slotVarName }],
                meta: {
                  ...step.customisationConfig?.meta,
                  pollAudio: true,
                },
              },
            };
            if (onActivateNode) onActivateNode(syntheticStep, { slotVarName, fileIndex });
          }}
          onRemoveAudio={({ slotVarName, fileIndex }) => {
            if (didDrag.current) return;
            const syntheticStep = {
              ...step,
              customisationConfig: {
                ...step.customisationConfig,
                tabKey: step.customisationConfig?.meta?.audioTabKey,
                varKey: step.customisationConfig?.meta?.audioGroupKey,
                variables: [{ variableName: slotVarName }],
                meta: {
                  ...step.customisationConfig?.meta,
                  pollAudio: true,
                },
              },
            };
            if (onRemoveGraphic) onRemoveGraphic(syntheticStep, { slotVarName, fileIndex });
          }}
          onDisruptorActivate={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            // The outcome node's disruptorLink is the same dot-path format as disruptorSelectDataLink.
            // Reuse handleFlowDisruptorActivate by temporarily mapping disruptorLink → disruptorSelectDataLink.
            const syntheticStep = { ...step, customisationConfig: { ...step.customisationConfig, meta: { ...step.customisationConfig?.meta, disruptorSelectDataLink: step.customisationConfig?.meta?.disruptorLink } } };
            if (onActivateDisruptor) onActivateDisruptor(syntheticStep);
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOverDisruptorStepId(step.stepId); }}
          onDragLeave={() => setDragOverDisruptorStepId(cur => cur === step.stepId ? null : cur)}
          onDrop={(e) => {
            const syntheticStep = { ...step, customisationConfig: { ...step.customisationConfig, meta: { ...step.customisationConfig?.meta, disruptorSelectDataLink: step.customisationConfig?.meta?.disruptorLink } } };
            handleDisruptorDrop(syntheticStep, e);
          }}
          onRemoveDisruptor={(removeIndex) => {
            if (didDrag.current) return;
            const syntheticStep = { ...step, customisationConfig: { ...step.customisationConfig, meta: { ...step.customisationConfig?.meta, disruptorSelectDataLink: step.customisationConfig?.meta?.disruptorLink } } };
            if (onRemoveDisruptor) onRemoveDisruptor(syntheticStep, removeIndex);
          }}
          allPolls={liveCustomisationData?.setup?.pollData?.polls}
          onPollTriggerDrop={(pollIndex, triggerField, e) => {
            const pollsStep = stepMap['polls_node'];
            if (pollsStep) onPollTriggerDrop?.(pollsStep, pollIndex, triggerField, e);
          }}
          onPollTriggerRemove={(pollIndex, triggerField, removeIndex) => {
            const pollsStep = stepMap['polls_node'];
            if (pollsStep) onPollTriggerRemove?.(pollsStep, pollIndex, triggerField, removeIndex);
          }}
        />
      );
    }
    if (kind === 'puzzleStack') {
      const cfg       = step.customisationConfig;
      const tabKey    = cfg?.tabKey;
      const varKey    = cfg?.varKey;
      const varName   = cfg?.variables?.[0]?.variableName;
      const liveVals  = liveCustomisationData && tabKey && varKey && varName
        ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName] : null;
      const items     = liveVals || step.populatedData?.variables?.[0]?.allValues || [];

      const stepX       = xOverrides[step.stepId] ?? nx(step);
      const parentStep  = parentMap[step.stepId];
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const parentStepY = parentStep ? (yOverrides[parentStep.stepId] ?? ny(parentStep, centreY)) : null;
      const parentHW    = parentStep ? halfW(parentStep) : CIRCLE_R;
      const stepCY      = yOverrides[step.stepId] ?? ny(step, centreY);

      return (
        <PuzzleStackNode
          key={step.stepId}
          step={step}
          stepX={stepX}
          stepCY={stepCY}
          parentStepX={parentStepX}
          parentStepY={parentStepY}
          parentHalfW={parentHW}
          items={items}
          activeStepId={activeStepId}
          activeGraphicIndex={activeStepId === step.stepId && typeof activeGraphicIndex === 'number' ? activeGraphicIndex : null}
          assetFolder={assetFolder}
          campaignId={campaignId}
          onPuzzleClick={(idx) => handleGraphicItemClick(step, idx)}
          onAddSolutionClick={(idx) => handleActivatePuzzleSolutionImage(step, idx)}
          onRemoveSolutionClick={(idx) => handleRemovePuzzleSolutionImage(step, idx)}
          onAddPuzzleClick={() => {
            if (!didDrag.current) handleAddGraphicClick(step);
          }}
          onRemovePuzzleClick={(idx) => {
            if (!didDrag.current && onRemoveGraphic) onRemoveGraphic(step, idx);
          }}
        />
      );
    }
    if (kind === 'goalsNode') {
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepY = yOverrides[step.stepId] ?? ny(step, centreY);
      const goalsHashTrigStep = stepMap['goals_hashtag_trigger'] ?? null;
      return (
        <GoalsNode
          key={step.stepId}
          step={step}
          overrideX={stepX}
          overrideY={mergedAnchorY ?? stepY}
          centreY={centreY}
          selected={isSelected}
          onClick={() => handleNodeClick(step)}
          liveCustomisationData={liveCustomisationData}
          campaignFeatures={campaignFeatures}
          assetFolder={assetFolder}
          campaignId={campaignId}
          activeGoalIndex={activeStepId === step.stepId ? activeGraphicIndex : null}
          onGoalFieldChange={(i, field, value) => {
            if (onGoalFieldChange) onGoalFieldChange(step, i, field, value);
          }}
          onActivate={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          onGoalActivate={(i, subField) => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(i);
            if (onActivateNode) onActivateNode(step, subField != null ? { goalIndex: i, field: subField } : i);
          }}
          onRemoveGoal={(i) => {
            if (onRemoveGraphic) onRemoveGraphic(step, i);
          }}
          onRewardDisruptorDrop={(i, e) => {
            if (onGoalRewardDisruptorDrop) onGoalRewardDisruptorDrop(step, i, e);
          }}
        />
      );
    }
    if (kind === 'videoStack') {
      const items = resolveStepItems(step);
      // Extra horizontal offset to leave room for the i + add buttons on the incoming connector.
      // ROW_STEP naturally gives only ~50px gap, but the buttons need ~80px.
      const stepX = (xOverrides[step.stepId] ?? nx(step)) + getStackButtonExtraX(step);
      const stepCY = yOverrides[step.stepId] ?? ny(step, centreY);
      const parentStep = parentMap[step.stepId] ?? null;
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const parentStepY = parentStep ? (connectorYOverrides[parentStep.stepId] ?? yOverrides[parentStep.stepId] ?? ny(parentStep, centreY)) : null;
      const nextStep = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
      const nextStepX = nextStep ? (xOverrides[nextStep.stepId] ?? nx(nextStep)) + getStackButtonExtraX(nextStep) : null;
      const nextStepY = nextStep ? (connectorYOverrides[nextStep.stepId] ?? yOverrides[nextStep.stepId] ?? ny(nextStep, centreY)) : null;
      return (
        <VideoStackNode
          key={step.stepId}
          step={step}
          stepX={stepX}
          stepCY={stepCY}
          parentStep={parentStep}
          parentStepX={parentStepX}
          parentStepY={parentStepY}
          nextStep={nextStep}
          nextStepX={nextStepX}
          nextStepY={nextStepY}
          nextStepHalfW={nextStep ? halfW(nextStep) : null}
          items={items}
          activeStepId={activeStepId}
          activeGraphicIndex={activeStepId === step.stepId && typeof activeGraphicIndex === 'number' ? activeGraphicIndex : null}
          assetFolder={featureInfo?.defaultAssetFolder}
          campaignId={campaignID}
          onVideoClick={(idx) => handleGraphicItemClick(step, idx)}
          onAddItemClick={() => { if (!didDrag.current) handleAddGraphicClick(step); }}
          onInfoClick={() => {
            if (didDrag.current) return;
            setActiveStepId(step.stepId);
            setActiveGraphicIndex(null);
            if (onActivateNode) onActivateNode(step, null);
          }}
          onRemoveVideo={(idx) => { if (!didDrag.current && onRemoveGraphic) onRemoveGraphic(step, idx); }}
        />
      );
    }
    if (kind === 'presetTextNode') {
      const items = resolveStepItems(step);
      const stepX = xOverrides[step.stepId] ?? nx(step);
      const stepCY = yOverrides[step.stepId] ?? ny(step, centreY);
      const parentStep = parentMap[step.stepId] ?? null;
      const parentStepX = parentStep ? (xOverrides[parentStep.stepId] ?? nx(parentStep)) : null;
      const parentStepY = parentStep ? (connectorYOverrides[parentStep.stepId] ?? yOverrides[parentStep.stepId] ?? ny(parentStep, centreY)) : null;
      const nextStep = step.connectsTo?.[0] ? stepMap[step.connectsTo[0]] : null;
      const nextStepX = nextStep ? (xOverrides[nextStep.stepId] ?? nx(nextStep)) : null;
      const nextStepY = nextStep ? (connectorYOverrides[nextStep.stepId] ?? yOverrides[nextStep.stepId] ?? ny(nextStep, centreY)) : null;
      // PresetTextNode always draws its own direct connector; never joins a shared merge spine
      const nextMergeX = null;
      return (
        <PresetTextNode
          key={step.stepId}
          step={step}
          stepX={stepX}
          stepCY={stepCY}
          parentStep={parentStep}
          parentStepX={parentStepX}
          parentStepY={parentStepY}
          nextStep={nextStep}
          nextStepX={nextStepX}
          nextStepY={nextStepY}
          nextMergeX={nextMergeX}
          items={items}
          headersEnabled={liveTextHeadersEnabled}
          activeStepId={activeStepId}
          activePresetIndex={activeStepId === step.stepId && typeof activeGraphicIndex === 'number' ? activeGraphicIndex : null}
          onPresetClick={(idx) => handleGraphicItemClick(step, idx)}
          onPresetFieldChange={(idx, field, value) => {
            if (onPresetFieldChange) onPresetFieldChange(step, idx, field, value);
          }}
          onRemovePreset={(idx) => {
            if (!didDrag.current && onRemoveGraphic) onRemoveGraphic(step, idx);
          }}
        />
      );
    }
    return null; // unknown kind — shouldn't happen
  }

  // Derive the selected circle step — used to show the info panel.
  // This is an inline lookup, not state, so it always reflects the current flow.
  const activeCircleStep = (() => {
    if (!activeStepId) return null;
    const s = stepMap[activeStepId];
    return (s && (stepKind(s) === 'circle' || stepKind(s) === 'infoButton' || ((stepKind(s) === 'videoStack' || stepKind(s) === 'audioStack') && activeGraphicIndex === null)) && s.stepDescription) ? s : null;
  })();

  return (
    <div className="fc-wrapper">
      {activeCircleStep && (
        <CircleInfoPanel
          step={activeCircleStep}
          onClose={() => { setActiveStepId(null); onClearSelection?.(); }}
        />
      )}
      <div
        ref={panAreaRef}
        className="fc-pan-area"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
      >
        <svg
          className="fc-svg"
          width="100%"
          height="100%"
          onClick={(e) => {
            if (didDrag.current) {
              return;
            }
            if (!e.target.closest('.fc-node')) {
              setActiveStepId(null);
              setActiveGraphicIndex(null);
              onClearSelection?.();
            }
          }}
        >
          <defs>
            {/* Grid pattern origin shifts with offset so the dots stay "fixed" */}
            <pattern id="fc-grid" width="30" height="30" patternUnits="userSpaceOnUse"
              x={offset.x % 30} y={offset.y % 30}>
              <circle cx="15" cy="15" r="1" fill={C.dot} />
            </pattern>
          </defs>
          {/* Background fills the whole viewport regardless of pan */}
          <rect width="100%" height="100%" fill={C.canvasBg} />
          <rect width="100%" height="100%" fill="url(#fc-grid)" />

          {/* All canvas content lives inside this panning + zoom group */}
          <g transform={`translate(${offset.x}, ${offset.y})`}>
            <g transform={`scale(${zoom})`}>
            {Array.from(multiImageStackTargetIds).map((stepId) => {
              const step = stepMap[stepId];
              if (!step) return null;

              // Compute anchor inline here so it always reflects the current render's
              // yOverrides / xOverrides rather than a separately-computed IIFE snapshot.
              const imageParents = (incomingParentsMap[stepId] || []).filter(p => stepKind(p) === 'imageStack');
              if (imageParents.length < 2) return null;

              // Sort by Y (low → high)
              const sortedParents = [...imageParents].sort(
                (a, b) => (yOverrides[a.stepId] ?? ny(a, centreY)) - (yOverrides[b.stepId] ?? ny(b, centreY))
              );

              // Collect per-parent info: right edge of their card stack and the Y of every
              // card — mirrors what ImageStackNode draws as spurs on the left side.
              const SPINE_GAP = 24; // gap between card right-edge and spine
              const parentInfos = sortedParents.map(p => {
                const pStepX = xOverrides[p.stepId] ?? nx(p);
                const pHalfH = imageStackHalfHByStepId[p.stepId] ?? halfH(p);
                const pY     = yOverrides[p.stepId] ?? ny(p, centreY);
                const cardRightX = pStepX + CARD_W / 2;
                // Derive card count from stored halfH (avoids needing resolveStepItems here)
                const count = Math.max(
                  Math.round((2 * pHalfH + IMAGE_STACK_CARD_GAP) / (IMAGE_STACK_CARD_H + IMAGE_STACK_CARD_GAP)),
                  1
                );
                const stackTopY = pY - pHalfH;
                const cardYs = Array.from({ length: count }, (_, i) =>
                  stackTopY + i * (IMAGE_STACK_CARD_H + IMAGE_STACK_CARD_GAP) + IMAGE_STACK_CARD_H / 2
                );
                return {
                  cardRightX,
                  cardYs,
                  firstCardY: cardYs[0],
                  lastCardY: cardYs[count - 1],
                  stackCenterY: pY,
                };
              });

              // Spine X: just past the rightmost card right-edge
              const spineX = Math.max(...parentInfos.map(pi => pi.cardRightX)) + SPINE_GAP;

              const targetX = xOverrides[stepId] ?? nx(step);
              const x2 = targetX - halfW(step);

              if (spineX >= x2) return null;

              // Spine spans from topmost card of first stack to bottommost card of last stack
              const spineTopY    = Math.min(...parentInfos.map(pi => pi.firstCardY));
              const spineBottomY = Math.max(...parentInfos.map(pi => pi.lastCardY));

              // anchorY: Y of the lowest common ancestor of all imageStack branches so the
              // exit wire stays inline with the upstream node (e.g. Audio at centreY).
              // Falls back to midpoint of stack centres if no LCA is found.
              const getAncestorIdsR = (startStepId) => {
                const ids = [];
                let cur = parentMap[startStepId];
                while (cur) { ids.push(cur.stepId); cur = parentMap[cur.stepId]; }
                return ids;
              };
              const firstAncs = getAncestorIdsR(sortedParents[0].stepId);
              const otherAncSets = sortedParents.slice(1).map(p => new Set(getAncestorIdsR(p.stepId)));
              const lcaS = firstAncs.map(id => stepMap[id]).find(s => s && otherAncSets.every(set => set.has(s.stepId)));
              const firstStackCenterY = parentInfos[0].stackCenterY;
              const lastStackCenterY  = parentInfos[parentInfos.length - 1].stackCenterY;
              const anchorY = lcaS
                ? (yOverrides[lcaS.stepId] ?? ny(lcaS, centreY))
                : (firstStackCenterY + lastStackCenterY) / 2;

              // Extend spine if anchorY falls outside the card range
              const effectiveSpineTopY    = Math.min(spineTopY, anchorY);
              const effectiveSpineBottomY = Math.max(spineBottomY, anchorY);

              return (
                <g key={`merged-image-stack-connector-${stepId}`} style={{ pointerEvents: 'none' }}>
                  {/* Vertical spine spanning all cards and the anchor point */}
                  {Math.abs(effectiveSpineBottomY - effectiveSpineTopY) > 2 && (
                    <line
                      x1={spineX} y1={effectiveSpineTopY}
                      x2={spineX} y2={effectiveSpineBottomY}
                      stroke={C.purple}
                      strokeWidth="1.5"
                      strokeOpacity="0.82"
                      strokeLinecap="square"
                    />
                  )}
                  {/* Spurs from every card in every stack to the spine */}
                  {parentInfos.map((pi, pIdx) =>
                    pi.cardYs.map((cy, cIdx) => (
                      <React.Fragment key={`spur-${pIdx}-${cIdx}`}>
                        <line
                          x1={pi.cardRightX} y1={cy}
                          x2={spineX}        y2={cy}
                          stroke={C.purple} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square"
                        />
                        <circle cx={pi.cardRightX} cy={cy} r="3" fill={C.purple} fillOpacity="0.9" />
                      </React.Fragment>
                    ))
                  )}
                  {/* Horizontal wire from spine midpoint to target node */}
                  <path
                    d={`M ${spineX} ${anchorY} H ${x2}`}
                    stroke={C.purple}
                    strokeWidth="1.5"
                    fill="none"
                    strokeOpacity="0.82"
                    strokeLinecap="square"
                  />
                  <circle cx={x2} cy={anchorY} r="3" fill={C.purple} fillOpacity="0.9" />
                </g>
              );
            })}

            {/* Merge connectors: shared vertical spine for multiple same-row sources → single target */}
            {mergeConnectorGroups.map((mg, gi) => {
              const color = C.purple;
              const op = 0.82;
              return (
                <g key={`merge-${gi}`} style={{ pointerEvents: 'none' }}>
                  {mg.sources.map((s, si) => (
                    <line key={`mb-${gi}-${si}`}
                      x1={s.fromRightX} y1={s.fromY} x2={mg.mergeX} y2={s.fromY}
                      stroke={color} strokeWidth="1.5" strokeOpacity={op} strokeLinecap="square" />
                  ))}
                  <line
                    x1={mg.mergeX} y1={mg.spineTopY} x2={mg.mergeX} y2={mg.spineBottomY}
                    stroke={color} strokeWidth="1.5" strokeOpacity={op} strokeLinecap="square" />
                  <line
                    x1={mg.mergeX} y1={mg.targetY} x2={mg.toBaseX} y2={mg.targetY}
                    stroke={color} strokeWidth="1.5" strokeOpacity={op} strokeLinecap="square" />
                  <circle cx={mg.toBaseX} cy={mg.targetY} r="3" fill={color} fillOpacity="0.9" />
                </g>
              );
            })}

            {outgoingBranchGroups.map((bg, gi) => {
              const color = C.purple;
              const op = 0.82;
              return (
                <g key={`out-branch-${gi}`} style={{ pointerEvents: 'none' }}>
                  <line
                    x1={bg.sourceRightX} y1={bg.fromY} x2={bg.branchX} y2={bg.fromY}
                    stroke={color} strokeWidth="1.5" strokeOpacity={op} strokeLinecap="square" />
                  <line
                    x1={bg.branchX} y1={bg.spineTopY} x2={bg.branchX} y2={bg.spineBottomY}
                    stroke={color} strokeWidth="1.5" strokeOpacity={op} strokeLinecap="square" />
                  {bg.children.map((ch, ci) => {
                    const childKind = stepKind(ch.c.to);
                    if (childKind === 'audioStack' || childKind === 'videoStack') return null;
                    return (
                      <React.Fragment key={`out-branch-${gi}-${ci}`}>
                        <line
                          x1={bg.branchX} y1={ch.toY} x2={ch.childLeftX} y2={ch.toY}
                          stroke={color} strokeWidth="1.5" strokeOpacity={op} strokeLinecap="square" />
                        <circle cx={ch.childLeftX} cy={ch.toY} r="3" fill={color} fillOpacity="0.9" />
                      </React.Fragment>
                    );
                  })}
                </g>
              );
            })}

            {connections.map((c, i) => {
              // Skip connections handled by shared branch / merge groups
              if (mergeConnectionKeys.has(`${c.from.stepId}--${c.to.stepId}`)) return null;
              if (outgoingBranchConnectionKeys.has(`${c.from.stepId}--${c.to.stepId}`)) return null;
              if (stepKind(c.from) === 'presetTextNode' || stepKind(c.to) === 'presetTextNode') return null;
              if (stepKind(c.from) === 'videoStack' || stepKind(c.to) === 'videoStack' || stepKind(c.from) === 'audioStack' || stepKind(c.to) === 'audioStack') return null;
              if (hideLiveTextResponsesBypassConnector && c.from.stepId === 'lt_responses_decision' && c.to.stepId === 'lt_complete_node') return null;
              if (hideSubBombExplosionBypassConnector && c.from.stepId === 'sb_allow_explosions_decision' && c.to.stepId === 'sb_active_node') return null;
              // imageStack exit connectors should leave from the parent's (infoButton's) Y —
              // the midpoint of the shared vertical branch between the sibling image stacks.
              let fromYOverride;
              let toYOverride;
              let fromXAnchorOverride;
              let toXAnchorOverride;
              let branchXOverride;

              if (stepKind(c.from) === 'imageStack' || stepKind(c.from) === 'particleImageStack' || stepKind(c.from) === 'particleColourStack') {
                const mergeAnchor = imageStackMergeAnchorByChildId[c.to.stepId];
                if (mergeAnchor) {
                  fromYOverride = mergeAnchor.y;
                  fromXAnchorOverride = mergeAnchor.x;
                } else {
                  const parent = parentMap[c.from.stepId];
                  if (stepKind(c.from) === 'imageStack' && c.from.customisationConfig?.meta?.stackAlign === 'belowParent') {
                    const stackCY = connectorYOverrides[c.from.stepId] ?? ny(c.from, centreY);
                    fromYOverride = stackCY + renderedHalfH(c.from);
                  } else if (parent && multiImageStackParentIds.has(parent.stepId)) {
                    fromYOverride = imageStackBranchMidYByParent[parent.stepId] ?? yOverrides[parent.stepId] ?? ny(parent, centreY);
                  }
                }
              }
              if (stepKind(c.from) === 'puzzleStack') {
                const parent = parentMap[c.from.stepId];
                if (parent) {
                  fromYOverride = yOverrides[parent.stepId] ?? ny(parent, centreY);
                }
              }
              // countdown stacks: exit from bottom, enter at top.
              // This preserves the original stack layout for image/text countdown nodes.
              if (stepKind(c.from) === 'countdownImageStack' || stepKind(c.from) === 'countdownTextStack') {
                const stackCY = connectorYOverrides[c.from.stepId] ?? ny(c.from, centreY);
                fromYOverride = stackCY + renderedHalfH(c.from);
              }
              if (stepKind(c.to) === 'countdownImageStack' || stepKind(c.to) === 'countdownTextStack') {
                const stackCY = connectorYOverrides[c.to.stepId] ?? ny(c.to, centreY);
                toYOverride = stackCY - renderedHalfH(c.to);
              }
              // countdownTimer sits on the main spine: force connectors to use centreY so
              // both the incoming and outgoing lines are perfectly horizontal.
              if (stepKind(c.from) === 'countdownTimer') {
                fromYOverride = centreY;
              }
              if (stepKind(c.to) === 'countdownTimer') {
                toYOverride = centreY;
              }
              // GoalsNode exit connector: depart from the actual right edge of the goals strip,
              // not the standard CARD_W/2 used by the static halfW function.
              if (stepKind(c.from) === 'goalsNode') {
                const goalsX = xOverrides[c.from.stepId] ?? nx(c.from);
                const actualHalfW = goalsHalfWByStepId[c.from.stepId] ?? halfW(c.from);
                fromXAnchorOverride = goalsX + actualHalfW;
              }
              if (stepKind(c.from) === 'slotReels') {
                const reelsX = xOverrides[c.from.stepId] ?? nx(c.from);
                const actualHalfW = slotReelsHalfWByStepId[c.from.stepId] ?? halfW(c.from);
                fromXAnchorOverride = reelsX + actualHalfW;
              }
              if (stepKind(c.to) === 'goalsNode') {
                const goalsX = xOverrides[c.to.stepId] ?? nx(c.to);
                const actualHalfW = goalsHalfWByStepId[c.to.stepId] ?? halfW(c.to);
                toXAnchorOverride = goalsX - actualHalfW;
              }
              if (stepKind(c.to) === 'slotReels') {
                const reelsX = xOverrides[c.to.stepId] ?? nx(c.to);
                const actualHalfW = slotReelsHalfWByStepId[c.to.stepId] ?? halfW(c.to);
                toXAnchorOverride = reelsX - actualHalfW;
              }

              const renderedOutgoingCount = renderedGenericConnectorCounts.outgoing[c.from.stepId] || 0;
              if (renderedOutgoingCount > 1) {
                const fromBaseX = fromXAnchorOverride ?? ((xOverrides[c.from.stepId] ?? nx(c.from)) + halfW(c.from));
                branchXOverride = fromBaseX + 38;
              }

              const fromAnchorX = fromXAnchorOverride ?? ((xOverrides[c.from.stepId] ?? nx(c.from)) + halfW(c.from));
              const toAnchorX = toXAnchorOverride ?? ((xOverrides[c.to.stepId] ?? nx(c.to)) - halfW(c.to));
              const renderedIncomingCount = renderedGenericConnectorCounts.incoming[c.to.stepId] || 0;
              if (!branchXOverride && renderedIncomingCount > 1) {
                // Only apply merge-left branchX when source is genuinely left of target;
                // right-of-target sources (e.g. response nodes → complete) use natural midX.
                if (fromAnchorX < toAnchorX) {
                  branchXOverride = toAnchorX - 38;
                }
              }
              if (!branchXOverride && c.to.stepId === 'sb_active_node' && fromAnchorX < toAnchorX) {
                branchXOverride = fromAnchorX + 38;
              }

              return (
                <Connector
                  key={`${c.from.stepId}--${c.to.stepId}--${i}`}
                  from={c.from}
                  to={c.to}
                  centreY={centreY}
                  yOverrides={connectorYOverrides}
                  xOverrides={xOverrides}
                  fromYOverride={fromYOverride}
                  toYOverride={toYOverride}
                  fromXAnchorOverride={fromXAnchorOverride}
                  toXAnchorOverride={toXAnchorOverride}
                  branchXOverride={branchXOverride}
                />
              );
            })}

            {visibleFlow.map(step => renderNode(step))}
            </g>
          </g>
        </svg>
      </div>

    </div>
  );
};

export default FlowCanvas;
